import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useModulePermission } from "../utils/useModulePermission";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Grid,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  Drawer,
  Divider,
  Card,
  CardContent,
  Select,
  FormControl,
  InputLabel,
  Chip,
  Tooltip,
  Stack,
  TableContainer,
  FormHelperText,
  Snackbar,
  Pagination,
  TablePagination,
  Autocomplete,
  InputAdornment,
  Collapse,
} from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import {
  Add as AddIcon,
  FileDownload as FileDownloadIcon,
  Delete as DeleteIcon,
  CalendarToday as CalendarIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AttachMoney,
  Cancel,
  Schedule,
  TrendingUp,
  Assignment,
  CheckCircle,
} from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import {
  fetchPurchaseOrders,
  fetchPOFullList,
  getPurchaseOrder,
  createPurchaseOrder,
  updatePurchaseOrder,
  deletePurchaseOrder,
  fetchPOClientNames,
  fetchPOProjectNames,
  fetchAccountManagers,
  fetchProjectManagers,
  fetchInvoicesForPO,
  createInvoiceForPO,
  PurchaseOrder,
  PurchaseOrderCreateInput,
  PurchaseOrderUpdateInput,
  Invoice,
  POPaginationParams,
} from "../api/po";
import CompactMetricCard from "../components/common/CompactMetricCard";
import apiClient, { removeAuthToken } from "../api/client";

interface POAnalysisPageProps {
  startDate?: string | null;
  endDate?: string | null;
}

interface POSummary {
  total_pos: number;
  active_pos: number;
  expired_pos: number;
  cancelled_pos: number;
  partially_cancelled_pos: number;
  total_value_usd: number;
  active_value_usd: number;
  expired_value_usd: number;
  cancelled_value_usd: number;
  avg_po_value: number;
  largest_po: {
    value: number;
    account: string;
    project: string;
  };
  most_recent_po: {
    date: string;
    account: string;
    project: string;
  };
}

// Interface for invoice planning row
interface InvoicePlanRow {
  id: string;
  description: string;
  planned_date: string;
  planned_amount: number | null;
  service_month: string;
}

// Interface for PO cancellation event (matching backend)
interface POCancellationEvent {
  id: string;
  date: string;
  new_value: number;
  change_amount: number;
  change_percentage: number;
  description?: string | null;
}

interface PORecord {
  id: number;
  po_id: string;
  po_title: string;
  rev_type?: string;
  rev_class?: string;
  am?: string;
  pm?: string;
  geo?: string;
  account?: string;
  project?: string;
  project_type?: string;
  milestone_desc?: string;
  po_date: string;
  po_value?: number;
  currency?: string;
  exchange_rate?: number;
  po_value_usd?: number;
  payment_terms?: string;
  status: string;
  referenced_msa?: string;
  remarks?: string;
  created_at: string;
  updated_at?: string;
}

type CreatableSelectOption =
  | string
  | {
      inputValue: string;
      title: string;
    };

const creatableSelectFilter = createFilterOptions<CreatableSelectOption>();

function CreatableSearchSelect(props: {
  label: string;
  required?: boolean;
  value: string;
  onChange: (value: string) => void;
  options: string[];
  loading: boolean;
  loadError: string | null;
  fieldError?: string;
  onClearFieldError?: () => void;
  createCtaLabel: string; // e.g. "Create new Account Manager"
}) {
  const {
    label,
    required,
    value,
    onChange,
    options,
    loading,
    loadError,
    fieldError,
    onClearFieldError,
    createCtaLabel,
  } = props;

  const [inputValue, setInputValue] = useState("");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [previousValue, setPreviousValue] = useState<string>("");

  if (showCreateForm) {
    return (
      <TextField
        label={label}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          onClearFieldError?.();
        }}
        fullWidth
        size="small"
        required={required}
        error={!!fieldError}
        helperText={fieldError}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={() => {
                  setShowCreateForm(false);
                  onChange(previousValue);
                }}
                edge="end"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{
          "& .MuiOutlinedInput-root": { borderRadius: 1 },
          "& .MuiInputLabel-root": {
            fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" },
          },
          "& .MuiInputBase-input": {
            fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
          },
          "& .MuiFormHelperText-root": {
            fontSize: { xs: "0.65rem", sm: "0.7rem" },
          },
        }}
      />
    );
  }

  return (
    <Autocomplete
      value={value || null}
      onChange={(_e, newValue) => {
        if (typeof newValue === "string") {
          onChange(newValue);
          onClearFieldError?.();
          return;
        }
        if (newValue && typeof newValue === "object" && "inputValue" in newValue) {
          setShowCreateForm(true);
          setPreviousValue(value || "");
          onChange(newValue.inputValue);
          setInputValue("");
          onClearFieldError?.();
          return;
        }
        onChange("");
      }}
      inputValue={inputValue}
      onInputChange={(_e, newInputValue) => setInputValue(newInputValue)}
      options={options as CreatableSelectOption[]}
      filterOptions={(opts, params) => {
        const filtered = creatableSelectFilter(opts, params);
        const typed = params.inputValue.trim();
        const exists =
          typed.length > 0 &&
          options.some((o) => o.toLowerCase() === typed.toLowerCase());
        // Only show "Create new ..." when there are no matching options at all
        // for the current typed value (prevents showing it for partial matches).
        if (typed.length > 0 && !exists && filtered.length === 0) {
          filtered.push({
            inputValue: typed,
            title: `${createCtaLabel}: "${typed}"`,
          });
        }
        return filtered;
      }}
      getOptionLabel={(option) => {
        if (typeof option === "string") return option;
        return option.title;
      }}
      renderOption={(renderProps, option) => (
        <li {...renderProps}>
          {typeof option === "string" ? option : option.title}
        </li>
      )}
      loading={loading}
      freeSolo={false}
      disableClearable={false}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          size="small"
          fullWidth
          error={!!fieldError}
          helperText={fieldError}
          sx={{
            "& .MuiOutlinedInput-root": { borderRadius: 1 },
            "& .MuiInputLabel-root": {
              fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" },
            },
            "& .MuiInputBase-input": {
              fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
            },
            "& .MuiFormHelperText-root": {
              fontSize: { xs: "0.65rem", sm: "0.7rem" },
            },
          }}
        />
      )}
      noOptionsText={
        loadError
          ? "Failed to load options"
          : loading
            ? "Loading..."
            : "No options"
      }
    />
  );
}

const POPage: React.FC<POAnalysisPageProps> = ({ startDate, endDate }) => {
  const makeClientId = () => {
    if (
      typeof crypto !== "undefined" &&
      typeof crypto.randomUUID === "function"
    ) {
      return crypto.randomUUID();
    }
    return `id-${Math.random().toString(16).slice(2)}-${Math.random().toString(16).slice(2)}`;
  };

  // Purchase Orders state
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [filteredPurchaseOrders, setFilteredPurchaseOrders] = useState<
    PurchaseOrder[]
  >([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);
  const [editingPO, setEditingPO] = useState<PurchaseOrder | null>(null);
  const [editFormData, setEditFormData] = useState<PurchaseOrderCreateInput>({
    client_name: "",
    project_name: "",
    project_type: "",
    geography: "",
    account_manager: "",
    project_manager: "",
    linked_msa: "",
    revenue_type: "",
    revenue_class: "",
    po_date: null,
    po_value_original: null,
    currency: "USD",
    fx_rate_usd: 1.0,
    fx_rate_inr: null,
    payment_terms: "",
    status: "Active",
    remarks: "",
  });

  const [summary, setSummary] = useState<POSummary | null>(null);
  const [pos, setPOs] = useState<PORecord[]>([]);
  const [, setFilteredPOs] = useState<PORecord[]>([]);

  // Filter and pagination state
  const [statusFilter, setStatusFilter] = useState<string>("All");
  const [clientFilter, setClientFilter] = useState<string>("");
  const [projectFilter, setProjectFilter] = useState<string>("");
  const [poProjectNames, setPoProjectNames] = useState<string[]>([]);
  const [loadingPOProjectNames, setLoadingPOProjectNames] =
    useState<boolean>(false);
  const [poProjectNamesError, setPoProjectNamesError] = useState<string | null>(
    null,
  );
  const [poClientNames, setPoClientNames] = useState<string[]>([]);
  const [loadingPOClientNames, setLoadingPOClientNames] =
    useState<boolean>(false);
  const [poClientNamesError, setPoClientNamesError] = useState<string | null>(
    null,
  );

  /** PO list tab filters: client/project options depend on the other field (API). */
  const [poFilterProjectNames, setPoFilterProjectNames] = useState<string[]>(
    [],
  );
  const [loadingPOFilterProjects, setLoadingPOFilterProjects] =
    useState<boolean>(false);
  const [poFilterProjectNamesError, setPoFilterProjectNamesError] = useState<
    string | null
  >(null);
  const [poFilterClientNames, setPoFilterClientNames] = useState<string[]>([]);
  const [loadingPOFilterClients, setLoadingPOFilterClients] =
    useState<boolean>(false);
  const [poFilterClientNamesError, setPoFilterClientNamesError] = useState<
    string | null
  >(null);

  const [accountManagers, setAccountManagers] = useState<string[]>([]);
  const [loadingAccountManagers, setLoadingAccountManagers] =
    useState<boolean>(false);
  const [accountManagersError, setAccountManagersError] = useState<
    string | null
  >(null);
  const [projectManagers, setProjectManagers] = useState<string[]>([]);
  const [loadingProjectManagers, setLoadingProjectManagers] =
    useState<boolean>(false);
  const [projectManagersError, setProjectManagersError] = useState<
    string | null
  >(null);
  const [poPage, setPoPage] = useState(1);
  const [poPageSize, setPoPageSize] = useState(20);
  const [poTotal, setPoTotal] = useState(0);
  const [poTotalPages, setPoTotalPages] = useState(0);

  // Invoice planning state
  const [openInvoicePlanner, setOpenInvoicePlanner] = useState<boolean>(false);
  const [selectedPO, setSelectedPO] = useState<PurchaseOrder | null>(null);
  const [invoicePlanRows, setInvoicePlanRows] = useState<InvoicePlanRow[]>([]);
  const [savingPlan, setSavingPlan] = useState<boolean>(false);
  const [planError, setPlanError] = useState<string | null>(null);
  const [planSuccess, setPlanSuccess] = useState<string | null>(null);
  const [invoicePlanSubmitAttempted, setInvoicePlanSubmitAttempted] =
    useState<boolean>(false);

  // PO Detail drawer state
  const [openPODetail, setOpenPODetail] = useState<boolean>(false);
  const [poDetailPO, setPODetailPO] = useState<PurchaseOrder | null>(null);
  const [poDetailInvoices, setPODetailInvoices] = useState<Invoice[]>([]);
  const [loadingPODetailInvoices, setLoadingPODetailInvoices] =
    useState<boolean>(false);
  const [poStatus, setPOStatus] = useState<string>("");
  const [poRemarks, setPORemarks] = useState<string>("");
  const [updatingPO, setUpdatingPO] = useState<boolean>(false);
  const [cancellationEvents, setCancellationEvents] = useState<
    POCancellationEvent[]
  >([]);
  const [newCancellationValue, setNewCancellationValue] = useState<
    number | null
  >(null);
  const [newCancellationDate, setNewCancellationDate] = useState<string>("");
  const [newCancellationDescription, setNewCancellationDescription] =
    useState<string>("");
  const [originalPOValue, setOriginalPOValue] = useState<number | null>(null);
  // Edit cancellation event state
  const [editingCancellationEvent, setEditingCancellationEvent] =
    useState<POCancellationEvent | null>(null);
  const [editCancellationValue, setEditCancellationValue] = useState<
    number | null
  >(null);
  const [editCancellationDate, setEditCancellationDate] = useState<string>("");
  const [editCancellationDescription, setEditCancellationDescription] =
    useState<string>("");

  const [confirmDeleteCancellationEventId, setConfirmDeleteCancellationEventId] =
    useState<string | null>(null);
  const [confirmDeleteEditPOCancellationEventId, setConfirmDeleteEditPOCancellationEventId] =
    useState<string | null>(null);

  // Edit Purchase Order dialog - cancellation events (saved on Edit submit)
  const [editPOCancellationEvents, setEditPOCancellationEvents] = useState<
    POCancellationEvent[]
  >([]);
  const [editPOOriginalValue, setEditPOOriginalValue] = useState<number>(0);
  const [editPONewCancellationValue, setEditPONewCancellationValue] = useState<
    number | null
  >(null);
  const [editPONewCancellationDate, setEditPONewCancellationDate] =
    useState<string>("");
  const [editPONewCancellationDescription, setEditPONewCancellationDescription] =
    useState<string>("");
  const [editPOEditingCancellationId, setEditPOEditingCancellationId] = useState<
    string | null
  >(null);

  /** PO list card: expanded financial summary + cached invoices per PO id */
  const [expandedPOCardIds, setExpandedPOCardIds] = useState<Set<number>>(
    new Set(),
  );
  const [poListInvoicesByPOId, setPOListInvoicesByPOId] = useState<
    Record<number, Invoice[]>
  >({});
  const poListInvoiceFetchStartedRef = useRef<Set<number>>(new Set());

  // Delete PO confirmation dialog state
  const [deletePODialogOpen, setDeletePODialogOpen] = useState<boolean>(false);
  const [deletingPO, setDeletingPO] = useState<PurchaseOrder | null>(null);
  const [deletingPOLoading, setDeletingPOLoading] = useState<boolean>(false);

  const [exportingPOCsv, setExportingPOCsv] = useState<boolean>(false);

  // Snackbar state
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  // Form state
  const [formData, setFormData] = useState<PurchaseOrderCreateInput>({
    client_name: "",
    project_name: "",
    project_type: "",
    geography: "",
    account_manager: "",
    project_manager: "",
    linked_msa: "",
    revenue_type: "",
    revenue_class: "",
    po_date: "",
    po_value_original: null,
    currency: "USD",
    fx_rate_usd: 1.0,
    fx_rate_inr: null,
    payment_terms: "",
    status: "Active",
    remarks: "",
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [editFormErrors, setEditFormErrors] = useState<Record<string, string>>(
    {},
  );

  const { can } = useModulePermission("Purchase Orders");

  // Fetch POs and summary
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      // Build query params for date filtering
      const params = new URLSearchParams();
      if (startDate) {
        // Convert YYYY-MM to YYYY-MM-01
        params.append("start_date", `${startDate}-01`);
      }
      if (endDate) {
        // Convert YYYY-MM to last day of month
        const endDateObj = new Date(`${endDate}-01`);
        const lastDay = new Date(
          endDateObj.getFullYear(),
          endDateObj.getMonth() + 1,
          0,
        );
        params.append("end_date", lastDay.toISOString().split("T")[0]);
      }

      const queryString = params.toString();
      const apiSuffix = queryString ? `?${queryString}` : "";

      const [posResponse, summaryResponse] = await Promise.all([
        // remove network fetch here and reuse current `pos` state to avoid duplicate requests
        Promise.resolve({ data: { pos } }),
        apiClient.get(
          `/api/v1/delivery/po-master/summary/statistics${apiSuffix}`,
        ),
      ]);

      // Sort by most recent PO date first.
      // Use string/ISO comparison where possible to avoid creating raw epoch numbers in the bundle.
      const sortedPOs = (posResponse.data.pos || []).sort(
        (a: PORecord, b: PORecord) => {
          const aDate = a.po_date || "";
          const bDate = b.po_date || "";
          // descending order: newer first
          return bDate.localeCompare(aDate);
        },
      );

      setPOs(sortedPOs);
      setFilteredPOs(sortedPOs);
      setSummary(summaryResponse.data.summary || null);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [endDate, pos, startDate]);

  const handleClearFilters = () => {
    setStatusFilter("All");
    setClientFilter("");
    setProjectFilter("");
  };

  // Helper function to format FastAPI validation errors
  const formatApiError = (error: any): string => {
    if (!error) return "An unknown error occurred";
    if (typeof error === "string") return error;
    if (Array.isArray(error)) {
      return error
        .map((err: any) => {
          const field =
            err.loc && err.loc.length > 1
              ? err.loc[err.loc.length - 1]
              : "field";
          return `${field}: ${err.msg || "Invalid value"}`;
        })
        .join(", ");
    }
    if (error.detail) return formatApiError(error.detail);
    if (error.message) return error.message;
    try {
      return JSON.stringify(error);
    } catch {
      return "An error occurred";
    }
  };

  const loadPOProjectNames = useCallback(async () => {
    setLoadingPOProjectNames(true);
    setPoProjectNamesError(null);
    try {
      const names = await fetchPOProjectNames();
      setPoProjectNames(
        Array.from(new Set(names)).sort((a, b) => a.localeCompare(b)),
      );
    } catch (err: any) {
      const errorMessage = formatApiError(
        err.response?.data?.detail || err.response?.data || err.message,
      );
      setPoProjectNamesError(errorMessage || "Failed to load project names");
    } finally {
      setLoadingPOProjectNames(false);
    }
  }, []);

  const loadPOClientNames = useCallback(async () => {
    setLoadingPOClientNames(true);
    setPoClientNamesError(null);
    try {
      const names = await fetchPOClientNames();
      setPoClientNames(
        Array.from(new Set(names)).sort((a, b) => a.localeCompare(b)),
      );
    } catch (err: any) {
      const errorMessage = formatApiError(
        err.response?.data?.detail || err.response?.data || err.message,
      );
      setPoClientNamesError(errorMessage || "Failed to load client names");
    } finally {
      setLoadingPOClientNames(false);
    }
  }, []);

  const loadAccountManagers = useCallback(async () => {
    setLoadingAccountManagers(true);
    setAccountManagersError(null);
    try {
      const names = await fetchAccountManagers();
      setAccountManagers(
        Array.from(new Set(names)).sort((a, b) => a.localeCompare(b)),
      );
    } catch (err: any) {
      const errorMessage = formatApiError(
        err.response?.data?.detail || err.response?.data || err.message,
      );
      setAccountManagersError(errorMessage || "Failed to load account managers");
    } finally {
      setLoadingAccountManagers(false);
    }
  }, []);

  const loadProjectManagers = useCallback(async () => {
    setLoadingProjectManagers(true);
    setProjectManagersError(null);
    try {
      const names = await fetchProjectManagers();
      setProjectManagers(
        Array.from(new Set(names)).sort((a, b) => a.localeCompare(b)),
      );
    } catch (err: any) {
      const errorMessage = formatApiError(
        err.response?.data?.detail || err.response?.data || err.message,
      );
      setProjectManagersError(errorMessage || "Failed to load project managers");
    } finally {
      setLoadingProjectManagers(false);
    }
  }, []);

  useEffect(() => {
    loadPOProjectNames();
  }, [loadPOProjectNames]);

  useEffect(() => {
    loadPOClientNames();
  }, [loadPOClientNames]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingPOFilterProjects(true);
      setPoFilterProjectNamesError(null);
      try {
        const names = await fetchPOProjectNames(
          clientFilter.trim() ? clientFilter : undefined,
        );
        const sorted = Array.from(new Set(names)).sort((a, b) =>
          a.localeCompare(b),
        );
        if (cancelled) return;
        setPoFilterProjectNames(sorted);
        setProjectFilter((prev) =>
          prev && !sorted.includes(prev) ? "" : prev,
        );
      } catch (err: any) {
        if (cancelled) return;
        const errorMessage = formatApiError(
          err.response?.data?.detail || err.response?.data || err.message,
        );
        setPoFilterProjectNamesError(
          errorMessage || "Failed to load project names",
        );
      } finally {
        if (!cancelled) setLoadingPOFilterProjects(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clientFilter]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingPOFilterClients(true);
      setPoFilterClientNamesError(null);
      try {
        const names = await fetchPOClientNames(
          projectFilter.trim() ? projectFilter : undefined,
        );
        const sorted = Array.from(new Set(names)).sort((a, b) =>
          a.localeCompare(b),
        );
        if (cancelled) return;
        setPoFilterClientNames(sorted);
        setClientFilter((prev) =>
          prev && !sorted.includes(prev) ? "" : prev,
        );
      } catch (err: any) {
        if (cancelled) return;
        const errorMessage = formatApiError(
          err.response?.data?.detail || err.response?.data || err.message,
        );
        setPoFilterClientNamesError(
          errorMessage || "Failed to load client names",
        );
      } finally {
        if (!cancelled) setLoadingPOFilterClients(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectFilter]);

  useEffect(() => {
    loadAccountManagers();
  }, [loadAccountManagers]);

  useEffect(() => {
    loadProjectManagers();
  }, [loadProjectManagers]);

  const loadPurchaseOrders = async (params?: POPaginationParams) => {
    setLoading(true);
    setError(null);
    try {
      const queryParams: POPaginationParams = {
        ...(params || {}),
        page: poPage,
        page_size: poPageSize,
      };
      const data = await fetchPurchaseOrders(queryParams);
      setPurchaseOrders(data.items);
      setFilteredPurchaseOrders(data.items);
      setPoTotal(data.total);
      setPoTotalPages(data.total_pages);
    } catch (err: any) {
      // 401 Unauthorized - redirect to login (check this first before timeout)
      if (
        err.response?.status === 401 ||
        err.message?.includes("401") ||
        err.message?.includes("Not authenticated")
      ) {
        removeAuthToken();
        // Let AuthWrapper re-authenticate on reload (MSAL session remains if user is logged in).
        window.location.reload();
        return;
      }
      // If it's a timeout or network error
      if (
        err.code === "ECONNABORTED" ||
        err.message?.includes("timeout") ||
        err.code === "ERR_NETWORK" ||
        err.code === "ERR_FAILED"
      ) {
        // Check if backend is actually reachable
        // try {
        //   const healthCheck = await fetch('http://localhost:8000/health', { method: 'GET', signal: AbortSignal.timeout(3000) });
        //   if (healthCheck.ok) {
        //     setError('Request timed out. This might be due to a slow database query or authentication issue. Please try refreshing the page or logging in again.');
        //   } else {
        //     setError('Connection timeout. The backend server may not be running. Please check: 1) Is the backend server running? 2) Is it accessible at http://localhost:8000?');
        //   }
        // } catch {
        //   setError('Connection timeout. The backend server may not be running. Please check: 1) Is the backend server running? 2) Is it accessible at http://localhost:8000?');
        // }
        setLoading(false);
        return;
      }
      // Other errors
      const errorMessage = formatApiError(
        err.response?.data?.detail || err.response?.data || err.message,
      );
      setError(errorMessage || "Failed to load purchase orders");
    } finally {
      setLoading(false);
    }
  };

  const purchaseOrdersFilterKeyRef = useRef<string>("");

  // Filter PurchaseOrders (server-side)
  useEffect(() => {
    const params: POPaginationParams = {
      status: statusFilter !== "All" ? statusFilter : undefined,
      client_name: clientFilter || undefined,
      project_name: projectFilter || undefined,
    };

    const filterKey = JSON.stringify(params);
    if (filterKey !== purchaseOrdersFilterKeyRef.current) {
      purchaseOrdersFilterKeyRef.current = filterKey;
      if (poPage !== 1) {
        setPoPage(1);
        return;
      }
    }

    loadPurchaseOrders(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poPage, poPageSize, statusFilter, clientFilter, projectFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpenDialog = () => {
    setOpenDialog(true);
    setError(null);
    setSuccess(null);
    setFormData({
      client_name: "",
      project_name: "",
      project_type: "",
      geography: "",
      account_manager: "",
      project_manager: "",
      linked_msa: "",
      revenue_type: "",
      revenue_class: "",
      po_date: "",
      po_value_original: null,
      currency: "USD",
      fx_rate_usd: 1.0,
      fx_rate_inr: null,
      payment_terms: "",
      status: "Active",
      remarks: "",
    });
  };

  // Handle currency change (no auto-fetch, just update currency)
  const handleCurrencyChange = (newCurrency: string) => {
    setFormData({ ...formData, currency: newCurrency });
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setError(null);
    setSuccess(null);
    setFormErrors({});
  };

  const isPartiallyCancelled = (status?: string | null) =>
    (status || "").toLowerCase() === "partially cancelled";

  const normalizeCancellationEventsForEdit = (
    events: POCancellationEvent[],
    originalValue: number,
  ): POCancellationEvent[] => {
    const sorted = [...events].sort((a, b) => {
      const ad = new Date(a.date).getTime();
      const bd = new Date(b.date).getTime();
      if (ad !== bd) return ad - bd;
      return a.id.localeCompare(b.id);
    });

    let prev = originalValue;
    return sorted.map((e) => {
      const changeAmount = e.new_value - prev;
      const changePercentage = prev !== 0 ? (changeAmount / prev) * 100 : 0;
      prev = e.new_value;
      return {
        ...e,
        change_amount: changeAmount,
        change_percentage: changePercentage,
      };
    });
  };

  const resetEditPOCancellationDraftForm = () => {
    setEditPOEditingCancellationId(null);
    setEditPONewCancellationValue(null);
    setEditPONewCancellationDate("");
    setEditPONewCancellationDescription("");
  };

  const handleOpenEditPO = (po: PurchaseOrder) => {
    console.log("handleOpenEditPO called:");
    setOpenPODetail(false);
    setEditingPO(po);
    setEditFormData({
      client_name: po.client_name || "",
      project_name: po.project_name || "",
      project_type: po.project_type || "",
      geography: po.geography || "",
      account_manager: po.account_manager || "",
      project_manager: po.project_manager || "",
      linked_msa: po.linked_msa || "",
      revenue_type: po.revenue_type || "",
      revenue_class: po.revenue_class || "",
      po_date: po.po_date || null,
      po_value_original: po.po_value_original || null,
      currency: po.currency || "USD",
      fx_rate_usd: po.fx_rate_usd || null,
      fx_rate_inr: po.fx_rate_inr || null,
      payment_terms: po.payment_terms || "",
      status: po.status || "Active",
      remarks: po.remarks || "",
    });

// Value before first cancellation: use first event's stored math when present, else current PO value. Never use po_value_initial (e.g. 40) here so we don't confuse with current value (e.g. 3333).
    const events =
      po.cancellation_events && Array.isArray(po.cancellation_events)
        ? (po.cancellation_events as any as POCancellationEvent[])
        : [];

      const sorted = [...events].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
      const first = sorted[0];
      const originalValue =
        first != null && first.change_amount != null && Number.isFinite(first.change_amount)
          ? first.new_value - first.change_amount
          : Number(po.po_value_original) || 0;
      setEditPOOriginalValue(originalValue);

    const normalized = normalizeCancellationEventsForEdit(events, originalValue);
    setEditPOCancellationEvents(normalized);
    resetEditPOCancellationDraftForm();

    if (isPartiallyCancelled(po.status) && normalized.length > 0) {
      setEditFormData((prev) => ({
        ...prev,
        po_value_original: normalized[normalized.length - 1].new_value,
      }));
    }
    setOpenEditDialog(true);
    setError(null);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setEditingPO(null);
    setError(null);
    setSuccess(null);
    setEditFormErrors({});
    setEditPOCancellationEvents([]);
    setEditPOOriginalValue(0);
    resetEditPOCancellationDraftForm();
  };

  const handleEditPOUpsertCancellationEvent = () => {
    if (!isPartiallyCancelled(editFormData.status)) return;
    if (!editPONewCancellationDate || editPONewCancellationValue === null) return;

    const nextEvent: POCancellationEvent = {
      id: editPOEditingCancellationId || `cancel-${makeClientId()}`,
      date: editPONewCancellationDate,
      new_value: editPONewCancellationValue,
      change_amount: 0,
      change_percentage: 0,
      description: editPONewCancellationDescription || null,
    };

    const nextEvents = editPOEditingCancellationId
      ? editPOCancellationEvents.map((e) =>
          e.id === editPOEditingCancellationId ? nextEvent : e,
        )
      : [...editPOCancellationEvents, nextEvent];

    const normalized = normalizeCancellationEventsForEdit(nextEvents, editPOOriginalValue);
    setEditPOCancellationEvents(normalized);

    if (normalized.length > 0) {
      setEditFormData((prev) => ({
        ...prev,
        po_value_original: normalized[normalized.length - 1].new_value,
      }));
    }

    resetEditPOCancellationDraftForm();
  };

  const handleEditPODeleteCancellationEvent = (eventId: string) => {
    const remaining = editPOCancellationEvents.filter((e) => e.id !== eventId);
    const normalized = normalizeCancellationEventsForEdit(remaining, editPOOriginalValue);
    setEditPOCancellationEvents(normalized);

    const nextValue =
      normalized.length > 0 ? normalized[normalized.length - 1].new_value : editPOOriginalValue;
    setEditFormData((prev) => ({ ...prev, po_value_original: nextValue }));

    if (editPOEditingCancellationId === eventId) {
      resetEditPOCancellationDraftForm();
    }
  };

  const handleEditPOStartEditCancellationEvent = (event: POCancellationEvent) => {
    setEditPOEditingCancellationId(event.id);
    setEditPONewCancellationDate(event.date);
    setEditPONewCancellationValue(event.new_value);
    setEditPONewCancellationDescription(event.description || "");
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingPO) return;

    const isEditPartial = isPartiallyCancelled(editFormData.status);

    // Validation
    const errors: Record<string, string> = {};
    if (!editFormData.po_date) errors.po_date = "PO Date is required";
    if (!editFormData.status) errors.status = "Status is required";
    if (!editFormData.client_name?.trim())
      errors.client_name = "Client Name is required";
    if (!editFormData.project_name?.trim())
      errors.project_name = "Project Name is required";
    if (!editFormData.project_type)
      errors.project_type = "Project Type is required";
    if (!editFormData.geography) errors.geography = "Geography is required";
    if (!editFormData.account_manager?.trim())
      errors.account_manager = "Account Manager is required";
    if (!editFormData.project_manager?.trim())
      errors.project_manager = "Project Manager is required";
    if (!editFormData.revenue_type)
      errors.revenue_type = "Revenue Type is required";
    if (!editFormData.revenue_class)
      errors.revenue_class = "Revenue Class is required";
    if (!editFormData.po_value_original && editFormData.po_value_original !== 0)
      errors.po_value_original = "PO Value is required";
    if (!editFormData.currency) errors.currency = "Currency is required";
  // linked_msa is optional
    if (!editFormData.fx_rate_inr)
      errors.fx_rate_inr = "FX Rate (INR) is required";
    if (!editFormData.fx_rate_usd)
      errors.fx_rate_usd = "FX Rate (USD) is required";
    if (!editFormData.payment_terms?.trim())
      errors.payment_terms = "Payment Terms is required";

    if (isEditPartial && editPOCancellationEvents.length === 0) {
      errors.status = "Add at least one cancellation event for Partially Cancelled";
    }

    if (Object.keys(errors).length > 0) {
      setEditFormErrors(errors);
      return;
    }

    setSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      // If PO value is being changed, also update po_value_initial to preserve the new original value
      // This is the ONLY place where po_value_initial should be updated
      const poValueChanged =
        editingPO &&
        editFormData.po_value_original !== editingPO.po_value_original &&
        !isEditPartial;

      const normalizedCancelEvents = isEditPartial
        ? normalizeCancellationEventsForEdit(editPOCancellationEvents, editPOOriginalValue)
        : [];
      const poValueFromCancellation =
        isEditPartial && normalizedCancelEvents.length > 0
          ? normalizedCancelEvents[normalizedCancelEvents.length - 1].new_value
          : editFormData.po_value_original;

      const submitData: PurchaseOrderUpdateInput = {
        client_name: editFormData.client_name?.trim() || null,
        project_name: editFormData.project_name?.trim() || null,
        project_type: editFormData.project_type?.trim() || null,
        geography: editFormData.geography?.trim() || null,
        account_manager: editFormData.account_manager?.trim() || null,
        project_manager: editFormData.project_manager?.trim() || null,
        linked_msa: editFormData.linked_msa?.trim() || null,
        revenue_type: editFormData.revenue_type?.trim() || null,
        revenue_class: editFormData.revenue_class?.trim() || null,
        po_date: editFormData.po_date || null,
        po_value_original: (poValueFromCancellation as any) || null,
        // IMPORTANT: Update po_value_initial ONLY if PO value is being changed in the edit dialog
        // This ensures the original value can only be changed through PO edit, not through cancellation events
        po_value_initial: poValueChanged
          ? editFormData.po_value_original || null
          : undefined,
        currency: editFormData.currency || null,
        fx_rate_usd: editFormData.fx_rate_usd || null,
        fx_rate_inr: editFormData.fx_rate_inr || null,
        payment_terms: editFormData.payment_terms?.trim() || null,
        status: editFormData.status || null,
        remarks: editFormData.remarks?.trim() || null,
        cancellation_events: isEditPartial
          ? (normalizedCancelEvents.length > 0 ? (normalizedCancelEvents as any) : null)
          : null,
      };

      await updatePurchaseOrder(editingPO.id, submitData);
      setSuccess("Purchase Order updated successfully!");
      setOpenEditDialog(false);
      await loadPurchaseOrders();
      await fetchData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      const errorMessage = formatApiError(
        err.response?.data?.detail || err.response?.data || err.message,
      );
      setError(errorMessage || "Failed to update purchase order");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    const errors: Record<string, string> = {};
    if (!formData.po_date) errors.po_date = "PO Date is required";
    if (!formData.status) errors.status = "Status is required";
    if (!formData.client_name?.trim())
      errors.client_name = "Client Name is required";
    if (!formData.project_name?.trim())
      errors.project_name = "Project Name is required";
    if (!formData.project_type)
      errors.project_type = "Project Type is required";
    if (!formData.geography) errors.geography = "Geography is required";
    if (!formData.account_manager?.trim())
      errors.account_manager = "Account Manager is required";
    if (!formData.project_manager?.trim())
      errors.project_manager = "Project Manager is required";
    if (!formData.revenue_type)
      errors.revenue_type = "Revenue Type is required";
    if (!formData.revenue_class)
      errors.revenue_class = "Revenue Class is required";
    if (!formData.po_value_original && formData.po_value_original !== 0)
      errors.po_value_original = "PO Value is required";
    if (!formData.currency) errors.currency = "Currency is required";
  // linked_msa is optional
    if (!formData.fx_rate_inr) errors.fx_rate_inr = "FX Rate (INR) is required";
    if (!formData.fx_rate_usd) errors.fx_rate_usd = "FX Rate (USD) is required";
    if (!formData.payment_terms?.trim())
      errors.payment_terms = "Payment Terms is required";

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      const submitData: PurchaseOrderCreateInput = {
        ...formData,
        client_name: formData.client_name?.trim() || null,
        project_name: formData.project_name?.trim() || null,
        project_type: formData.project_type?.trim() || null,
        geography: formData.geography?.trim() || null,
        account_manager: formData.account_manager?.trim() || null,
        project_manager: formData.project_manager?.trim() || null,
        linked_msa: formData.linked_msa?.trim() || null,
        revenue_type: formData.revenue_type?.trim() || null,
        revenue_class: formData.revenue_class?.trim() || null,
        po_date: formData.po_date || null,
        payment_terms: formData.payment_terms?.trim() || null,
        status: formData.status || "Active",
        remarks: formData.remarks?.trim() || null,
        // Set po_value_initial = po_value_original when creating (preserve original value)
        po_value_initial: formData.po_value_original || null,
      };

      await createPurchaseOrder(submitData);
      setSuccess("Purchase Order created successfully!");
      setOpenDialog(false);
      await loadPurchaseOrders();
      await fetchData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      const errorMessage = formatApiError(
        err.response?.data?.detail || err.response?.data || err.message,
      );
      setError(errorMessage || "Failed to create purchase order");
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenPODetail = async (po: PurchaseOrder) => {
    setOpenPODetail(true);
    setPODetailPO(po);
    setPOStatus(po.status || "");
    setPORemarks(po.remarks || "");

       // Refetch PO by ID so view details always show correct server state (e.g. after cancellation events)
       try {
         const fresh = await getPurchaseOrder(po.id);
         setPODetailPO(fresh);
         setPOStatus(fresh.status || "");
         setPORemarks(fresh.remarks || "");
         po = fresh;
       } catch {
         // keep using passed-in po if fetch fails
       }
   
       // Load cancellation events from backend  
    const events =
    po.cancellation_events && Array.isArray(po.cancellation_events)
      ? (po.cancellation_events as any as POCancellationEvent[])
      : [];

    const sorted = [...events].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    const first = sorted[0];
    const originalValue =
      first != null && first.change_amount != null && Number.isFinite(first.change_amount)
        ? first.new_value - first.change_amount
        : Number(po.po_value_original) || 0;
    console.log("[originalValue]: ",originalValue);
    setEditPOOriginalValue(originalValue);
    setCancellationEvents(events);

    // Original PO value is stored in po_value_initial (never changes)
    // If po_value_initial is not set (old records), use po_value_original as fallback
    // This value should NEVER be updated except through PO edit
    setOriginalPOValue(po.po_value_initial ?? po.po_value_original);

    setNewCancellationValue(null);
    setNewCancellationDate("");
    setNewCancellationDescription("");
    setEditingCancellationEvent(null);
    setEditCancellationValue(null);
    setEditCancellationDate("");
    setEditCancellationDescription("");
    setOpenPODetail(true);
    setLoadingPODetailInvoices(true);
    try {
      const invoicesData = await fetchInvoicesForPO(po.id);
      setPODetailInvoices(invoicesData);
    } catch (err: any) {
    } finally {
      setLoadingPODetailInvoices(false);
    }
  };

  const togglePOListCardFinancial = useCallback((po: PurchaseOrder) => {
    setExpandedPOCardIds((prev) => {
      const next = new Set(prev);
      if (next.has(po.id)) next.delete(po.id);
      else next.add(po.id);
      return next;
    });
  }, []);

  const expandedPOCardIdsKey = useMemo(
    () => Array.from(expandedPOCardIds).sort((a, b) => a - b).join(","),
    [expandedPOCardIds],
  );

  useEffect(() => {
    if (!expandedPOCardIdsKey) return;
    const ids = expandedPOCardIdsKey.split(",").map(Number);
    ids.forEach((poId) => {
      if (poListInvoiceFetchStartedRef.current.has(poId)) return;
      poListInvoiceFetchStartedRef.current.add(poId);
      fetchInvoicesForPO(poId)
        .then((invoices) => {
          setPOListInvoicesByPOId((c) => ({ ...c, [poId]: invoices }));
        })
        .catch(() => {
          setPOListInvoicesByPOId((c) => ({ ...c, [poId]: [] }));
        });
    });
  }, [expandedPOCardIdsKey]);

  const handleUpdateCancellationEvent = async () => {
    if (
      !poDetailPO ||
      !editingCancellationEvent ||
      editCancellationValue === null ||
      !editCancellationDate
    )
      return;

     // Get the current value before this event (for calculating change).
    // For the first event: use stored change_amount to derive value before, so we use the PO value before any cancellation (not po_value_initial).
    const eventIndex = cancellationEvents.findIndex(
      (e) => e.id === editingCancellationEvent.id,
    );
    const originalValue =
      poDetailPO.po_value_initial ??
      originalPOValue ??
      poDetailPO.po_value_original ??
      0;
   const previousValue =
      eventIndex === 0
        ? (editingCancellationEvent.change_amount !== undefined &&
          editingCancellationEvent.change_amount !== null
          ? editingCancellationEvent.new_value - editingCancellationEvent.change_amount
          : originalValue)
        : cancellationEvents[eventIndex - 1].new_value;

    // Calculate change
    const changeAmount = editCancellationValue - previousValue;
    const changePercentage =
      previousValue !== 0 ? (changeAmount / previousValue) * 100 : 0;

    // Update the event
    const updatedEvents = cancellationEvents.map((event) =>
      event.id === editingCancellationEvent.id
        ? {
            ...event,
            date: editCancellationDate,
            new_value: editCancellationValue,
            change_amount: changeAmount,
            change_percentage: changePercentage,
            description: editCancellationDescription || null,
          }
        : event,
    );

    // If this is the last event, update the PO value
    const isLastEvent = eventIndex === cancellationEvents.length - 1;
    const newPOValue = isLastEvent
      ? editCancellationValue
      : poDetailPO.po_value_original;

    setUpdatingPO(true);
    try {
      const updateData: PurchaseOrderUpdateInput = {
        po_value_original: isLastEvent ? newPOValue : undefined,
        cancellation_events: updatedEvents as any,
      };
      const updated = await updatePurchaseOrder(poDetailPO.id, updateData);
      setPODetailPO(updated);
      setCancellationEvents(updatedEvents);

      // Reset edit form
      setEditingCancellationEvent(null);
      setEditCancellationValue(null);
      setEditCancellationDate("");
      setEditCancellationDescription("");

      await loadPurchaseOrders();
      setSuccess("Cancellation event updated successfully!");
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      const errorMessage = formatApiError(
        err.response?.data?.detail || err.response?.data || err.message,
      );
      setError(errorMessage || "Failed to update cancellation event");
    } finally {
      setUpdatingPO(false);
    }
  };

  const handleDeleteCancellationEvent = async (eventId: string) => {
    if (!poDetailPO) return;

    const eventIndex = cancellationEvents.findIndex((e) => e.id === eventId);
    if (eventIndex === -1) return;

    const isLastEvent = eventIndex === cancellationEvents.length - 1;

    // Remove the event
    const updatedEvents = cancellationEvents.filter((e) => e.id !== eventId);

    // If this was the last event, update PO value to the previous event's value (or original if no events left)
    // Original value (po_value_initial) should NEVER be changed here
    let newPOValue = poDetailPO.po_value_original;
    const originalValue =
      poDetailPO.po_value_initial ??
      originalPOValue ??
      poDetailPO.po_value_original;
    if (isLastEvent && updatedEvents.length > 0) {
      newPOValue = updatedEvents[updatedEvents.length - 1].new_value;
    } else if (isLastEvent && updatedEvents.length === 0) {
      // No events left, restore to original value
      newPOValue = originalValue;
    }

    setUpdatingPO(true);
    try {
      const updateData: PurchaseOrderUpdateInput = {
        po_value_original: isLastEvent ? newPOValue : undefined,
        cancellation_events:
          updatedEvents.length > 0 ? (updatedEvents as any) : null,
      };
      const updated = await updatePurchaseOrder(poDetailPO.id, updateData);
      setPODetailPO(updated);
      setCancellationEvents(updatedEvents);

      await loadPurchaseOrders();
      setSuccess("Cancellation event deleted successfully!");
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      const errorMessage = formatApiError(
        err.response?.data?.detail || err.response?.data || err.message,
      );
      setError(errorMessage || "Failed to delete cancellation event");
    } finally {
      setUpdatingPO(false);
    }
  };

  const handleClosePODetail = () => {
    setOpenPODetail(false);
    setPODetailPO(null);
    setPODetailInvoices([]);
    setPOStatus("");
    setPORemarks("");
    setEditingCancellationEvent(null);
    setEditCancellationValue(null);
    setEditCancellationDate("");
    setEditCancellationDescription("");
    setCancellationEvents([]);
    setNewCancellationValue(null);
    setNewCancellationDate("");
    setNewCancellationDescription("");
    setOriginalPOValue(null);
  };

  const handleUpdatePOStatus = async (newStatus: string) => {
    if (!poDetailPO) return;
    setUpdatingPO(true);
    try {
      const updateData: PurchaseOrderUpdateInput = { status: newStatus };
      const updated = await updatePurchaseOrder(poDetailPO.id, updateData);
      setPODetailPO(updated);
      setPOStatus(newStatus);
      // Reset cancellation events if status changes away from Partially Cancelled
      if (newStatus !== "Partially Cancelled") {
        setCancellationEvents([]);
        setNewCancellationValue(null);
        setNewCancellationDate("");
      }
      await loadPurchaseOrders();
    } catch (err: any) {
      const errorMessage = formatApiError(
        err.response?.data?.detail || err.response?.data || err.message,
      );
      setError(errorMessage || "Failed to update PO status");
    } finally {
      setUpdatingPO(false);
    }
  };

  const handleAddCancellationEvent = async () => {
    if (!poDetailPO || newCancellationValue === null || !newCancellationDate)
      return;


    // Value before this cancellation = current PO value (po_value_original). Use Number() so we never confuse with po_value_initial (e.g. 3333 vs 40).
    const currentValue = Number(poDetailPO?.po_value_original) || 0;

    // Calculate change (negative = reduction from current value)
    const changeAmount = newCancellationValue - currentValue;
    const changePercentage =
      currentValue !== 0 ? (changeAmount / currentValue) * 100 : 0;

    const newEvent: POCancellationEvent = {
      id: `cancel-${makeClientId()}`,
      date: newCancellationDate,
      new_value: newCancellationValue,
      change_amount: changeAmount,
      change_percentage: changePercentage,
      description: newCancellationDescription || null,
    };

    const updatedEvents = [...cancellationEvents, newEvent];

    setUpdatingPO(true);
    try {
      // Update PO with cancellation events and new PO value
      // po_value_original gets updated to the new value (this is the "PO Value" used for calculations)
      // po_value_initial remains unchanged (preserves the original value)
      const updateData: PurchaseOrderUpdateInput = {
        po_value_original: newCancellationValue, // Update to new value for calculations
        cancellation_events: updatedEvents as any,
      };
      const updated = await updatePurchaseOrder(poDetailPO.id, updateData);
      setPODetailPO(updated);

      // Update local state
      setCancellationEvents(updatedEvents);

      // Reset form
      setNewCancellationValue(null);
      setNewCancellationDate("");
      setNewCancellationDescription("");

      await loadPurchaseOrders();
      setSuccess("Cancellation event recorded successfully!");
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      const errorMessage = formatApiError(
        err.response?.data?.detail || err.response?.data || err.message,
      );
      setError(errorMessage || "Failed to record cancellation event");
    } finally {
      setUpdatingPO(false);
    }
  };

  const handleUpdatePORemarks = async () => {
    if (!poDetailPO) return;
    setUpdatingPO(true);
    try {
      const updateData: PurchaseOrderUpdateInput = {
        remarks: poRemarks || null,
      };
      const updated = await updatePurchaseOrder(poDetailPO.id, updateData);
      setPODetailPO(updated);
      await loadPurchaseOrders();
      setSuccess("PO remarks updated successfully!");
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      const errorMessage = formatApiError(
        err.response?.data?.detail || err.response?.data || err.message,
      );
      setError(errorMessage || "Failed to update PO remarks");
    } finally {
      setUpdatingPO(false);
    }
  };

  const handleUpdatePOField = async (field: string, value: any) => {
    if (!poDetailPO) return;
    setUpdatingPO(true);
    try {
      const updateData: PurchaseOrderUpdateInput = { [field]: value };
      const updated = await updatePurchaseOrder(poDetailPO.id, updateData);
      setPODetailPO(updated);
      await loadPurchaseOrders();
      setSuccess(`PO ${field.replace("_", " ")} updated successfully!`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      const errorMessage = formatApiError(
        err.response?.data?.detail || err.response?.data || err.message,
      );
      setError(errorMessage || `Failed to update PO ${field}`);
    } finally {
      setUpdatingPO(false);
    }
  };

  const handleOpenInvoicePlanner = async (po: PurchaseOrder) => {
    setSelectedPO(po);
    setOpenInvoicePlanner(true);
    setPlanError(null);
    setPlanSuccess(null);
    setInvoicePlanSubmitAttempted(false);
    const defaultDescription = po.remarks || po.project_name || "";
    // Use PO date as default for planned dates
    const defaultDate = po.po_date || "";
    setInvoicePlanRows([
      {
        id: `row-${makeClientId()}`,
        description: defaultDescription,
        planned_date: defaultDate,
        planned_amount: null,
        service_month: "",
      },
    ]);
  };

  const handleCloseInvoicePlanner = () => {
    setOpenInvoicePlanner(false);
    setSelectedPO(null);
    setInvoicePlanRows([]);
    setPlanError(null);
    setPlanSuccess(null);
    setInvoicePlanSubmitAttempted(false);
  };

  const handleAddInvoiceRow = () => {
    const defaultDescription =
      selectedPO?.remarks || selectedPO?.project_name || "";
    // Use PO date as default for planned dates
    const defaultDate = selectedPO?.po_date || "";
    setInvoicePlanRows([
      ...invoicePlanRows,
      {
        id: `row-${makeClientId()}`,
        description: defaultDescription,
        planned_date: defaultDate,
        planned_amount: null,
        service_month: "",
      },
    ]);
  };

  const handleRemoveInvoiceRow = (rowId: string) => {
    setInvoicePlanRows(invoicePlanRows.filter((row) => row.id !== rowId));
  };

  const handleUpdateInvoiceRow = (
    rowId: string,
    field: keyof InvoicePlanRow,
    value: any,
  ) => {
    setInvoicePlanRows(
      invoicePlanRows.map((row) =>
        row.id === rowId ? { ...row, [field]: value } : row,
      ),
    );
  };

  const handleSaveInvoicePlan = async () => {
    if (!selectedPO) return;
    setInvoicePlanSubmitAttempted(true);
    setSavingPlan(true);
    setPlanError(null);
    setPlanSuccess(null);
    try {
      const validRows = invoicePlanRows.filter(
        (row) =>
          row.description &&
          row.description.trim().length > 0 &&
          row.planned_date &&
          row.planned_amount !== null &&
          row.planned_amount > 0,
      );
      if (validRows.length === 0) {
        setPlanError(
          "Please add at least one invoice with a description, planned date and amount.",
        );
        setSavingPlan(false);
        return;
      }
      const convertMonthToDate = (monthValue: string | null): string | null => {
        if (!monthValue || monthValue.length === 0) return null;
        if (monthValue.length === 10) return monthValue;
        if (monthValue.length === 7 && monthValue.match(/^\d{4}-\d{2}$/)) {
          return `${monthValue}-01`;
        }
        return null;
      };
      const invoicesData = validRows.map((row) => {
        // Backend will auto-generate invoice_code and inherit PO details
        // Backend will also default finance_planned_date and operational_planned_date from PO date if not provided
        return {
          po_id: selectedPO.id,
          description: row.description.trim(),
          est_value_original: row.planned_amount,
          // client_name, project_name, currency, fx_rate, payment_terms will be inherited from PO by backend
          // finance_planned_date and operational_planned_date: use row.planned_date if set, otherwise backend will default to PO date
          finance_planned_date: row.planned_date || null,
          operational_planned_date: row.planned_date || null,
          service_month_date: convertMonthToDate(row.service_month),
          status: "Planned",
        };
      });

      await createInvoiceForPO(selectedPO.id, invoicesData);
      setPlanSuccess(
        `Successfully created ${validRows.length} planned invoice(s)!`,
      );
      setTimeout(() => {
        handleCloseInvoicePlanner();
        loadPurchaseOrders();
      }, 2000);
    } catch (err: any) {
      const errorMessage = formatApiError(
        err.response?.data?.detail || err.response?.data || err.message,
      );
      setPlanError(errorMessage || "Failed to save invoice plan");
    } finally {
      setSavingPlan(false);
    }
  };

  const handleDeletePO = (po: PurchaseOrder) => {
    setDeletingPO(po);
    setDeletePODialogOpen(true);
  };

  const handleConfirmDeletePO = async () => {
    if (!deletingPO) return;

    setDeletingPOLoading(true);
    try {
      await deletePurchaseOrder(deletingPO.id);
      setSnackbar({
        open: true,
        message: `Purchase Order ${deletingPO.po_code} deleted successfully!`,
        severity: "success",
      });
      await loadPurchaseOrders();
      await fetchData();
      setDeletePODialogOpen(false);
      setDeletingPO(null);
    } catch (err: any) {
      const errorMessage = formatApiError(
        err.response?.data?.detail || err.response?.data || err.message,
      );
      setSnackbar({
        open: true,
        message: errorMessage || "Failed to delete purchase order",
        severity: "error",
      });
    } finally {
      setDeletingPOLoading(false);
    }
  };

  const handleCloseDeletePODialog = () => {
    if (!deletingPOLoading) {
      setDeletePODialogOpen(false);
      setDeletingPO(null);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const formatCurrency = (value: number | null, currency: string | null) => {
    if (value === null || value === undefined) return "-";
    const currencyUpper = currency ? currency.toUpperCase() : "";
    const currencySymbol =
      currencyUpper === "USD"
        ? "$"
        : currencyUpper === "EUR"
          ? "€"
          : currencyUpper === "INR"
            ? "₹"
            : "";
    return `${currencySymbol}${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const SummaryCardformatCurrency = (value?: number) => {
    if (value === undefined || value === null) return "-";
    return `$${value.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  // Calculate USD value from original value and FX rate (Base to USD)
  const calculateUSDValue = (
    originalValue: number | null,
    fxRateUSD: number | null,
  ): number | null => {
    if (
      originalValue === null ||
      originalValue === undefined ||
      fxRateUSD === null ||
      fxRateUSD === undefined
    )
      return null;
    return originalValue * fxRateUSD;
  };

  const csvEscape = (value: unknown): string => {
    if (value === null || value === undefined) return "";
    const text = String(value);
    if (/[",\n\r]/.test(text)) {
      return `"${text.replace(/"/g, '""')}"`;
    }
    return text;
  };

  const formatCsvDate = (dateString: string | null | undefined): string => {
    if (!dateString) return "";
    const parsed = dayjs(dateString);
    return parsed.isValid() ? parsed.format("D-MMM-YY") : dateString;
  };

  const extractMilestoneFromRemarks = (
    remarks: string | null | undefined,
  ): string => {
    if (!remarks) return "";
    const match = remarks.match(/Milestone:\s*([^|]+)/i);
    return match ? match[1].trim() : "";
  };

  const fetchAllPOForExport = async () =>
    fetchPOFullList({
      status: statusFilter !== "All" ? statusFilter : undefined,
      client_name: clientFilter || undefined,
      project_name: projectFilter || undefined,
    });

  const handleExportPOCsv = async () => {
    try {
      setExportingPOCsv(true);
      const allPOs = await fetchAllPOForExport();
      if (allPOs.length === 0) {
        setSnackbar({
          open: true,
          message: "No purchase orders found for export.",
          severity: "error",
        });
        return;
      }

      const headers = [
        "PO_ID / SOW_ID",
        "PO_title",
        "Rev Type",
        "Rev Class",
        "AM",
        "PM",
        "Geo",
        "Account",
        "Project",
        "Project_Type",
        "milestone_desc",
        "PO_Date",
        " PO_Value ",
        "Currency",
        "Exchange Rate",
        " PO_Value_USD ",
        "Payment Terms",
        "Status",
        "Referenced_MSA",
        "Remarks",
      ];

      const rows = allPOs.map((po) => {
        const poValueUsd = calculateUSDValue(
          po.po_value_original,
          po.fx_rate_usd,
        );
        return [
          po.po_excel_id || po.po_code || "",
          po.po_title || "",
          po.revenue_type || "",
          po.revenue_class || "",
          po.account_manager || "",
          po.project_manager || "",
          po.geography || "",
          po.client_name || "",
          po.project_name || "",
          po.project_type || "",
          extractMilestoneFromRemarks(po.remarks),
          formatCsvDate(po.po_date),
          po.po_value_original ?? "",
          po.currency || "",
          po.fx_rate_usd ?? "",
          poValueUsd ?? "",
          po.payment_terms || "",
          po.status || "",
          po.linked_msa || "",
          po.remarks || "",
        ];
      });

      const csvContent = [
        headers.map(csvEscape).join(","),
        ...rows.map((row) => row.map(csvEscape).join(",")),
      ].join("\r\n");

      const blob = new Blob([csvContent], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `purchase_orders_export_${dayjs().format("YYYYMMDD_HHmmss")}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setSnackbar({
        open: true,
        message: `Exported ${allPOs.length} purchase orders to CSV.`,
        severity: "success",
      });
    } catch (err: any) {
      const errorMessage = formatApiError(
        err.response?.data?.detail || err.response?.data || err.message,
      );
      setSnackbar({
        open: true,
        message: errorMessage || "Failed to export purchase orders.",
        severity: "error",
      });
    } finally {
      setExportingPOCsv(false);
    }
  };

  // Calculate INR value from original value and FX rate (Base to INR)
  const calculateINRValue = (
    originalValue: number | null,
    fxRateINR: number | null,
  ): number | null => {
    if (
      originalValue === null ||
      originalValue === undefined ||
      fxRateINR === null ||
      fxRateINR === undefined
    )
      return null;
    return originalValue * fxRateINR;
  };

  return (
    <Box sx={{ maxWidth: "100%", mx: "auto", position: "relative" }}>
      {/* Summary cards */}
      {/* Summary Cards */}

      <Grid
        container
        spacing={{ xs: 1, sm: 1.25, md: 1, lg: 2 }}
        sx={{
          mb: { xs: 0.5, sm: 0.5, md: 1 },
          px: { xs: 0.5, sm: 1, md: 0 },
        }}
      >
        <Grid
          item
          xs={12}
          sm={6}
          md={6}
          sx={{
            "@media (min-width: 1400px)": { flexBasis: "25%", maxWidth: "25%" },
          }}
        >
          <CompactMetricCard
            title="Total POs"
            value={summary?.total_pos || 0}
            icon={Assignment}
            color="primary"
            loading={loading}
          />
        </Grid>
        <Grid
          item
          xs={12}
          sm={6}
          md={6}
          sx={{
            "@media (min-width: 1400px)": { flexBasis: "25%", maxWidth: "25%" },
          }}
        >
          <CompactMetricCard
            title="Total Value"
            value={SummaryCardformatCurrency(summary?.total_value_usd)}
            icon={AttachMoney}
            color="primary"
            loading={loading}
          />
        </Grid>
        <Grid
          item
          xs={12}
          sm={6}
          md={6}
          sx={{
            "@media (min-width: 1400px)": { flexBasis: "25%", maxWidth: "25%" },
          }}
        >
          <CompactMetricCard
            title="Active POs"
            value={summary?.active_pos || 0}
            subtitle={`Value: ${SummaryCardformatCurrency(summary?.active_value_usd)}`}
            icon={CheckCircle}
            color="success"
            loading={loading}
          />
        </Grid>
        <Grid
          item
          xs={12}
          sm={6}
          md={6}
          sx={{
            "@media (min-width: 1400px)": { flexBasis: "25%", maxWidth: "25%" },
          }}
        >
          <CompactMetricCard
            title="Expired POs"
            value={summary?.expired_pos || 0}
            subtitle={`Value: ${SummaryCardformatCurrency(summary?.expired_value_usd)}`}
            icon={Schedule}
            color="info"
            loading={loading}
          />
        </Grid>
        <Grid
          item
          xs={12}
          sm={6}
          md={6}
          sx={{
            "@media (min-width: 1400px)": { flexBasis: "25%", maxWidth: "25%" },
          }}
        >
          <CompactMetricCard
            title="Cancelled POs"
            value={summary?.cancelled_pos || 0}
            subtitle={`Value: ${SummaryCardformatCurrency(summary?.cancelled_value_usd)}`}
            icon={Cancel}
            color="error"
            loading={loading}
          />
        </Grid>
        <Grid
          item
          xs={12}
          sm={6}
          md={6}
          sx={{
            "@media (min-width: 1400px)": { flexBasis: "25%", maxWidth: "25%" },
          }}
        >
          <CompactMetricCard
            title="Average PO Value"
            value={SummaryCardformatCurrency(summary?.avg_po_value)}
            icon={TrendingUp}
            color="secondary"
            loading={loading}
          />
        </Grid>
        <Grid
          item
          xs={12}
          sm={6}
          md={6}
          sx={{
            "@media (min-width: 1400px)": { flexBasis: "25%", maxWidth: "25%" },
          }}
        >
          <CompactMetricCard
            title="Largest PO"
            value={SummaryCardformatCurrency(summary?.largest_po?.value)}
            subtitle={`${summary?.largest_po?.account || "N/A"} - ${summary?.largest_po?.project || "N/A"}`}
            icon={AttachMoney}
            color="warning"
            loading={loading}
          />
        </Grid>
        <Grid
          item
          xs={12}
          sm={6}
          md={6}
          sx={{
            "@media (min-width: 1400px)": { flexBasis: "25%", maxWidth: "25%" },
          }}
        >
          <CompactMetricCard
            title="Most Recent PO"
            value={
              summary?.most_recent_po?.date
                ? formatDate(summary.most_recent_po.date)
                : "N/A"
            }
            subtitle={`${summary?.most_recent_po?.account || "N/A"} - ${summary?.most_recent_po?.project || "N/A"}`}
            icon={Schedule}
            color="info"
            loading={loading}
          />
        </Grid>
      </Grid>
      {/* End of summary cards */}

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: { xs: 1, sm: 1.5, md: 2 },
          flexWrap: "wrap",
          gap: { xs: 2, sm: 0 },
        }}
      >
        {/* <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 700,
            // Keep heading compact on md/lg/xl
            fontSize: {
              xs: "1rem",
              sm: "1.15rem",
              md: "clamp(1.15rem, 0.8vw, 1.3rem)",
            },
            lineHeight: 1.2,
          }}
        >
          Purchase Orders
        </Typography> */}
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenDialog}
          sx={{
            ml: "auto",
            fontSize: {
              xs: "0.75rem",
              sm: "0.8rem",
              md: "0.875rem",
              lg: "0.9rem",
              xl: "0.9rem",
            },
            px: { xs: 2, sm: 3 },
            py: { xs: 0.8, sm: 1 },
            height: { xs: 36, sm: 40, md: 44 },
            whiteSpace: "nowrap",
          }}
          disabled={can("write") === false}
        >
          New PO
        </Button>
      </Box>

      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      {error && !openDialog && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      <Box>
          <Paper
            sx={{
              p: { xs: 0.5, sm: 0.5, md: 1.5, lg: 2 },
              mb: { xs: 0.5, sm: 0.5, md: 1, lg: 2 },
            }}
          >
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                fontWeight: 700,
                mb: { xs: 1.5, sm: 2 },
                pb: 1,
                borderBottom: 2,
                borderColor: "primary.main",
                fontSize: {
                  xs: "0.95rem",
                  sm: "1.05rem",
                  md: "1.05rem",
                  lg: "1.05rem",
                },
                        }}
            >
              Filter Purchase Orders
            </Typography>

            <Grid container spacing={{ xs: 1, sm: 1.25, md: 1.5, lg: 2 }}>
             
             {/* status filter */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl
                  fullWidth
                  size="small"
                  sx={{
                    "& .MuiInputLabel-root": {
                      fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" },
                    },
                    "& .MuiSelect-select": {
                      fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                    },
                  }}
                >
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="All">All Statuses</MenuItem>
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Expired">Expired</MenuItem>
                    <MenuItem value="Cancelled">Cancelled</MenuItem>
                    <MenuItem value="Partially Cancelled">
                      Partially Cancelled
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {/* client filter */}
              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  options={poFilterClientNames}
                  value={clientFilter || null}
                  onChange={(_e, newValue) => setClientFilter(newValue || "")}
                  loading={loadingPOFilterClients}
                  noOptionsText={
                    loadingPOFilterClients
                      ? "Loading clients..."
                      : poFilterClientNamesError
                        ? "Failed to load clients"
                        : "No clients available"
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      size="small"
                      label="Client"
                      placeholder="Type to search..."
                      sx={{
                        "& .MuiInputBase-input": {
                          fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                        },
                        "& .MuiInputLabel-root": {
                          fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" },
                        },
                      }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingPOFilterClients ? (
                              <CircularProgress color="inherit" size={16} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
              {/* project filter */}
              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  options={poFilterProjectNames}
                  value={projectFilter || null}
                  onChange={(_e, newValue) =>
                    setProjectFilter(newValue || "")
                  }
                  loading={loadingPOFilterProjects}
                  noOptionsText={
                    loadingPOFilterProjects
                      ? "Loading projects..."
                      : poFilterProjectNamesError
                        ? "Failed to load projects"
                        : "No projects available"
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      size="small"
                      label="Project"
                      placeholder="Type to search..."
                      sx={{
                        "& .MuiInputBase-input": {
                          fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                        },
                        "& .MuiInputLabel-root": {
                          fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" },
                        },
                      }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingPOFilterProjects ? (
                              <CircularProgress color="inherit" size={16} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Button
                  variant="outlined"
                  onClick={handleClearFilters}
                  fullWidth
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,

                    fontSize: {
                      xs: "0.75rem",
                      sm: "0.8rem",
                      md: "0.9rem",
                    },

                    height: {
                      xs: 36,
                      sm: 38,
                      md: 40,
                      lg: 44,
                    },
                  }}
                >
                  Clear Filters
                </Button>
              </Grid>
            </Grid>

      
          </Paper>



        
            
          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              overflow: "hidden",
              bgcolor: "transparent",
              boxShadow: "none",
              border: "none",
            }}
          >
            {/* po pagination */}
            {!loading && poTotal > 0 && (
            <Box  
              sx={{
                mt: { xs: 0.5, sm: 0.5, md: 1 },
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontSize: {
                    xs: "0.7rem",
                    sm: "0.75rem",
                    md: "0.8rem",
                    lg: "0.85rem",
                  },
                }}
              >
                Showing{" "}
                {poTotal > 0
                  ? (poPage - 1) * poPageSize + 1
                  : 0}
                -{Math.min(poPage * poPageSize, poTotal)}{" "}
                of {poTotal} POs
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FileDownloadIcon />}
                  onClick={handleExportPOCsv}
                  disabled={loading || exportingPOCsv}
                  sx={{ textTransform: "none", fontWeight: 600 }}
                >
                  {exportingPOCsv ? "Exporting..." : "Export PO"}
                </Button>

                <TablePagination
                  component="div"
                  count={poTotal}
                  page={poPage - 1}
                  onPageChange={(_, newPage) => setPoPage(newPage + 1)}
                  rowsPerPage={poPageSize}
                  onRowsPerPageChange={(e) => {
                    const next = parseInt(e.target.value, 10);
                    setPoPageSize(Number.isFinite(next) ? next : 20);
                    setPoPage(1);
                  }}
                  rowsPerPageOptions={[5, 10, 20, 50, 100]}
                  labelDisplayedRows={() => ""}
                  sx={{ border: 0 }}
                />

                {poTotalPages > 1 && (
                  <Pagination
                    count={poTotalPages}
                    page={poPage}
                    onChange={(_, pageNum) => setPoPage(pageNum)}
                    showFirstButton
                    showLastButton
                    color="primary"
                    size="small"
                    disabled={loading}
                  />
                )}
              </Box>
            </Box>
            )}
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
                <CircularProgress />
              </Box>
            ) : purchaseOrders.length === 0 ? (
              <Box sx={{ p: 6, textAlign: "center" }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Purchase Orders
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  Get started by creating your first purchase order
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleOpenDialog}
                  sx={{ textTransform: "none", fontWeight: 600 }}
                >
                  Create First PO
                </Button>
              </Box>
            ) : filteredPurchaseOrders.length === 0 ? (
              <Box sx={{ p: 6, textAlign: "center" }}>
                <Typography color="text.secondary">
                  No matching purchase orders found.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ p: 0 }}>
                {filteredPurchaseOrders.map((po) => (
                    <Card
                      key={po.id}
                      elevation={1}
                      sx={{
                        mb: { xs: 1, sm: 1.5, md: 2 },
                        borderRadius: { xs: 1, sm: 1.5 },
                        transition: "all 0.2s",
                      "&:hover": {
                            transform: "translateY(-2px)",
                            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                            borderColor: "primary.200",
                          },
                        "&:last-child": {
                          mb: 0,
                        },

                        border: 1,
                        borderColor: "divider",
                        borderLeft: 4,
                        borderLeftColor: "primary.main",
             
                      }}
                    >
                      <CardContent
                        sx={{
                          p: { xs: 1.5, sm: 2, md: 2.5 },
                          "&:last-child": { pb: { xs: 1.5, sm: 2, md: 2.5 } },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: { xs: 1, sm: 1.5, md: 2 },
                            flexWrap: "wrap",
                          }}
                        >
                          {/* PO Code & Client */}
                          <Box
                            sx={{
                              minWidth: { xs: 100, sm: 120 },
                              flex: "0 0 auto",
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 600,
                                color: "primary.main",
                                mb: 0.5,
                                fontSize: {
                                  xs: "0.75rem",
                                  sm: "0.8rem",
                                  md: "0.9rem",
                                  lg: "1rem",
                                },
                              }}
                            >
                              {po.po_code}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              noWrap
                              sx={{
                                fontSize: {
                                  xs: "0.65rem",
                                  sm: "0.7rem",
                                  md: "0.75rem",
                                },
                              }}
                            >
                              {po.client_name || "-"}
                            </Typography>
                          </Box>

                          {/* Project */}
                          <Box
                            sx={{
                              minWidth: { xs: 130, sm: 150 },
                              flex: "1 1 auto",
                            }}
                          >
                            <Typography
                              variant="body2"
                              fontWeight="medium"
                              noWrap
                              sx={{
                                fontSize: {
                                  xs: "0.75rem",
                                  sm: "0.8rem",
                                  md: "0.9rem",
                                  lg: "0.95rem",
                                },
                              }}
                            >
                              {po.project_name || "-"}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                fontSize: {
                                  xs: "0.65rem",
                                  sm: "0.7rem",
                                  md: "0.75rem",
                                },
                              }}
                            >
                              {po.project_type || "-"}{" "}
                              {po.geography ? `• ${po.geography}` : ""}
                            </Typography>
                          </Box>

                          {/* PO Value */}
                          <Box
                            sx={{
                              minWidth: { xs: 100, sm: 120 },
                              textAlign: "right",
                              flex: "0 0 auto",
                            }}
                          >
                            <Typography
                              variant="body2"
                              fontWeight="600"
                              color="primary.main"
                              sx={{
                                fontSize: {
                                  xs: "0.75rem",
                                  sm: "0.85rem",
                                  md: "0.95rem",
                                  lg: "1rem",
                                },
                              }}
                            >
                              {formatCurrency(
                                po.po_value_original,
                                po.currency,
                              )}
                            </Typography>

                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                fontSize: {
                                  xs: "0.6rem",
                                  sm: "0.65rem",
                                  md: "0.7rem",
                                },
                              }}
                            >
                              {po.currency || "-"}
                            </Typography>
                          </Box>

                          {/* Date */}
                          <Box
                            sx={{
                              minWidth: { xs: 80, sm: 90 },
                              flex: "0 0 auto",
                            }}
                          >
                            <Typography
                              variant="body2"
                              fontWeight="medium"
                              sx={{
                                fontSize: {
                                  xs: "0.7rem",
                                  sm: "0.8rem",
                                  md: "0.85rem",
                                },
                              }}
                            >
                              {formatDate(po.po_date)}
                            </Typography>

                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                fontSize: {
                                  xs: "0.6rem",
                                  sm: "0.65rem",
                                  md: "0.7rem",
                                },
                              }}
                            >
                              PO Date
                            </Typography>
                          </Box>

                          {/* Status */}
                          <Box
                            sx={{
                              minWidth: { xs: 80, sm: 90 },
                              flex: "0 0 auto",
                            }}
                          >
                            <Chip
                              label={po.status || "-"}
                              size="small"
                              color={
                                po.status === "Active"
                                  ? "success"
                                  : po.status === "Cancelled"
                                    ? "error"
                                    : po.status === "Partially Cancelled"
                                      ? "warning"
                                      : "default"
                              }
                              sx={{
                                fontWeight: 600,
                                fontSize: {
                                  xs: "0.6rem",
                                  sm: "0.65rem",
                                  md: "0.7rem",
                                },
                                height: {
                                  xs: 20,
                                  sm: 22,
                                  md: 24,
                                },
                              }}
                            />
                          </Box>

                          {/* Actions */}
                          <Box
                            sx={{
                              display: "flex",
                              gap: { xs: 0.3, sm: 0.5, md: 0.75 },
                              flex: "0 0 auto",
                            }}
                          >

                            {/* //! --------------------- Edit PO ------------------------------ */}
                            <Tooltip title="Edit PO">
                              <IconButton
                                size="small"
                                color="warning"
                                onClick={() => handleOpenEditPO(po)}
                                disabled={can("update") === false}
                                sx={{
                                  border: 1,
                                  borderColor: "divider",
                                  p: { xs: 0.4, sm: 0.6, md: 0.8 }, // responsive padding
                                  "&:hover": {
                                    bgcolor: "warning.light",
                                    color: "white",
                                  },
                                }}
                              >
                                <EditIcon
                                  sx={{
                                    fontSize: {
                                      xs: 16,
                                      sm: 18,
                                      md: 20,
                                      lg: 22,
                                    },
                                  }}
                                />
                              </IconButton>
                            </Tooltip>
                            {/* //! -------------------- View Deatils------------------------------- */}
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleOpenPODetail(po)}
                                sx={{
                                  border: 1,
                                  borderColor: "divider",
                                  p: { xs: 0.4, sm: 0.6, md: 0.8 },
                                  "&:hover": {
                                    bgcolor: "primary.light",
                                    color: "white",
                                  },
                                }}
                              >
                                <VisibilityIcon
                                  sx={{
                                    fontSize: {
                                      xs: 16,
                                      sm: 18,
                                      md: 20,
                                      lg: 22,
                                    },
                                  }}
                                />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Plan Invoices">
                              <IconButton
                                size="small"
                                color="secondary"
                                disabled={can("write") === false}
                                onClick={() => handleOpenInvoicePlanner(po)}
                                sx={{
                                  border: 1,
                                  borderColor: "divider",
                                  p: { xs: 0.4, sm: 0.6, md: 0.8 },
                                  "&:hover": {
                                    bgcolor: "secondary.light",
                                    color: "white",
                                  },
                                }}
                              >
                                <CalendarIcon
                                  sx={{
                                    fontSize: {
                                      xs: 16,
                                      sm: 18,
                                      md: 20,
                                      lg: 22,
                                    },
                                  }}
                                />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Delete PO">
                              <IconButton
                                size="small"
                                color="error"
                                disabled={can("delete") === false}
                                onClick={() => handleDeletePO(po)}
                                sx={{
                                  border: 1,
                                  borderColor: "divider",
                                  p: { xs: 0.4, sm: 0.6, md: 0.8 },
                                  "&:hover": {
                                    bgcolor: "error.light",
                                    color: "white",
                                  },
                                }}
                              >
                                <DeleteIcon
                                  sx={{
                                    fontSize: {
                                      xs: 16,
                                      sm: 18,
                                      md: 20,
                                      lg: 22,
                                    },
                                  }}
                                />
                              </IconButton>
                            </Tooltip>

                            <Tooltip
                              title={
                                expandedPOCardIds.has(po.id)
                                  ? "Hide financial summary"
                                  : "Show financial summary"
                              }
                            >
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => togglePOListCardFinancial(po)}
                                aria-expanded={expandedPOCardIds.has(po.id)}
                                sx={{
                                  border: 1,
                                  borderColor: "divider",
                                  transform: expandedPOCardIds.has(po.id)
                                    ? "rotate(180deg)"
                                    : "none",
                                  transition: "transform 0.2s",
                                }}
                              >
                                <ExpandMoreIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>

                        <Collapse in={expandedPOCardIds.has(po.id)} timeout="auto">
                          <Divider sx={{ my: { xs: 1.5, sm: 2 } }} />
                          <Grid
                            container
                            spacing={{ xs: 1.5, sm: 2 }}
                            alignItems="stretch"
                          >
                            <Grid
                              item
                              xs={6}
                              sm={3}
                              sx={{ display: "flex", alignSelf: "stretch" }}
                            >
                              <Paper
                                elevation={0}
                                sx={{
                                  flex: 1,
                                  width: "100%",
                                  display: "flex",
                                  flexDirection: "column",
                                  p: 1.5,
                                  bgcolor: "grey.50",
                                  borderRadius: 1.5,
                                  border: 1,
                                  borderColor: "divider",
                
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    fontSize: {
                                      xs: "0.65rem",
                                      sm: "0.7rem",
                                      md: "0.72rem",
                                    },
                                  }}
                                >
                                  PO Value
                                </Typography>
                                <Typography
                                  variant="body2"
                                  fontWeight="700"
                                  color="text.primary"
                                  sx={{
                                    mt: 0.5,
                                    fontSize: {
                                      xs: "0.8rem",
                                      sm: "0.85rem",
                                      md: "0.9rem",
                                    },
                                  }}
                                >
                                  {formatCurrency(
                                    po.po_value_original,
                                    po.currency,
                                  )}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    display: "block",
                                    mt: 0.25,
                                    fontSize: {
                                      xs: "0.65rem",
                                      sm: "0.7rem",
                                      md: "0.72rem",
                                    },
                                  }}
                                >
                                  {po.currency || "-"}
                                </Typography>
                              </Paper>
                            </Grid>
                            {po.po_value_original && po.fx_rate_usd && (
                              <Grid
                                item
                                xs={6}
                                sm={3}
                                sx={{ display: "flex", alignSelf: "stretch" }}
                              >
                                <Paper
                                  elevation={0}
                                  sx={{
                                    flex: 1,
                                    width: "100%",
                                    display: "flex",
                                    flexDirection: "column",
                                    p: 1.5,
                                    bgcolor: "grey.50",
                                    borderRadius: 1.5,
                                    border: 1,
                                    borderColor: "divider",
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                      fontSize: {
                                        xs: "0.65rem",
                                        sm: "0.7rem",
                                        md: "0.72rem",
                                      },
                                    }}
                                  >
                                    PO Value (USD)
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    fontWeight="700"
                                    color="text.primary"
                                    sx={{
                                      mt: 0.5,
                                      fontSize: {
                                        xs: "0.8rem",
                                        sm: "0.85rem",
                                        md: "0.9rem",
                                      },
                                    }}
                                  >
                                    {formatCurrency(
                                      calculateUSDValue(
                                        po.po_value_original,
                                        po.fx_rate_usd,
                                      ),
                                      "USD",
                                    )}
                                  </Typography>
                                </Paper>
                              </Grid>
                            )}
                            {po.po_value_original && po.fx_rate_inr && (
                              <Grid
                                item
                                xs={6}
                                sm={3}
                                sx={{ display: "flex", alignSelf: "stretch" }}
                              >
                                <Paper
                                  elevation={0}
                                  sx={{
                                    flex: 1,
                                    width: "100%",
                                    display: "flex",
                                    flexDirection: "column",
                                    p: 1.5,
                                    bgcolor: "grey.50",
                                    borderRadius: 1.5,
                                    border: 1,
                                    borderColor: "divider",
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                      fontSize: {
                                        xs: "0.65rem",
                                        sm: "0.7rem",
                                        md: "0.72rem",
                                      },
                                    }}
                                  >
                                    PO Value (INR)
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    fontWeight="700"
                                    color="text.primary"
                                    sx={{
                                      mt: 0.5,
                                      fontSize: {
                                        xs: "0.8rem",
                                        sm: "0.85rem",
                                        md: "0.9rem",
                                      },
                                    }}
                                  >
                                    {formatCurrency(
                                      calculateINRValue(
                                        po.po_value_original,
                                        po.fx_rate_inr,
                                      ),
                                      "INR",
                                    )}
                                  </Typography>
                                </Paper>
                              </Grid>
                            )}
                            <Grid
                              item
                              xs={6}
                              sm={3}
                              sx={{ display: "flex", alignSelf: "stretch" }}
                            >
                              <Paper
                                elevation={0}
                                sx={{
                                  flex: 1,
                                  width: "100%",
                                  display: "flex",
                                  flexDirection: "column",
                                  p: 1.5,
                                  bgcolor: "grey.50",
                                  borderRadius: 1.5,
                                  border: 1,
                                  borderColor: "divider",
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    fontSize: {
                                      xs: "0.65rem",
                                      sm: "0.7rem",
                                      md: "0.72rem",
                                    },
                                  }}
                                >
                                  Total Invoices
                                </Typography>
                                {!Object.prototype.hasOwnProperty.call(
                                  poListInvoicesByPOId,
                                  po.id,
                                ) ? (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      minHeight: 32,
                                      mt: 0.5,
                                    }}
                                  >
                                    <CircularProgress size={20} />
                                  </Box>
                                ) : (
                                  <>
                                    <Typography
                                      variant="body2"
                                      fontWeight="700"
                                      color="text.primary"
                                      sx={{
                                        mt: 0.5,
                                        fontSize: {
                                          xs: "0.8rem",
                                          sm: "0.85rem",
                                          md: "0.9rem",
                                        },
                                      }}
                                    >
                                      {(poListInvoicesByPOId[po.id] ?? []).length}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{
                                        display: "block",
                                        mt: 0.25,
                                        fontSize: {
                                          xs: "0.65rem",
                                          sm: "0.7rem",
                                          md: "0.72rem",
                                        },
                                      }}
                                    >
                                      {
                                        (poListInvoicesByPOId[po.id] ?? []).filter(
                                          (inv) => inv.status === "Received",
                                        ).length
                                      }{" "}
                                      Received
                                    </Typography>
                                  </>
                                )}
                              </Paper>
                            </Grid>
                          </Grid>
                        </Collapse>
                      </CardContent>
                    </Card>
                  ))}
              </Box>
            )}
          </Paper>
        </Box>

      {/* New PO Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: { xs: "92vh", sm: "94vh", md: "95vh" },
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            overscrollBehavior: "contain",
            minHeight: 0,

            // Ensure DialogContent can actually scroll inside flex layout
            "& form": {
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minHeight: 0,
            },
            "& .MuiDialogContent-root": {
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              WebkitOverflowScrolling: "touch",
            },
            "& .MuiDialogActions-root": {
              flexShrink: 0,
            },

            // Hide scrollbars but keep scroll behavior
            scrollbarWidth: "none", // Firefox
            msOverflowStyle: "none", // IE/old Edge
            "& *": {
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            },
            "& *::-webkit-scrollbar": {
              display: "none",
              width: 0,
              height: 0,
            },
          },
        }}
      >
        <form onSubmit={handleSubmit} noValidate>
{/* Sticky Header */}
<Box
  sx={{
    px: { xs: 2, sm: 3, md: 4 },
    py: { xs: 1.5, sm: 2, md: 2.5 },
    borderBottom: 1,
    borderColor: "divider",
    bgcolor: "background.paper",
    position: "sticky",
    top: 0,
    zIndex: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    backdropFilter: "blur(6px)",
    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
  }}
>
  <Box>
    <Typography
      variant="h6"
      sx={{
        fontWeight: 700,
        color: "text.primary",
        lineHeight: 1.2,
        letterSpacing: 0.3,
        fontSize: {
          xs: "1rem",
          sm: "1.15rem",
          md: "1.3rem",
          lg: "1.4rem",
        },
      }}
    >
      Create New Purchase Order
    </Typography>

    <Typography
      variant="caption"
      color="text.secondary"
      sx={{
        display: "block",
        mt: 0.5,
        lineHeight: 1.4,
        fontWeight: 400,
        fontSize: {
          xs: "0.65rem",
          sm: "0.72rem",
          md: "0.8rem",
          lg: "0.85rem",
        },
      }}
    >
      Fill in the details below to create a new purchase order
    </Typography>
  </Box>

  <IconButton
    onClick={handleCloseDialog}
    size="small"
    sx={{
      color: "text.secondary",
      transition: "all 0.2s ease",
      "&:hover": {
        bgcolor: "action.hover",
        transform: "rotate(90deg)",
      },
    }}
  >
    <CloseIcon
      sx={{
        fontSize: { xs: 18, sm: 20, md: 22 },
      }}
    />
  </IconButton>
</Box>



          <DialogContent sx={{ pt: 3, pb: 2, flex: 1, overflow: "auto" }}>
            {error && (
              <Alert
                severity="error"
                sx={{ mb: 3, borderRadius: 2 }}
                onClose={() => setError(null)}
              >
                {error}
              </Alert>
            )}

{/* Basic Information Section */}
<Card
  elevation={0}
  sx={{ mb: 3, border: 1, borderColor: "divider", borderRadius: 2 }}
>
  <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
    <Typography
      variant="subtitle2"
      sx={{
        mb: { xs: 2, sm: 2.5 },
        fontWeight: 700,
        color: "text.primary",
        textTransform: "uppercase",
        letterSpacing: 0.5,
        fontSize: {
          xs: "0.65rem",
          sm: "0.7rem",
          md: "0.75rem",
          lg: "0.8rem",
        },
      }}
    >
      Basic Information
    </Typography>

    <Grid container spacing={{ xs: 2, sm: 2.5 }}>
      <Grid item xs={12} sm={6}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="PO Date"
            value={formData.po_date ? dayjs(formData.po_date) : null}
            onChange={(newValue) => {
              setFormData({
                ...formData,
                po_date: newValue
                  ? newValue.format("YYYY-MM-DD")
                  : "",
              });
              if (formErrors.po_date)
                setFormErrors({ ...formErrors, po_date: "" });
            }}
            slotProps={{
              textField: {
                size: "small",
                fullWidth: true,
                required: true,
                InputLabelProps: { shrink: true },
                error: !!formErrors.po_date,
                helperText: formErrors.po_date,
                sx: {
                              "& .MuiOutlinedInput-root": { borderRadius: 1},
                  "& .MuiInputLabel-root": {
                    fontSize: {
                      xs: "0.75rem",
                      sm: "0.8rem",
                      md: "0.85rem",
                    },
                  },
                  "& .MuiInputBase-input": {
                    fontSize: {
                      xs: "0.8rem",
                      sm: "0.85rem",
                      md: "0.9rem",
                    },
                  },
                              "& .MuiFormHelperText-root": {
                                fontSize: { xs: "0.65rem", sm: "0.7rem" },
                              },
                },
              },
            }}
            format="DD/MM/YYYY"
          />
        </LocalizationProvider>
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormControl
          fullWidth
          size="small"
          required
          error={!!formErrors.status}
          sx={{
            "& .MuiOutlinedInput-root": { borderRadius: 1 },
            "& .MuiInputLabel-root": {
              fontSize: {
                xs: "0.75rem",
                sm: "0.8rem",
                md: "0.85rem",
              },
            },
            "& .MuiSelect-select": {
              fontSize: {
                xs: "0.8rem",
                sm: "0.85rem",
                md: "0.9rem",
              },
            },
            "& .MuiFormHelperText-root": {
              fontSize: { xs: "0.65rem", sm: "0.7rem" },
            },
          }}
        >
          <InputLabel>Status</InputLabel>
          <Select
            value={formData.status}
            label="Status"
            onChange={(e) => {
              setFormData({ ...formData, status: e.target.value });
              if (formErrors.status)
                setFormErrors({ ...formErrors, status: "" });
            }}
          >
            <MenuItem value="Active">Active</MenuItem>
            <MenuItem value="Expired">Expired</MenuItem>
            <MenuItem value="Cancelled">Cancelled</MenuItem>
            <MenuItem value="Partially Cancelled">
              Partially Cancelled
            </MenuItem>
          </Select>

          {formErrors.status && (
            <FormHelperText
              sx={{
                fontSize: {
                  xs: "0.65rem",
                  sm: "0.7rem",
                },
              }}
            >
              {formErrors.status}
            </FormHelperText>
          )}
        </FormControl>
      </Grid>
    </Grid>
  </CardContent>
</Card>


            {/* Client & Project Section */}
            <Card
              elevation={0}
              sx={{ mb: 3, border: 1, borderColor: "divider", borderRadius: 1 }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    mb: { xs: 2, sm: 2.5 },
                    fontWeight: 700,
                    color: "text.primary",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    fontSize: {
                      xs: "0.65rem",
                      sm: "0.7rem",
                      md: "0.75rem",
                      lg: "0.8rem",
                    },
                  }}
                >
                  Client & Project
                </Typography>

                <Grid container spacing={{ xs: 2, sm: 2.5 }}>

                  {/* Client Name */}
                  <Grid item xs={12} sm={6}>
                    <CreatableSearchSelect
                      label="Client Name"
                      required
                      value={formData.client_name || ""}
                      onChange={(newValue) =>
                        setFormData({ ...formData, client_name: newValue })
                      }
                      options={poClientNames}
                      loading={loadingPOClientNames}
                      loadError={poClientNamesError}
                      fieldError={formErrors.client_name}
                      onClearFieldError={() => {
                        if (formErrors.client_name) {
                          setFormErrors({ ...formErrors, client_name: "" });
                        }
                      }}
                      createCtaLabel="Create new Client Name"
                    />
                  </Grid>
                  {/* Project Name */}
                  <Grid item xs={12} sm={6}>
                    <CreatableSearchSelect
                      label="Project Name"
                      required
                      value={formData.project_name || ""}
                      onChange={(newValue) =>
                        setFormData({ ...formData, project_name: newValue })
                      }
                      options={poProjectNames}
                      loading={loadingPOProjectNames}
                      loadError={poProjectNamesError}
                      fieldError={formErrors.project_name}
                      onClearFieldError={() => {
                        if (formErrors.project_name) {
                          setFormErrors({ ...formErrors, project_name: "" });
                        }
                      }}
                      createCtaLabel="Create new Project Name"
                    />
                  </Grid>
                  {/* Project Type */}
                  <Grid item xs={12} sm={6}>
                    <FormControl
                      fullWidth
                      size="small"
                      required
                      error={!!formErrors.project_type}
                      sx={{
                        "& .MuiOutlinedInput-root": { borderRadius: 1 },
                        "& .MuiInputLabel-root": {
                          fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" },
                        },
                        "& .MuiSelect-select": {
                          fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                        },
                      }}
                    >
                      <InputLabel>Project Type</InputLabel>
                      <Select
                        value={formData.project_type}
                        label="Project Type"
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            project_type: e.target.value,
                          });
                          if (formErrors.project_type)
                            setFormErrors({ ...formErrors, project_type: "" });
                        }}
                      >
                        <MenuItem value="">None</MenuItem>
                        <MenuItem value="Staff Aug">Staff Aug</MenuItem>
                        <MenuItem value="Managed Services">
                          Managed Services
                        </MenuItem>
                        <MenuItem value="Turnkey">Turnkey</MenuItem>
                      </Select>
                      {formErrors.project_type && (
                        <FormHelperText
                          sx={{ fontSize: { xs: "0.65rem", sm: "0.7rem" } }}
                        >
                          {formErrors.project_type}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                  {/* Geography */}
                  <Grid item xs={12} sm={6}>
                    <FormControl
                      fullWidth
                      size="small"
                      required
                      error={!!formErrors.geography}
                      sx={{
                        "& .MuiOutlinedInput-root": { borderRadius: 1 },
                        "& .MuiInputLabel-root": {
                          fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" },
                        },
                        "& .MuiSelect-select": {
                          fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                        },
                      }}
                    >
                      <InputLabel>Geography</InputLabel>
                      <Select
                        value={formData.geography || ""}
                        label="Geography"
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            geography: e.target.value as string,
                          });
                          if (formErrors.geography)
                            setFormErrors({ ...formErrors, geography: "" });
                        }}
                      >
                        <MenuItem value="">None</MenuItem>
                        <MenuItem value="Europe">Europe</MenuItem>
                        <MenuItem value="USA">USA</MenuItem>
                        <MenuItem value="India">India</MenuItem>
                        <MenuItem value="Singapore">Singapore</MenuItem>
                      </Select>
                      {formErrors.geography && (
                        <FormHelperText
                          sx={{ fontSize: { xs: "0.65rem", sm: "0.7rem" } }}
                        >
                          {formErrors.geography}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                  {/* Account Manager */}
                  <Grid item xs={12} sm={6}>
                    <CreatableSearchSelect
                      label="Account Manager (AM)"
                      required
                      value={formData.account_manager || ""}
                      onChange={(newValue) =>
                        setFormData({ ...formData, account_manager: newValue })
                      }
                      options={accountManagers}
                      loading={loadingAccountManagers}
                      loadError={accountManagersError}
                      fieldError={formErrors.account_manager}
                      onClearFieldError={() => {
                        if (formErrors.account_manager) {
                          setFormErrors({ ...formErrors, account_manager: "" });
                        }
                      }}
                      createCtaLabel="Create new Account Manager"
                    />
                  </Grid>
                  {/* Project Manager */}
                  <Grid item xs={12} sm={6}>
                    <CreatableSearchSelect
                      label="Project Manager (PM)"
                      required
                      value={formData.project_manager || ""}
                      onChange={(newValue) =>
                        setFormData({ ...formData, project_manager: newValue })
                      }
                      options={projectManagers}
                      loading={loadingProjectManagers}
                      loadError={projectManagersError}
                      fieldError={formErrors.project_manager}
                      onClearFieldError={() => {
                        if (formErrors.project_manager) {
                          setFormErrors({ ...formErrors, project_manager: "" });
                        }
                      }}
                      createCtaLabel="Create new Project Manager"
                    />
                  </Grid>
                  {/* Linked MSA */}
                  <Grid item xs={12} sm={6}>
                    <FormControl
                      fullWidth
                      size="small"
                      required
                      error={!!formErrors.revenue_type}
                      sx={{
                        "& .MuiOutlinedInput-root": { borderRadius: 1 },
                        "& .MuiInputLabel-root": {
                          fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" },
                        },
                        "& .MuiSelect-select": {
                          fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                        },
                      }}
                    >
                      <InputLabel>Revenue Type</InputLabel>
                      <Select
                        value={formData.revenue_type}
                        label="Revenue Type"
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            revenue_type: e.target.value,
                          });
                          if (formErrors.revenue_type)
                            setFormErrors({ ...formErrors, revenue_type: "" });
                        }}
                      >
                        <MenuItem value="">None</MenuItem>
                        <MenuItem value="ENEB">ENEB</MenuItem>
                        <MenuItem value="ENNB">ENNB</MenuItem>
                        <MenuItem value="NNNB">NNNB</MenuItem>
                      </Select>
                      {formErrors.revenue_type && (
                        <FormHelperText
                          sx={{ fontSize: { xs: "0.65rem", sm: "0.7rem" } }}
                        >
                          {formErrors.revenue_type}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                  {/* Revenue Class */}
                  <Grid item xs={12} sm={6}>
                    <FormControl
                      fullWidth
                      size="small"
                      required
                      error={!!formErrors.revenue_class}
                      sx={{
                        "& .MuiOutlinedInput-root": { borderRadius: 1 },
                        "& .MuiInputLabel-root": {
                          fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" },
                        },
                        "& .MuiSelect-select": {
                          fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                        },
                      }}
                    >
                      <InputLabel>Revenue Class</InputLabel>
                      <Select
                        value={formData.revenue_class}
                        label="Revenue Class"
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            revenue_class: e.target.value,
                          });
                          if (formErrors.revenue_class)
                            setFormErrors({ ...formErrors, revenue_class: "" });
                        }}
                      >
                        <MenuItem value="">None</MenuItem>
                        <MenuItem value="Backlog">Backlog</MenuItem>
                        <MenuItem value="New">New</MenuItem>
                      </Select>
                      {formErrors.revenue_class && (
                        <FormHelperText
                          sx={{ fontSize: { xs: "0.65rem", sm: "0.7rem" } }}
                        >
                          {formErrors.revenue_class}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                  {/* Linked MSA */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Linked MSA"
                      value={formData.linked_msa}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          linked_msa: e.target.value,
                        });
                        if (formErrors.linked_msa)
                          setFormErrors({ ...formErrors, linked_msa: "" });
                      }}
                      // onChange={(e) => setFormData({ ...formData, linked_msa: e.target.value })}
                      fullWidth
                      size="small"
                      error={!!formErrors.linked_msa}
                      helperText={formErrors.linked_msa}
                      sx={{
                        "& .MuiOutlinedInput-root": { borderRadius: 1 },
                        "& .MuiInputLabel-root": {
                          fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" },
                        },
                        "& .MuiInputBase-input": {
                          fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                        },
                        "& .MuiFormHelperText-root": {
                          fontSize: { xs: "0.65rem", sm: "0.7rem" },
                        },
                      }}
                    />
                  </Grid>
                  
                </Grid>
              </CardContent>
            </Card>

            {/* Financial Details Section */}
            <Card
              elevation={0}
              sx={{ mb: 3, border: 1, borderColor: "divider", borderRadius: 1 }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    mb: { xs: 2, sm: 2.5 },
                    fontWeight: 700,
                    color: "text.primary",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    fontSize: {
                      xs: "0.65rem",
                      sm: "0.7rem",
                      md: "0.75rem",
                      lg: "0.8rem",
                    },
                  }}
                >
                  Financial Details
                </Typography>
                <Grid container spacing={{ xs: 2, sm: 2.5 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      required
                      label="PO Value"
                      type="number"
                      value={formData.po_value_original || ""}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          po_value_original: e.target.value
                            ? parseFloat(e.target.value)
                            : null,
                        });
                        if (formErrors.po_value_original)
                          setFormErrors({
                            ...formErrors,
                            po_value_original: "",
                          });
                      }}
                      fullWidth
                      size="small"
                      inputProps={{ step: "0.01", min: "0" }}
                      helperText={
                        formErrors.po_value_original ||
                        "Original PO value (used for calculations)"
                      }
                      error={!!formErrors.po_value_original}
                      sx={{
                        "& .MuiOutlinedInput-root": { borderRadius: 1 },
                        "& .MuiInputLabel-root": {
                          fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" },
                        },
                        "& .MuiInputBase-input": {
                          fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                        },
                        "& .MuiFormHelperText-root": {
                          fontSize: { xs: "0.65rem", sm: "0.7rem" },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl
                      fullWidth
                      size="small"
                      sx={{
                        "& .MuiOutlinedInput-root": { borderRadius: 1 },
                        "& .MuiInputLabel-root": {
                          fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" },
                        },
                        "& .MuiSelect-select": {
                          fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                        },
                      }}
                    >
                      <InputLabel>Currency</InputLabel>
                      <Select
                        value={formData.currency}
                        label="Currency"
                        onChange={(e) =>
                          handleCurrencyChange(e.target.value as string)
                        }
                      >
                        <MenuItem value="USD">USD</MenuItem>
                        <MenuItem value="EUR">EUR</MenuItem>
                        <MenuItem value="INR">INR</MenuItem>
                      </Select>
                      {formErrors.currency && (
                        <FormHelperText
                          sx={{ fontSize: { xs: "0.65rem", sm: "0.7rem" } }}
                        >
                          {formErrors.currency}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="FX Rate (Base to USD)"
                      type="number"
                      value={formData.fx_rate_usd || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          fx_rate_usd: e.target.value
                            ? parseFloat(e.target.value)
                            : null,
                        })
                      }
                      fullWidth
                      size="small"
                      inputProps={{ step: "0.000001", min: "0" }}
                      placeholder="Rate to convert base currency to USD"
                      error={!!formErrors.fx_rate_usd}
                      helperText={
                        formErrors.fx_rate_usd ||
                        "Rate to convert base currency to USD"
                      }
                      sx={{
                        "& .MuiOutlinedInput-root": { borderRadius: 1 },
                        "& .MuiInputLabel-root": {
                          fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" },
                        },
                        "& .MuiInputBase-input": {
                          fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                        },
                        "& .MuiFormHelperText-root": {
                          fontSize: { xs: "0.65rem", sm: "0.7rem" },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="FX Rate (Base to INR)"
                      type="number"
                      value={formData.fx_rate_inr || ""}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          fx_rate_inr: e.target.value
                            ? parseFloat(e.target.value)
                            : null,
                        });
                        if (formErrors.fx_rate_inr)
                          setFormErrors({ ...formErrors, fx_rate_inr: "" });
                      }}
                      fullWidth
                      size="small"
                      inputProps={{ step: "0.000001", min: "0" }}
                      placeholder="Rate to convert base currency to INR"
                      error={!!formErrors.fx_rate_inr}
                      helperText={
                        formErrors.fx_rate_inr ||
                        "Rate to convert base currency to INR"
                      }
                      sx={{
                        "& .MuiOutlinedInput-root": { borderRadius: 1 },
                        "& .MuiInputLabel-root": {
                          fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" },
                        },
                        "& .MuiInputBase-input": {
                          fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                        },
                        "& .MuiFormHelperText-root": {
                          fontSize: { xs: "0.65rem", sm: "0.7rem" },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Payment Terms"
                      value={formData.payment_terms}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          payment_terms: e.target.value,
                        });
                        if (formErrors.payment_terms)
                          setFormErrors({ ...formErrors, payment_terms: "" });
                      }}
                      fullWidth
                      size="small"
                      placeholder="e.g., 30, 60, EOM+60"
                      error={!!formErrors.payment_terms}
                      helperText={formErrors.payment_terms}
                      sx={{
                        "& .MuiOutlinedInput-root": { borderRadius: 1 },
                        "& .MuiInputLabel-root": {
                          fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" },
                        },
                        "& .MuiInputBase-input": {
                          fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                        },
                        "& .MuiFormHelperText-root": {
                          fontSize: { xs: "0.65rem", sm: "0.7rem" },
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Additional Information Section */}
            <Card
              elevation={0}
              sx={{ mb: 2, border: 1, borderColor: "divider", borderRadius: 1 }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    mb: { xs: 2, sm: 2.5 },
                    fontWeight: 700,
                    color: "text.primary",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    fontSize: {
                      xs: "0.65rem",
                      sm: "0.7rem",
                      md: "0.75rem",
                      lg: "0.8rem",
                    },
                  }}
                >
                  Additional Information
                </Typography>
                <Grid container spacing={{ xs: 2, sm: 2.5 }}>
                  <Grid item xs={12}>
                    <TextField
                      label="Remarks"
                      value={formData.remarks}
                      onChange={(e) =>
                        setFormData({ ...formData, remarks: e.target.value })
                      }
                      fullWidth
                      size="small"
                      multiline
                      rows={3}
                      placeholder="Enter any additional notes or remarks..."
                      sx={{
                        "& .MuiOutlinedInput-root": { borderRadius: 2 },
                        "& .MuiInputLabel-root": {
                          fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" },
                        },
                        "& .MuiInputBase-input": {
                          fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                        },
                        "& .MuiFormHelperText-root": {
                          fontSize: { xs: "0.65rem", sm: "0.7rem" },
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </DialogContent>
          <DialogActions
            sx={{
              p: { xs: 2, sm: 3 },
              borderTop: 1,
              borderColor: "divider",
              gap: { xs: 1, sm: 1.5 },
              bgcolor: "background.paper",
              position: "sticky",
              bottom: 0,
              zIndex: 1,
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "stretch", sm: "center" },
              justifyContent: { xs: "stretch", sm: "flex-end" },
            }}
          >
            <Button
              onClick={handleCloseDialog}
              disabled={submitting}
              sx={{
                textTransform: "none",
                fontSize: { xs: "0.75rem", sm: "0.85rem", md: "0.9rem" },
                minWidth: { xs: "100%", sm: 120, md: 130 },
                px: { xs: 2, sm: 2.5, md: 3 },
                height: { xs: 34, sm: 38, md: 40 },
                color: "text.secondary",
                "&:hover": {
                  bgcolor: "action.hover",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={submitting}
              startIcon={
                submitting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <AddIcon />
                )
              }
              sx={{
                textTransform: "none",
                fontSize: { xs: "0.75rem", sm: "0.85rem", md: "0.9rem" },
                minWidth: { xs: "100%", sm: 160, md: 180 },
                px: { xs: 2, sm: 2.5, md: 3 },
                height: { xs: 34, sm: 38, md: 40 },
                fontWeight: 600,
                boxShadow: 2,
                "&:hover": {
                  boxShadow: 4,
                },
              }}
            >
              {submitting ? "Creating..." : "Create Purchase Order"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Edit Purchase Order Dialog */}
      <Dialog
        open={openEditDialog}
        onClose={handleCloseEditDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: { xs: "92vh", sm: "94vh", md: "95vh" },
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            overscrollBehavior: "contain",
            minHeight: 0,

            // Ensure DialogContent can actually scroll inside flex layout
            "& form": {
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minHeight: 0,
            },
            "& .MuiDialogContent-root": {
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              WebkitOverflowScrolling: "touch",
            },
            "& .MuiDialogActions-root": {
              flexShrink: 0,
            },

            // Hide scrollbars but keep scroll behavior
            scrollbarWidth: "none", // Firefox
            msOverflowStyle: "none", // IE/old Edge
            "& *": {
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            },
            "& *::-webkit-scrollbar": {
              display: "none",
              width: 0,
              height: 0,
            },
          },
        }}
      >
        <form onSubmit={handleEditSubmit} noValidate>
          {/* Sticky Header */}
          <Box
            sx={{
              px: { xs: 2, sm: 3, md: 4 },
              py: { xs: 1.5, sm: 2, md: 2.5 },
              borderBottom: 1,
              borderColor: "divider",
              bgcolor: "background.paper",
              position: "sticky",
              top: 0,
              zIndex: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              backdropFilter: "blur(6px)",
              boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: { xs: 34, sm: 38, md: 40 },
                  height: { xs: 34, sm: 38, md: 40 },
                  borderRadius: 1.5,
                  bgcolor: "warning.main",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                }}
              >
                <EditIcon sx={{ fontSize: { xs: 18, sm: 20, md: 22 } }} />
              </Box>
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: "text.primary",
                    lineHeight: 1.2,
                    letterSpacing: 0.3,
                    fontSize: {
                      xs: "1rem",
                      sm: "1.15rem",
                      md: "1.3rem",
                      lg: "1.4rem",
                    },
                  }}
                >
                  Edit Purchase Order
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: "block",
                    mt: 0.5,
                    lineHeight: 1.4,
                    fontWeight: 400,
                    fontSize: {
                      xs: "0.65rem",
                      sm: "0.72rem",
                      md: "0.8rem",
                      lg: "0.85rem",
                    },
                  }}
                >
                  {editingPO?.po_code}
                </Typography>
              </Box>
            </Box>
            <IconButton
              onClick={handleCloseEditDialog}
              size="small"
              sx={{
                color: "text.secondary",
                transition: "all 0.2s ease",
                "&:hover": {
                  bgcolor: "action.hover",
                  transform: "rotate(90deg)",
                },
              }}
            >
              <CloseIcon sx={{ fontSize: { xs: 18, sm: 20, md: 22 } }} />
            </IconButton>
          </Box>

          <DialogContent sx={{ pt: 3, pb: 2, flex: 1, overflow: "auto" }}>
            {error && (
              <Alert
                severity="error"
                sx={{ mb: 3, borderRadius: 2 }}
                onClose={() => setError(null)}
              >
                {error}
              </Alert>
            )}

            {/* Basic Information Section */}
            <Card
              elevation={0}
              sx={{ mb: 3, border: 1, borderColor: "divider", borderRadius: 2 }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    mb: { xs: 2, sm: 2.5 },
                    fontWeight: 700,
                    color: "text.primary",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    fontSize: {
                      xs: "0.65rem",
                      sm: "0.7rem",
                      md: "0.75rem",
                      lg: "0.8rem",
                    },
                  }}
                >
                  Basic Information
                </Typography>
                <Grid container spacing={{ xs: 2, sm: 2.5 }}>
                  <Grid item xs={12} sm={6}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="PO Date"
                        value={
                          editFormData.po_date
                            ? dayjs(editFormData.po_date)
                            : null
                        }
                        onChange={(newValue) => {
                          setEditFormData({
                            ...editFormData,
                            po_date: newValue
                              ? newValue.format("YYYY-MM-DD")
                              : null,
                          });
                          if (editFormErrors.po_date)
                            setEditFormErrors({
                              ...editFormErrors,
                              po_date: "",
                            });
                        }}
                        slotProps={{
                          textField: {
                            size: "small",
                            fullWidth: true,
                            required: true,
                            InputLabelProps: { shrink: true },
                            error: !!editFormErrors.po_date,
                            helperText: editFormErrors.po_date,
                            sx: {
                              "& .MuiOutlinedInput-root": { borderRadius: 1 },
                              "& .MuiInputLabel-root": {
                                fontSize: {
                                  xs: "0.75rem",
                                  sm: "0.8rem",
                                  md: "0.85rem",
                                },
                              },
                              "& .MuiInputBase-input": {
                                fontSize: {
                                  xs: "0.8rem",
                                  sm: "0.85rem",
                                  md: "0.9rem",
                                },
                              },
                              "& .MuiFormHelperText-root": {
                                fontSize: { xs: "0.65rem", sm: "0.7rem" },
                              },
                            },
                          },
                        }}
                        format="DD/MM/YYYY"
                      />
                    </LocalizationProvider>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl
                      fullWidth
                      size="small"
                      required
                      error={!!editFormErrors.status}
                      sx={{
                        "& .MuiOutlinedInput-root": { borderRadius: 1 },
                        "& .MuiInputLabel-root": {
                          fontSize: {
                            xs: "0.75rem",
                            sm: "0.8rem",
                            md: "0.85rem",
                          },
                        },
                        "& .MuiSelect-select": {
                          fontSize: {
                            xs: "0.8rem",
                            sm: "0.85rem",
                            md: "0.9rem",
                          },
                        },
                        "& .MuiFormHelperText-root": {
                          fontSize: { xs: "0.65rem", sm: "0.7rem" },
                        },
                      }}
                    >
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={editFormData.status}
                        label="Status"
                        onChange={(e) => {
                          setEditFormData({
                            ...editFormData,
                            status: e.target.value,
                          });
                          if (editFormErrors.status)
                            setEditFormErrors({
                              ...editFormErrors,
                              status: "",
                            });
                        }}
                      >
                        <MenuItem value="Active">Active</MenuItem>
                        <MenuItem value="Expired">Expired</MenuItem>
                        <MenuItem value="Cancelled">Cancelled</MenuItem>
                        <MenuItem value="Partially Cancelled">
                          Partially Cancelled
                        </MenuItem>
                      </Select>
                      {editFormErrors.status && (
                        <FormHelperText
                          sx={{ fontSize: { xs: "0.65rem", sm: "0.7rem" } }}
                        >
                          {editFormErrors.status}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Partial cancellation Section (Edit Purchase Order) */}
            {isPartiallyCancelled(editFormData.status) && (
              <Card
                elevation={0}
                sx={{
                  mb: 3,
                  bgcolor: "warning.50",
                  border: 2,
                  borderColor: "warning.main",
                  borderRadius: 2,
                }}
              >
              <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    mb: { xs: 2, sm: 2.5 },
                    fontWeight: 700,
                    color: "text.primary",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    fontSize: {
                      xs: "0.65rem",
                      sm: "0.7rem",
                      md: "0.75rem",
                      lg: "0.8rem",
                    },
                  }}
                >
                  {/* edit purchase order cancellation events */}
                  Cancellation Events
                </Typography>


                {/* <small><pre>{JSON.stringify(editFormData, null, 2)}</pre></small> */}


                            {/* Summary Cards */}
                            <Grid container spacing={2} sx={{ mb: 3 }}>
                              {/* Original PO Value */}
                              <Grid item xs={12} sm={4}>
                                <Card
                                  elevation={0}
                                  sx={{
                                    bgcolor: "background.paper",
                                    border: 1,
                                    borderColor: "divider",
                                    borderRadius: 2,
                                  }}
                                >
                                  <CardContent
                                    sx={{ p: 2, "&:last-child": { pb: 2 } }}
                                  >
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{
                                        fontSize: "0.7rem",
                                        textTransform: "uppercase",
                                        letterSpacing: 0.5,
                                      }}
                                    >
                                      Original PO Value
                                    </Typography>
                                    <Typography
                                      variant="h6"
                                      sx={{
                                        fontWeight: 700,
                                        color: "text.primary",
                                        mt: 0.5,
                                      }}
                                    >
                                      {formatCurrency(
                                          editPOOriginalValue,
                                          editFormData.currency || null,
                                      )}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ fontSize: "0.7rem" }}
                                    >
                                      {editFormData.currency || "-"}
                                    </Typography>
                                  </CardContent>
                                </Card>
                              </Grid>

                              {/* PO Value (Current) */}
                              <Grid item xs={12} sm={4}>
                                <Card
                                  elevation={0}
                                  sx={{
                                    bgcolor: "background.paper",
                                    border: 1,
                                    borderColor: "divider",
                                    borderRadius: 2,
                                  }}
                                >
                                  <CardContent
                                    sx={{ p: 2, "&:last-child": { pb: 2 } }}
                                  >
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{
                                        fontSize: "0.7rem",
                                        textTransform: "uppercase",
                                        letterSpacing: 0.5,
                                      }}
                                    >
                                      PO Value
                                    </Typography>
                                    <Typography
                                      variant="h6"
                                      sx={{
                                        fontWeight: 700,
                                        color: "primary.main",
                                        mt: 0.5,
                                      }}
                                    >
                                      {formatCurrency(
                                        editFormData.po_value_original || 0,
                                        editFormData.currency || null,
                                      )}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ fontSize: "0.7rem" }}
                                    >
                                      {editFormData.currency || "-"}
                                    </Typography>
                                  </CardContent>
                                </Card>
                              </Grid>

                              {/* Total Reduction */}
                              <Grid item xs={12} sm={4}>
                                <Card
                                  elevation={0}
                                  sx={{
                                    bgcolor: "background.paper",
                                    border: 1,
                                    borderColor: "divider",
                                    borderRadius: 2,
                                  }}
                                >
                                  <CardContent
                                    sx={{ p: 2, "&:last-child": { pb: 2 } }}
                                  >
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{
                                        fontSize: "0.7rem",
                                        textTransform: "uppercase",
                                        letterSpacing: 0.5,
                                      }}
                                    >
                                      Total Reduction
                                    </Typography>
                                      {(() => {
                                      // Use same "value before first cancellation" as Edit page (editPOOriginalValue), not po_value_initial which can be wrong
                                      const originalValue = editPOOriginalValue;
                                      if (
                                        originalValue &&
                                        editFormData.po_value_original
                                      ) {
                                        return (
                                          <>
                                            <Typography
                                              variant="h6"
                                              sx={{
                                                fontWeight: 700,
                                                color: "error.main",
                                                mt: 0.5,
                                              }}
                                            >
                                              {formatCurrency(
                                                editFormData.po_value_original -
                                                  originalValue,
                                                  editFormData.currency || null,
                                              )}
                                            </Typography>
                                            <Typography
                                              variant="caption"
                                              color="text.secondary"
                                              sx={{ fontSize: "0.7rem" }}
                                            >
                                              {(
                                                ((editFormData.po_value_original -
                                                  originalValue) /
                                                  originalValue) *
                                                100
                                              ).toFixed(2)}
                                              %
                                            </Typography>
                                          </>
                                        );
                                      }
                                      return (
                                        <Typography
                                          variant="body2"
                                          color="text.disabled"
                                          sx={{ mt: 0.5 }}
                                        >
                                          No cancellations yet
                                        </Typography>
                                      );
                                    })()}
                                  </CardContent>
                                </Card>
                              </Grid>
                            </Grid>

                     {/* Add New Cancellation Event Form */}
                            <Card
                              elevation={1}
                              sx={{
                                mb: 3,
                                borderRadius: 2,
                                border: 1,
                                borderColor: "warning.300",
                              }}
                            >
                              <CardContent
                                sx={{
                                  p: { xs: 1.5, sm: 2, md: 2.5 },
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 2,
                                    mb: 2,
                                  }}
                                >
                                  <Button
                                    variant="contained"
                                    color="warning"
                                    startIcon={<AddIcon />}
                                    onClick={handleEditPOUpsertCancellationEvent}
                                    disabled={
                                      submitting ||
                                      editPONewCancellationValue === null ||
                                      !editPONewCancellationDate ||
                                      can("update") === false
                                    }
                                    sx={{
                                      textTransform: "none",
                                      fontWeight: 600,
                                      fontSize: {
                                        xs: "0.8rem",
                                        sm: "0.85rem",
                                        md: "0.8125rem",
                                      },
                                    }}
                                  >
                                    {editPOEditingCancellationId
                                      ? "Update Cancellation Event"
                                      : "Add Cancellation Event"}
                                  </Button>
                                </Box>

                                              {!editPOEditingCancellationId && (
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      mb: 2,
                                      color: "text.secondary",
                                      fontSize: {
                                        xs: "0.75rem",
                                        sm: "0.8rem",
                                        md: "0.75rem",
                                      },
                                    }}
                                  >
                                    Value before this cancellation:{" "}
                                    {formatCurrency(
                                      editPOCancellationEvents.length > 0
                                        ? editPOCancellationEvents[editPOCancellationEvents.length - 1].new_value
                                        : editPOOriginalValue,
                                      editFormData.currency || "USD",
                                    )}
                                  </Typography>
                                )}

                              <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                                  <Grid item xs={12} sm={6} md={6} lg={6}>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                      <DatePicker
                                        label="Cancellation Date"
                                        value={
                                          editPONewCancellationDate
                                            ? dayjs(editPONewCancellationDate)
                                            : null
                                        }
                                        onChange={(newValue) => {
                                          setEditPONewCancellationDate(
                                            newValue
                                              ? newValue.format("YYYY-MM-DD")
                                              : "",
                                          );
                                        }}
                                        disabled={submitting}
                                        slotProps={{
                                          textField: {
                                            size: "small",
                                            fullWidth: true,
                                            InputLabelProps: { shrink: true },
                                            sx: {
                                              fontWeight:700,
                                              "& .MuiOutlinedInput-root": {
                                                borderRadius: 1,
                                              },
                                              "& .MuiInputLabel-root": {
                                                fontSize: {
                                                  xs: "0.75rem",
                                                  sm: "0.8rem",
                                                  md: "0.85rem",
                                                },
                                              },
                                              "& .MuiInputBase-input": {
                                                fontSize: {
                                                  xs: "0.8rem",
                                                  sm: "0.85rem",
                                                  md: "0.85rem",
                                                },
                                              },
                                              "& .MuiInputBase-input::placeholder": {
                                                fontSize: {
                                                  xs: "0.8rem",
                                                  sm: "0.85rem",
                                                  md: "0.85rem",
                                                },
                                              },
                                            },
                                          },
                                        }}
                                        format="DD/MM/YYYY"
                                      />
                                    </LocalizationProvider>
                                  </Grid>
                                  <Grid item xs={12} sm={6} md={6} lg={6}>
                                    <TextField
                                      label="PO Value After Cancellation"
                                      type="number"
                                      value={
                                        editPONewCancellationValue !== null
                                          ? editPONewCancellationValue
                                          : ""
                                      }
                                      onChange={(e) =>
                                        setEditPONewCancellationValue(
                                          e.target.value
                                            ? parseFloat(e.target.value)
                                            : null,
                                        )
                                      }
                                      fullWidth
                                      size="small"
                                      inputProps={{ step: "0.01", min: "0" }}
                                      disabled={submitting}
                                      placeholder="Enter new value"
                                      sx={{
                                        fontWeight:700,
                                        "& .MuiOutlinedInput-root": {
                                          borderRadius: 1,
                                        },
                                        "& .MuiInputLabel-root": {
                                          fontSize: {
                                            xs: "0.75rem",
                                            sm: "0.8rem",
                                            md: "0.85rem",
                                          },
                                        },
                                        "& .MuiInputBase-input": {
                                          fontSize: {
                                            xs: "0.8rem",
                                            sm: "0.85rem",
                                            md: "0.85rem",
                                          },
                                        },
                                        "& .MuiInputBase-input::placeholder": {
                                          fontSize: {
                                            xs: "0.8rem",
                                            sm: "0.85rem",
                                            md: "0.85rem",
                                          },
                                        },
                                      }}
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={12}>
                                    <TextField
                                      label="Cancellation Description"
                                      value={editPONewCancellationDescription}
                                      onChange={(e) =>
                                        setEditPONewCancellationDescription(e.target.value)
                                      }
                                      fullWidth
                                      size="small"
                                      multiline
                                      rows={2}
                                      disabled={submitting}
                                      placeholder="Enter description for this cancellation event..."
                                      sx={{
                                        fontWeight:700,
                                        "& .MuiOutlinedInput-root": {
                                          borderRadius: 1,
                                        },
                                        "& .MuiInputLabel-root": {
                                          fontSize: {
                                            xs: "0.75rem",
                                            sm: "0.8rem",
                                            md: "0.85rem",
                                          },
                                        },
                                        "& .MuiInputBase-input": {
                                          fontSize: {
                                            xs: "0.8rem",
                                            sm: "0.85rem",
                                            md: "0.85rem",
                                          },
                                        },
                                        "& .MuiInputBase-input::placeholder": {
                                          fontSize: {
                                            xs: "0.8rem",
                                            sm: "0.85rem",
                                            md: "0.85rem",
                                          },
                                        },
                                      }}
                                    />
                                  </Grid>
                                </Grid>
                              </CardContent>
                            </Card>
                            {/* Render editPOCancellationEvents as JSON */}
                            {/* <pre>{JSON.stringify(editPOCancellationEvents, null, 2)}</pre>

                            <pre>ORIGINAL_VALUE = {JSON.stringify(editPOOriginalValue)}</pre> */}
                            {/* <pre>EDIT_FORM_DATA = {JSON.stringify(editFormData, null, 2)}</pre> */}
                            {/* Cancellation Events List */}
                            {editPOCancellationEvents.length > 0 && (
                              <Box>
                                <Typography
                                  variant="subtitle2"
                                  sx={{
                                    mb: 2,
                                    fontWeight: 600,
                                    color: "text.secondary",
                                    textTransform: "uppercase",
                                    letterSpacing: 0.5,
                                    fontSize: "0.75rem",
                                  }}
                                >


        {/* //! --------------------- Edit PO Cancellation History Table -------------------------------- */}
                                 
                                 Cancellation History
                                </Typography>
                                <TableContainer
                                 component={Paper}
                                 elevation={1}
                                 sx={{
                                   borderRadius: 2,
                                   border: 1,
                                   borderColor: "divider",
                                   overflowX: "auto",
                                   WebkitOverflowScrolling: "touch",
                                   maxWidth: "100%",
                                 }}
                                >
                                  {/* <small><pre>editPOOriginalValue :{JSON.stringify(editPOOriginalValue, null, 2)}</pre></small> */}
                                  <Table size="small" sx={{ minWidth: 720 }}>
                                    <TableHead>
                                      <TableRow sx={{ bgcolor: "grey.50" }}>
                                        <TableCell
                                          sx={{
                                            fontWeight: 700,
                                            fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" },
                                          }}
                                        >
                                          #
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            fontWeight: 700,
                                            fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" },
                                          }}
                                        >
                                          Date
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            fontWeight: 700,
                                            fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" },
                                          }}
                                        >
                                          Description
                                        </TableCell>
                                        {/* <TableCell
                                          sx={{
                                            fontWeight: 700,
                                            fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" },
                                          }}
                                          align="right"
                                        >
                                          Original PO Value
                                        </TableCell> */}
                                        <TableCell
                                          sx={{
                                            fontWeight: 700,
                                            fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" },
                                          }}
                                          align="right"
                                        >
                                          PO Value
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            fontWeight: 700,
                                            fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" },
                                          }}
                                          align="right"
                                        >
                                          Change Amount
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            fontWeight: 700,
                                            fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" },
                                          }}
                                          align="right"
                                        >
                                          Change %
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            fontWeight: 700,
                                            fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" },
                                          }}
                                          align="right"
                                        >
                                          Reduction from Original
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            fontWeight: 700,
                                            fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" },
                                          }}
                                          align="right"
                                        >
                                          Actions
                                        </TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {editPOCancellationEvents.map(
                                        (event, index) => {
                                          const originalValue = editPOOriginalValue;
                                          const reductionFromOriginal =
                                            event.new_value - originalValue;
                                          const reductionPercentageFromOriginal =
                                            originalValue !== 0
                                              ? (reductionFromOriginal / originalValue) * 100
                                              : 0;

                                          const descriptionText = event.description || "-";
                                          return (
                                            <TableRow key={event.id} hover>
                                              <TableCell sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem", md: "0.8125rem" } }}>
                                                {index + 1}
                                              </TableCell>
                                              {/* date */}
                                              <TableCell sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem", md: "0.8125rem" } }}>
                                                {formatDate(event.date)}
                                              </TableCell>
                                              {/* description */}
                                              <TableCell sx={{ maxWidth: { xs: 100, sm: 140, md: 200 } }}>
                                                {/* <Tooltip title={descriptionText} placement="top" enterDelay={300}>
                                                 
                                                </Tooltip> */}
                                                 <Typography
                                                    variant="body2"
                                                    sx={{
                                                      fontSize: { xs: "0.7rem", sm: "0.75rem", md: "0.8125rem" },
                                                      overflow: "hidden",
                                                      textOverflow: "ellipsis",
                                                      whiteSpace: "nowrap",
                                                      maxWidth: "100%",
                                                    }}
                                                  >
                                                    {descriptionText}
                                                  </Typography>
                                            
                                              </TableCell>
                                              {/* original po value */}
                                              {/* <TableCell align="right" sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem", md: "0.8125rem" } }}>
                                                <Typography
                                                  variant="body2"
                                                  fontWeight="medium"
                                                  color="text.secondary"
                                                  sx={{ fontSize: "inherit" }}
                                                >
                                                  {formatCurrency(
                                                    originalValue,
                                                    editFormData.currency || null,
                                                  )}
                                                </Typography>
                                              </TableCell> */}

                                              {/* po value */}
                                              <TableCell align="right" sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem", md: "0.8125rem" } }}>
                                                <Typography
                                                  variant="body2"
                                                  fontWeight="medium"
                                                  sx={{ fontSize: "inherit" }}
                                                >
                                                  {formatCurrency(
                                                    event.new_value,
                                                    editFormData.currency || null,
                                                  )}
                                                </Typography>
                                              </TableCell>


                                              {/* change amount */}
                                              <TableCell align="right" sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem", md: "0.8125rem" } }}>
                                                <Typography
                                                  variant="body2"
                                                  fontWeight="600"
                                                  color={
                                                    event.change_amount < 0
                                                      ? "error.main"
                                                      : "success.main"
                                                  }
                                                  sx={{ fontSize: "inherit" }}
                                                >
                                                  {formatCurrency(
                                                    event.change_amount,
                                                    editFormData.currency || null,
                                                  )}
                                                </Typography>
                                              </TableCell>

                                                {/* change percentage */}
                                              <TableCell align="right" sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem", md: "0.8125rem" } }}>
                                                <Typography
                                                  variant="body2"
                                                  color={
                                                    event.change_percentage < 0
                                                      ? "error.main"
                                                      : "success.main"
                                                  }
                                                  sx={{ fontSize: "inherit" }}
                                                >
                                                  {event.change_percentage.toFixed(2)}%
                                                </Typography>
                                              </TableCell>

                                              {/* Reduction from original */}
                                              <TableCell align="right" sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem", md: "0.8125rem" } }}>
                                                <Typography
                                                  variant="body2"
                                                  fontWeight="600"
                                                  color="error.main"
                                                  sx={{ fontSize: "inherit" }}
                                                >
                                                  {formatCurrency(
                                                    reductionFromOriginal,
                                                    editFormData.currency || null,
                                                  )}
                                                </Typography>
                                                <Typography
                                                  variant="caption"
                                                  color="error.main"
                                                  sx={{
                                                    display: "block",
                                                    fontSize: { xs: "0.6rem", sm: "0.65rem", md: "0.7rem" },
                                                  }}
                                                >
                                                  (
                                                  {reductionPercentageFromOriginal.toFixed(
                                                    2,
                                                  )}
                                                  %)
                                                </Typography>
                                              </TableCell>

                                              {/* Actions */}
                                              <TableCell align="right">
                                                <Box
                                                  sx={{
                                                    display: "flex",
                                                    gap: 0.5,
                                                    justifyContent: "flex-end",
                                                  }}
                                                >
                                                  <Tooltip title="Edit Cancellation Event">
                                                    <IconButton
                                                      size="small"
                                                      onClick={() =>
                                                        handleEditPOStartEditCancellationEvent(
                                                          event,
                                                        )
                                                      }
                                                      disabled={
                                                        submitting ||
                                                        can("update") === false
                                                      }
                                                      sx={{
                                                        color: "warning.main",
                                                        "&:hover": {
                                                          bgcolor:
                                                            "warning.light",
                                                          color: "white",
                                                        },
                                                      }}
                                                    >
                                                      <EditIcon fontSize="small" />
                                                    </IconButton>
                                                  </Tooltip>
                                                  <Tooltip title="Delete Cancellation Event">
                                                    <IconButton
                                                      size="small"
                                                      onClick={() =>
                                                        setConfirmDeleteEditPOCancellationEventId(
                                                          event.id,
                                                        )
                                                      }
                                                      disabled={
                                                        submitting ||
                                                        can("delete") === false
                                                      }
                                                      sx={{
                                                        color: "error.main",
                                                        "&:hover": {
                                                          bgcolor:
                                                            "error.light",
                                                          color: "white",
                                                        },
                                                      }}
                                                    >
                                                      <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                  </Tooltip>
                                                </Box>
                                              </TableCell>


                                            </TableRow>
                                          );
                                        },
                                      )}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              </Box>
                            )}

                {/* <Grid container spacing={{ xs: 2, sm: 2.5 }}>
                  <Grid item xs={12} sm={6}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="PO Date"
                        value={
                          editFormData.po_date
                            ? dayjs(editFormData.po_date)
                            : null
                        }
                        onChange={(newValue) => {
                          setEditFormData({
                            ...editFormData,
                            po_date: newValue
                              ? newValue.format("YYYY-MM-DD")
                              : null,
                          });
                          if (editFormErrors.po_date)
                            setEditFormErrors({
                              ...editFormErrors,
                              po_date: "",
                            });
                        }}
                        slotProps={{
                          textField: {
                            size: "small",
                            fullWidth: true,
                            required: true,
                            InputLabelProps: { shrink: true },
                            error: !!editFormErrors.po_date,
                            helperText: editFormErrors.po_date,
                            sx: {
                              "& .MuiOutlinedInput-root": { borderRadius: 1 },
                              "& .MuiInputLabel-root": {
                                fontSize: {
                                  xs: "0.75rem",
                                  sm: "0.8rem",
                                  md: "0.85rem",
                                },
                              },
                              "& .MuiInputBase-input": {
                                fontSize: {
                                  xs: "0.8rem",
                                  sm: "0.85rem",
                                  md: "0.9rem",
                                },
                              },
                              "& .MuiFormHelperText-root": {
                                fontSize: { xs: "0.65rem", sm: "0.7rem" },
                              },
                            },
                          },
                        }}
                        format="DD/MM/YYYY"
                      />
                    </LocalizationProvider>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl
                      fullWidth
                      size="small"
                      required
                      error={!!editFormErrors.status}
                      sx={{
                        "& .MuiOutlinedInput-root": { borderRadius: 1 },
                        "& .MuiInputLabel-root": {
                          fontSize: {
                            xs: "0.75rem",
                            sm: "0.8rem",
                            md: "0.85rem",
                          },
                        },
                        "& .MuiSelect-select": {
                          fontSize: {
                            xs: "0.8rem",
                            sm: "0.85rem",
                            md: "0.9rem",
                          },
                        },
                        "& .MuiFormHelperText-root": {
                          fontSize: { xs: "0.65rem", sm: "0.7rem" },
                        },
                      }}
                    >
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={editFormData.status}
                        label="Status"
                        onChange={(e) => {
                          setEditFormData({
                            ...editFormData,
                            status: e.target.value,
                          });
                          if (editFormErrors.status)
                            setEditFormErrors({
                              ...editFormErrors,
                              status: "",
                            });
                        }}
                      >
                        <MenuItem value="Active">Active</MenuItem>
                        <MenuItem value="Expired">Expired</MenuItem>
                        <MenuItem value="Cancelled">Cancelled</MenuItem>
                        <MenuItem value="Partially Cancelled">
                          Partially Cancelled
                        </MenuItem>
                      </Select>
                      {editFormErrors.status && (
                        <FormHelperText
                          sx={{ fontSize: { xs: "0.65rem", sm: "0.7rem" } }}
                        >
                          {editFormErrors.status}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                </Grid> */}
              </CardContent>
              </Card>
            )}

            {/* Client & Project Section */}
            <Card
              elevation={0}
              sx={{ mb: 3, border: 1, borderColor: "divider", borderRadius: 1 }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    mb: { xs: 2, sm: 2.5 },
                    fontWeight: 700,
                    color: "text.primary",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    fontSize: {
                      xs: "0.65rem",
                      sm: "0.7rem",
                      md: "0.75rem",
                      lg: "0.8rem",
                    },
                  }}
                >
                  Client & Project
                </Typography>
                <Grid container spacing={{ xs: 2, sm: 2.5 }}>
                  <Grid item xs={12} sm={6}>
                    <CreatableSearchSelect
                      label="Client Name"
                      required
                      value={editFormData.client_name || ""}
                      onChange={(newValue) =>
                        setEditFormData({ ...editFormData, client_name: newValue })
                      }
                      options={poClientNames}
                      loading={loadingPOClientNames}
                      loadError={poClientNamesError}
                      fieldError={editFormErrors.client_name}
                      onClearFieldError={() => {
                        if (editFormErrors.client_name) {
                          setEditFormErrors({
                            ...editFormErrors,
                            client_name: "",
                          });
                        }
                      }}
                      createCtaLabel="Create new Client Name"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <CreatableSearchSelect
                      label="Project Name"
                      required
                      value={editFormData.project_name || ""}
                      onChange={(newValue) =>
                        setEditFormData({ ...editFormData, project_name: newValue })
                      }
                      options={poProjectNames}
                      loading={loadingPOProjectNames}
                      loadError={poProjectNamesError}
                      fieldError={editFormErrors.project_name}
                      onClearFieldError={() => {
                        if (editFormErrors.project_name) {
                          setEditFormErrors({
                            ...editFormErrors,
                            project_name: "",
                          });
                        }
                      }}
                      createCtaLabel="Create new Project Name"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl
                      fullWidth
                      size="small"
                      required
                      error={!!editFormErrors.project_type}
                      sx={{
                        "& .MuiOutlinedInput-root": { borderRadius: 1 },
                        "& .MuiInputLabel-root": {
                          fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" },
                        },
                        "& .MuiSelect-select": {
                          fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                        },
                      }}
                    >
                      <InputLabel>Project Type</InputLabel>
                      <Select
                        value={editFormData.project_type}
                        label="Project Type"
                        onChange={(e) => {
                          setEditFormData({
                            ...editFormData,
                            project_type: e.target.value,
                          });
                          if (editFormErrors.project_type)
                            setEditFormErrors({
                              ...editFormErrors,
                              project_type: "",
                            });
                        }}
                      >
                        <MenuItem value="">None</MenuItem>
                        <MenuItem value="Staff Aug">Staff Aug</MenuItem>
                        <MenuItem value="Managed Services">
                          Managed Services
                        </MenuItem>
                        <MenuItem value="Turnkey">Turnkey</MenuItem>
                      </Select>
                      {editFormErrors.project_type && (
                        <FormHelperText
                          sx={{ fontSize: { xs: "0.65rem", sm: "0.7rem" } }}
                        >
                          {editFormErrors.project_type}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl
                      fullWidth
                      size="small"
                      required
                      error={!!editFormErrors.geography}
                      sx={{
                        "& .MuiOutlinedInput-root": { borderRadius: 1 },
                        "& .MuiInputLabel-root": {
                          fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" },
                        },
                        "& .MuiSelect-select": {
                          fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                        },
                      }}
                    >
                      <InputLabel>Geography</InputLabel>
                      <Select
                        value={editFormData.geography || ""}
                        label="Geography"
                        onChange={(e) => {
                          setEditFormData({
                            ...editFormData,
                            geography: e.target.value as string,
                          });
                          if (editFormErrors.geography)
                            setEditFormErrors({
                              ...editFormErrors,
                              geography: "",
                            });
                        }}
                      >
                        <MenuItem value="">None</MenuItem>
                        <MenuItem value="Europe">Europe</MenuItem>
                        <MenuItem value="USA">USA</MenuItem>
                        <MenuItem value="India">India</MenuItem>
                        <MenuItem value="Singapore">Singapore</MenuItem>
                      </Select>
                      {editFormErrors.geography && (
                        <FormHelperText
                          sx={{ fontSize: { xs: "0.65rem", sm: "0.7rem" } }}
                        >
                          {editFormErrors.geography}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <CreatableSearchSelect
                      label="Account Manager (AM)"
                      required
                      value={editFormData.account_manager || ""}
                      onChange={(newValue) =>
                        setEditFormData({
                          ...editFormData,
                          account_manager: newValue,
                        })
                      }
                      options={accountManagers}
                      loading={loadingAccountManagers}
                      loadError={accountManagersError}
                      fieldError={editFormErrors.account_manager}
                      onClearFieldError={() => {
                        if (editFormErrors.account_manager) {
                          setEditFormErrors({
                            ...editFormErrors,
                            account_manager: "",
                          });
                        }
                      }}
                      createCtaLabel="Create new Account Manager"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <CreatableSearchSelect
                      label="Project Manager (PM)"
                      required
                      value={editFormData.project_manager || ""}
                      onChange={(newValue) =>
                        setEditFormData({
                          ...editFormData,
                          project_manager: newValue,
                        })
                      }
                      options={projectManagers}
                      loading={loadingProjectManagers}
                      loadError={projectManagersError}
                      fieldError={editFormErrors.project_manager}
                      onClearFieldError={() => {
                        if (editFormErrors.project_manager) {
                          setEditFormErrors({
                            ...editFormErrors,
                            project_manager: "",
                          });
                        }
                      }}
                      createCtaLabel="Create new Project Manager"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl
                      fullWidth
                      size="small"
                      required
                      error={!!editFormErrors.revenue_type}
                      sx={{
                        "& .MuiOutlinedInput-root": { borderRadius: 1 },
                        "& .MuiInputLabel-root": {
                          fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" },
                        },
                        "& .MuiSelect-select": {
                          fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                        },
                      }}
                    >
                      <InputLabel>Revenue Type</InputLabel>
                      <Select
                        value={editFormData.revenue_type}
                        label="Revenue Type"
                        onChange={(e) => {
                          setEditFormData({
                            ...editFormData,
                            revenue_type: e.target.value,
                          });
                          if (editFormErrors.revenue_type)
                            setEditFormErrors({
                              ...editFormErrors,
                              revenue_type: "",
                            });
                        }}
                      >
                        <MenuItem value="">None</MenuItem>
                        <MenuItem value="ENEB">ENEB</MenuItem>
                        <MenuItem value="ENNB">ENNB</MenuItem>
                        <MenuItem value="NNNB">NNNB</MenuItem>
                      </Select>
                      {editFormErrors.revenue_type && (
                        <FormHelperText
                          sx={{ fontSize: { xs: "0.65rem", sm: "0.7rem" } }}
                        >
                          {editFormErrors.revenue_type}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl
                      fullWidth
                      size="small"
                      required
                      error={!!editFormErrors.revenue_class}
                      sx={{
                        "& .MuiOutlinedInput-root": { borderRadius: 1 },
                        "& .MuiInputLabel-root": {
                          fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" },
                        },
                        "& .MuiSelect-select": {
                          fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                        },
                      }}
                    >
                      <InputLabel>Revenue Class</InputLabel>
                      <Select
                        value={editFormData.revenue_class}
                        label="Revenue Class"
                        onChange={(e) => {
                          setEditFormData({
                            ...editFormData,
                            revenue_class: e.target.value,
                          });
                          if (editFormErrors.revenue_class)
                            setEditFormErrors({
                              ...editFormErrors,
                              revenue_class: "",
                            });
                        }}
                      >
                        <MenuItem value="">None</MenuItem>
                        <MenuItem value="Backlog">Backlog</MenuItem>
                        <MenuItem value="New">New</MenuItem>
                      </Select>
                      {editFormErrors.revenue_class && (
                        <FormHelperText
                          sx={{ fontSize: { xs: "0.65rem", sm: "0.7rem" } }}
                        >
                          {editFormErrors.revenue_class}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Linked MSA"
                      value={editFormData.linked_msa}
                      onChange={(e) => {
                        setEditFormData({
                          ...editFormData,
                          linked_msa: e.target.value,
                        });
                        if (editFormErrors.linked_msa)
                          setEditFormErrors({
                            ...editFormErrors,
                            linked_msa: "",
                          });
                      }}
                      fullWidth
                      size="small"
                      error={!!editFormErrors.linked_msa}
                      helperText={editFormErrors.linked_msa}
                      sx={{
                        "& .MuiOutlinedInput-root": { borderRadius: 1 },
                        "& .MuiInputLabel-root": {
                          fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" },
                        },
                        "& .MuiInputBase-input": {
                          fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                        },
                        "& .MuiFormHelperText-root": {
                          fontSize: { xs: "0.65rem", sm: "0.7rem" },
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Financial Details Section */}
            <Card
              elevation={0}
              sx={{ mb: 3, border: 1, borderColor: "divider", borderRadius: 1 }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    mb: { xs: 2, sm: 2.5 },
                    fontWeight: 700,
                    color: "text.primary",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    fontSize: {
                      xs: "0.65rem",
                      sm: "0.7rem",
                      md: "0.75rem",
                      lg: "0.8rem",
                    },
                  }}
                >
                  Financial Details
                </Typography>
                <Grid container spacing={{ xs: 2, sm: 2.5 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      required
                      label="PO Value"
                      type="number"
                      value={editFormData.po_value_original || ""}
                      onChange={(e) => {
                        setEditFormData({
                          ...editFormData,
                          po_value_original: e.target.value
                            ? parseFloat(e.target.value)
                            : null,
                        });
                        if (editFormErrors.po_value_original)
                          setEditFormErrors({
                            ...editFormErrors,
                            po_value_original: "",
                          });
                      }}
                      fullWidth
                      size="small"
                      inputProps={{ step: "0.01", min: "0" }}
                      error={!!editFormErrors.po_value_original}
                      helperText={
                        editFormErrors.po_value_original ||
                        "Current PO value (used for calculations)"
                      }
                      sx={{
                        "& .MuiOutlinedInput-root": { borderRadius: 1 },
                        "& .MuiInputLabel-root": {
                          fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" },
                        },
                        "& .MuiInputBase-input": {
                          fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                        },
                        "& .MuiFormHelperText-root": {
                          fontSize: { xs: "0.65rem", sm: "0.7rem" },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl
                      fullWidth
                      size="small"
                      required
                      error={!!editFormErrors.currency}
                      sx={{
                        "& .MuiOutlinedInput-root": { borderRadius: 1 },
                        "& .MuiInputLabel-root": {
                          fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" },
                        },
                        "& .MuiSelect-select": {
                          fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                        },
                      }}
                    >
                      <InputLabel>Currency</InputLabel>
                      <Select
                        value={editFormData.currency}
                        label="Currency"
                        onChange={(e) => {
                          setEditFormData({
                            ...editFormData,
                            currency: e.target.value as string,
                          });
                          if (editFormErrors.currency)
                            setEditFormErrors({
                              ...editFormErrors,
                              currency: "",
                            });
                        }}
                      >
                        <MenuItem value="USD">USD</MenuItem>
                        <MenuItem value="EUR">EUR</MenuItem>
                        <MenuItem value="INR">INR</MenuItem>
                      </Select>
                      {editFormErrors.currency && (
                        <FormHelperText
                          sx={{ fontSize: { xs: "0.65rem", sm: "0.7rem" } }}
                        >
                          {editFormErrors.currency}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      required
                      label="FX Rate (Base to USD)"
                      type="number"
                      value={editFormData.fx_rate_usd || ""}
                      onChange={(e) => {
                        setEditFormData({
                          ...editFormData,
                          fx_rate_usd: e.target.value
                            ? parseFloat(e.target.value)
                            : null,
                        });
                        if (editFormErrors.fx_rate_usd)
                          setEditFormErrors({
                            ...editFormErrors,
                            fx_rate_usd: "",
                          });
                      }}
                      fullWidth
                      size="small"
                      inputProps={{ step: "0.000001", min: "0" }}
                      error={!!editFormErrors.fx_rate_usd}
                      helperText={
                        editFormErrors.fx_rate_usd ||
                        "Rate to convert base currency to USD"
                      }
                      sx={{
                        "& .MuiOutlinedInput-root": { borderRadius: 1 },
                        "& .MuiInputLabel-root": {
                          fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" },
                        },
                        "& .MuiInputBase-input": {
                          fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                        },
                        "& .MuiFormHelperText-root": {
                          fontSize: { xs: "0.65rem", sm: "0.7rem" },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      required
                      label="FX Rate (Base to INR)"
                      type="number"
                      value={editFormData.fx_rate_inr || ""}
                      onChange={(e) => {
                        setEditFormData({
                          ...editFormData,
                          fx_rate_inr: e.target.value
                            ? parseFloat(e.target.value)
                            : null,
                        });
                        if (editFormErrors.fx_rate_inr)
                          setEditFormErrors({
                            ...editFormErrors,
                            fx_rate_inr: "",
                          });
                      }}
                      fullWidth
                      size="small"
                      inputProps={{ step: "0.000001", min: "0" }}
                      error={!!editFormErrors.fx_rate_inr}
                      helperText={
                        editFormErrors.fx_rate_inr ||
                        "Rate to convert base currency to INR"
                      }
                      sx={{
                        "& .MuiOutlinedInput-root": { borderRadius: 1 },
                        "& .MuiInputLabel-root": {
                          fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" },
                        },
                        "& .MuiInputBase-input": {
                          fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                        },
                        "& .MuiFormHelperText-root": {
                          fontSize: { xs: "0.65rem", sm: "0.7rem" },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      required
                      label="Payment Terms"
                      value={editFormData.payment_terms}
                      onChange={(e) => {
                        setEditFormData({
                          ...editFormData,
                          payment_terms: e.target.value,
                        });
                        if (editFormErrors.payment_terms)
                          setEditFormErrors({
                            ...editFormErrors,
                            payment_terms: "",
                          });
                      }}
                      fullWidth
                      size="small"
                      error={!!editFormErrors.payment_terms}
                      helperText={
                        editFormErrors.payment_terms || "e.g., 30, 60, EOM+60"
                      }
                      sx={{
                        "& .MuiOutlinedInput-root": { borderRadius: 1 },
                        "& .MuiInputLabel-root": {
                          fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" },
                        },
                        "& .MuiInputBase-input": {
                          fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                        },
                        "& .MuiFormHelperText-root": {
                          fontSize: { xs: "0.65rem", sm: "0.7rem" },
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Additional Information Section */}
            <Card
              elevation={0}
              sx={{ mb: 2, border: 1, borderColor: "divider", borderRadius: 1 }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    mb: { xs: 2, sm: 2.5 },
                    fontWeight: 700,
                    color: "text.primary",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    fontSize: {
                      xs: "0.65rem",
                      sm: "0.7rem",
                      md: "0.75rem",
                      lg: "0.8rem",
                    },
                  }}
                >
                  Additional Information
                </Typography>
                <Grid container spacing={{ xs: 2, sm: 2.5 }}>
                  <Grid item xs={12}>
                    <TextField
                      label="Remarks"
                      value={editFormData.remarks}
                      onChange={(e) =>
                        setEditFormData({
                          ...editFormData,
                          remarks: e.target.value,
                        })
                      }
                      fullWidth
                      size="small"
                      multiline
                      rows={3}
                      placeholder="Enter any additional notes or remarks..."
                      sx={{
                        "& .MuiOutlinedInput-root": { borderRadius: 2 },
                        "& .MuiInputLabel-root": {
                          fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" },
                        },
                        "& .MuiInputBase-input": {
                          fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                        },
                        "& .MuiFormHelperText-root": {
                          fontSize: { xs: "0.65rem", sm: "0.7rem" },
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </DialogContent>
          <DialogActions
            sx={{
              p: { xs: 2, sm: 3 },
              borderTop: 1,
              borderColor: "divider",
              gap: { xs: 1, sm: 1.5 },
              bgcolor: "background.paper",
              position: "sticky",
              bottom: 0,
              zIndex: 1,
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "stretch", sm: "center" },
              justifyContent: { xs: "stretch", sm: "flex-end" },
            }}
          >
            <Button
              onClick={handleCloseEditDialog}
              disabled={submitting}
              sx={{
                textTransform: "none",
                fontSize: { xs: "0.75rem", sm: "0.85rem", md: "0.9rem" },
                minWidth: { xs: "100%", sm: 120, md: 130 },
                px: { xs: 2, sm: 2.5, md: 3 },
                height: { xs: 34, sm: 38, md: 40 },
                color: "text.secondary",
                "&:hover": {
                  bgcolor: "action.hover",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={submitting || can("update") === false}
              startIcon={
                submitting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <EditIcon />
                )
              }
              color="warning"
              sx={{
                textTransform: "none",
                fontSize: { xs: "0.75rem", sm: "0.85rem", md: "0.9rem" },
                minWidth: { xs: "100%", sm: 160, md: 180 },
                px: { xs: 2, sm: 2.5, md: 3 },
                height: { xs: 34, sm: 38, md: 40 },
                fontWeight: 600,
                boxShadow: 2,
                "&:hover": {
                  boxShadow: 4,
                },
              }}
            >
              {submitting ? "Updating..." : "Update Purchase Order"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* PO Detail Drawer */}
      <Drawer
        anchor="right"
        open={openPODetail}
        onClose={handleClosePODetail}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: "92%", md: "85%", lg: "78%", xl: "70%" },
            maxWidth: { xs: "100%", sm: 1200, md: 1400 },
            boxShadow: "-4px 0 20px rgba(0,0,0,0.1)",
            borderTopLeftRadius: { xs: 0, sm: 2 },
            borderBottomLeftRadius: { xs: 0, sm: 2 },
          },
        }}
      >
        <Box
          sx={{
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Header Section */}
          <Box
            sx={{
              px: { xs: 2, sm: 2.5, md: 3 },
              py: { xs: 2, sm: 2.5, md: 3 },
              borderBottom: 1,
              borderColor: "divider",
              bgcolor: "background.paper",
              position: "sticky",
              top: 0,
              zIndex: 10,
              boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                mb: { xs: 1.5, sm: 2 },
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: { xs: 1, sm: 1.5, md: 2 },
                    mb: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <Typography
                    variant="h4"
                    component="h2"
                    sx={{
                      fontWeight: 700,
                      color: "primary.main",
                      fontSize: "clamp(1.25rem, 2.2vw, 2rem)",
                      lineHeight: 1.15,
                    }}
                  >
                    {poDetailPO?.po_code}
                  </Typography>

                  {/* //! ------- PO Status Chip ------- */}
                  {poDetailPO && (
                    <Tooltip
                      title={
                        (poStatus || poDetailPO.status) ===
                        "Partially Cancelled"
                          ? "Go to Cancellation Events"
                          : ""
                      }
                    >
                      <Chip
                        label={poStatus || poDetailPO.status || "-"}
                        size="small"
                        color={
                          (poStatus || poDetailPO.status) === "Active"
                            ? "success"
                            : (poStatus || poDetailPO.status) === "Cancelled"
                              ? "error"
                              : (poStatus || poDetailPO.status) ===
                                  "Partially Cancelled"
                                ? "warning"
                                : "default"
                        }
                        clickable={
                          (poStatus || poDetailPO.status) ===
                          "Partially Cancelled"
                        }
                        onClick={() => {
                          const status = poStatus || poDetailPO.status;
                          if (status === "Partially Cancelled") {
                            document
                              .getElementById("po-partially-cancelled-section")
                              ?.scrollIntoView({
                                behavior: "smooth",
                                block: "start",
                              });
                          }
                        }}
                        sx={{
                          fontWeight: 600,
                          fontSize: {
                            xs: "0.7rem",
                            sm: "0.75rem",
                            md: "0.8rem",
                          },
                          height: { xs: 22, sm: 24, md: 26 },
                          ...((poStatus || poDetailPO.status) ===
                            "Partially Cancelled" && {
                            cursor: "pointer",
                          }),
                        }}
                      />
                    </Tooltip>
                  )}
                </Box>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{
                    mb: 0.5,
                    fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1rem" },
                    lineHeight: 1.35,
                  }}
                >
                  {poDetailPO?.client_name || "No Client"} •{" "}
                  {poDetailPO?.project_name || "No Project"}
                </Typography>
                {poDetailPO?.project_type && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem", md: "0.8rem" } }}
                  >
                    {poDetailPO.project_type}{" "}
                    {poDetailPO.geography ? `• ${poDetailPO.geography}` : ""}
                  </Typography>
                )}
              </Box>
              {/* //! ------- Edit Purchase Order Button */}
              <Box sx={{ display: "flex", gap: 1 }}>
                <Tooltip title="Edit Purchase Order">
                  <IconButton
                    disabled={can("update") === false}
                    onClick={() => poDetailPO && handleOpenEditPO(poDetailPO)}
                    sx={{
                      width: { xs: 34, sm: 38, md: 40 },
                      height: { xs: 34, sm: 38, md: 40 },
                      border: 1,
                      borderColor: "divider",
                      color: "warning.main",
                      "&:hover": {
                        bgcolor: "warning.light",
                        color: "white",
                      },
                    }}
                  >
                    <EditIcon sx={{ fontSize: { xs: 18, sm: 20, md: 22 } }} />
                  </IconButton>
                </Tooltip>
                <IconButton
                  onClick={handleClosePODetail}
                  sx={{
                    width: { xs: 34, sm: 38, md: 40 },
                    height: { xs: 34, sm: 38, md: 40 },
                    border: 1,
                    borderColor: "divider",
                    "&:hover": {
                      bgcolor: "action.hover",
                    },
                  }}
                >
                  <CloseIcon sx={{ fontSize: { xs: 18, sm: 20, md: 22 } }} />
                </IconButton>
              </Box>
            </Box>

            {/* //! -------------------------------- Financial Summary Cards  -----------------------------  */}
            {poDetailPO && (
              <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mt: { xs: 1.5, sm: 2 } }}>
                {/* PO Value card */}
                <Grid item xs={12} sm={6} md={3}>
                  <Card
                    elevation={0}
                    sx={{
                      bgcolor: "primary.50",
                      border: 1,
                      borderColor: "primary.200",
                      borderRadius: { xs: 1.5, sm: 2 },
                    }}
                  >
                    <CardContent
                      sx={{
                        p: { xs: 1.5, sm: 2 },
                        "&:last-child": { pb: { xs: 1.5, sm: 2 } },
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                          fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" },
                        }}
                      >
                        PO Value
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          color: "primary.main",
                          mt: 0.5,
                          fontSize: "clamp(1.05rem, 1.4vw, 1.35rem)",
                          lineHeight: 1.2,
                        }}
                      >
                        {formatCurrency(
                          poDetailPO.po_value_original,
                          poDetailPO.currency,
                        )}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" } }}
                      >
                        {poDetailPO.currency || "-"}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                {poDetailPO.po_value_original && poDetailPO.fx_rate_usd && (
                // * PO Value in USD 
                 <Grid item xs={12} sm={6} md={3}>
                    <Card
                      elevation={0}
                      sx={{
                        bgcolor: "success.50",
                        border: 1,
                        borderColor: "success.200",
                        borderRadius: { xs: 1.5, sm: 2 },
                      }}
                    >
                      <CardContent
                        sx={{
                          p: { xs: 1.5, sm: 2 },
                          "&:last-child": { pb: { xs: 1.5, sm: 2 } },
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                            fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" },
                          }}
                        >
                          PO Value (USD)
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            color: "success.main",
                            mt: 0.5,
                            fontSize: "clamp(1.05rem, 1.4vw, 1.35rem)",
                            lineHeight: 1.2,
                          }}
                        >
                          {formatCurrency(
                            calculateUSDValue(
                              poDetailPO.po_value_original,
                              poDetailPO.fx_rate_usd,
                            ),
                            "USD",
                          )}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" } }}
                        >
                          USD
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
                {poDetailPO.po_value_original && poDetailPO.fx_rate_inr && (
                  // * PO Value in INR
                  <Grid item xs={12} sm={6} md={3}>
                    <Card
                      elevation={0}
                      sx={{
                        bgcolor: "info.50",
                        border: 1,
                        borderColor: "info.200",
                        borderRadius: { xs: 1.5, sm: 2 },
                      }}
                    >
                      <CardContent
                        sx={{
                          p: { xs: 1.5, sm: 2 },
                          "&:last-child": { pb: { xs: 1.5, sm: 2 } },
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                            fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" },
                          }}
                        >
                          PO Value (INR)
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            color: "info.main",
                            mt: 0.5,
                            fontSize: "clamp(1.05rem, 1.4vw, 1.35rem)",
                            lineHeight: 1.2,
                          }}
                        >
                          {formatCurrency(
                            calculateINRValue(
                              poDetailPO.po_value_original,
                              poDetailPO.fx_rate_inr,
                            ),
                            "INR",
                          )}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" } }}
                        >
                          INR
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {/* // * Total invoices */}
                <Grid item xs={12} sm={6} md={3}>
                  <Card
                    elevation={0}
                    sx={{
                      bgcolor: "grey.50",
                      border: 1,
                      borderColor: "grey.300",
                      borderRadius: { xs: 1.5, sm: 2 },
                    }}
                  >
                    <CardContent
                      sx={{
                        p: { xs: 1.5, sm: 2 },
                        "&:last-child": { pb: { xs: 1.5, sm: 2 } },
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                          fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" },
                        }}
                      >
                        Total Invoices
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          color: "text.primary",
                          mt: 0.5,
                          fontSize: "clamp(1.05rem, 1.4vw, 1.35rem)",
                          lineHeight: 1.2,
                        }}
                      >
                        {poDetailInvoices.length}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" } }}
                      >
                        {
                          poDetailInvoices.filter(
                            (inv) => inv.status === "Received",
                          ).length
                        }{" "}
                        Received
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>


              </Grid>

            )}
          </Box>

          {/* Scrollable Content */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              overflowX: "hidden",
              p: { xs: 2, sm: 3, md: 3 },
            }}
          >
            {poDetailPO && (
              <>
                {/* PO Information Section */}
                <Card
                  elevation={1}
                  sx={{
                    mb: 3,
                    borderRadius: 2,
                    border: 1,
                    borderColor: "divider",
                  }}
                >
                  <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 3,
                        fontWeight: 600,
                        color: "text.primary",
                        pb: 1,
                        borderBottom: 2,
                        borderColor: "primary.main",
                        fontSize: { xs: "1rem", sm: "1.1rem", md: "1.2rem" },
                      }}
                    >
                      Purchase Order Information
                    </Typography>

                    <Grid container spacing={3}>
                      {/* Basic Information */}
                      <Grid item xs={12}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            mb: 2,
                            fontWeight: 600,
                            color: "text.secondary",
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                            fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" },
                          }}
                        >
                          Basic Information
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Box sx={{ mb: 2 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" },
                              textTransform: "uppercase",
                              letterSpacing: 0.5,
                            }}
                          >
                            PO Date
                          </Typography>
                          <Typography
                            variant="body1"
                            fontWeight="600"
                            sx={{
                              mt: 0.5,
                              fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1rem" },
                            }}
                          >
                            {formatDate(poDetailPO.po_date)}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Box sx={{ mb: 2 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" },
                              textTransform: "uppercase",
                              letterSpacing: 0.5,
                            }}
                          >
                            Payment Terms
                          </Typography>
                          <Typography
                            variant="body1"
                            fontWeight="600"
                            sx={{
                              mt: 0.5,
                              fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1rem" },
                            }}
                          >
                            {poDetailPO.payment_terms || "-"}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Box sx={{ mb: 2 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" },
                              textTransform: "uppercase",
                              letterSpacing: 0.5,
                            }}
                          >
                            Linked MSA
                          </Typography>
                          <Typography
                            variant="body1"
                            fontWeight="600"
                            sx={{
                              mt: 0.5,
                              fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1rem" },
                            }}
                          >
                            {poDetailPO.linked_msa || "-"}
                          </Typography>
                        </Box>
                      </Grid>

                      {/* Project & Team */}
                      <Grid item xs={12} sx={{ mt: 1 }}>
                        <Divider sx={{ my: 2 }} />
                        <Typography
                          variant="subtitle2"
                          sx={{
                            mb: 2,
                            fontWeight: 600,
                            color: "text.secondary",
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                            fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" },
                          }}
                        >
                          Project & Team
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Box sx={{ mb: 2 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" },
                              textTransform: "uppercase",
                              letterSpacing: 0.5,
                            }}
                          >
                            Account Manager
                          </Typography>
                          <Typography
                            variant="body1"
                            fontWeight="600"
                            sx={{
                              mt: 0.5,
                              fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1rem" },
                            }}
                          >
                            {poDetailPO.account_manager || "-"}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Box sx={{ mb: 2 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" },
                              textTransform: "uppercase",
                              letterSpacing: 0.5,
                            }}
                          >
                            Project Manager
                          </Typography>
                          <Typography
                            variant="body1"
                            fontWeight="600"
                            sx={{
                              mt: 0.5,
                              fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1rem" },
                            }}
                          >
                            {poDetailPO.project_manager || "-"}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Box sx={{ mb: 2 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" },
                              textTransform: "uppercase",
                              letterSpacing: 0.5,
                            }}
                          >
                            Revenue Type
                          </Typography>
                          <Typography
                            variant="body1"
                            fontWeight="600"
                            sx={{
                              mt: 0.5,
                              fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1rem" },
                            }}
                          >
                            {poDetailPO.revenue_type || "-"}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Box sx={{ mb: 2 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" },
                              textTransform: "uppercase",
                              letterSpacing: 0.5,
                            }}
                          >
                            Revenue Class
                          </Typography>
                          <Typography
                            variant="body1"
                            fontWeight="600"
                            sx={{
                              mt: 0.5,
                              fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1rem" },
                            }}
                          >
                            {poDetailPO.revenue_class || "-"}
                          </Typography>
                        </Box>
                      </Grid>

                      {/* Financial Details */}
                      <Grid item xs={12} sx={{ mt: 1 }}>
                        <Divider sx={{ my: 2 }} />
                        <Typography
                          variant="subtitle2"
                          sx={{
                            mb: 2,
                            fontWeight: 600,
                            color: "text.secondary",
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                            fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" },
                          }}
                        >
                          Financial Details
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Box sx={{ mb: 2 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" },
                              textTransform: "uppercase",
                              letterSpacing: 0.5,
                            }}
                            gutterBottom
                          >
                            FX Rate (Base to USD)
                          </Typography>
                          <TextField
                            type="number"
                            value={poDetailPO.fx_rate_usd || ""}
                            onChange={(e) => {
                              const newFxRate = e.target.value
                                ? parseFloat(e.target.value)
                                : null;
                              if (poDetailPO) {
                                handleUpdatePOField("fx_rate_usd", newFxRate);
                              }
                            }}
                            fullWidth
                            size="small"
                            inputProps={{ step: "0.000001", min: "0" }}
                            disabled={updatingPO}
                            helperText={`Rate to convert ${poDetailPO.currency || "currency"} to USD`}
                            sx={{
                              mt: 0.5,
                              "& .MuiInputLabel-root": {
                                fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" },
                              },
                              "& .MuiInputBase-input": {
                                fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                              },
                              "& .MuiFormHelperText-root": {
                                fontSize: { xs: "0.65rem", sm: "0.7rem" },
                              },
                            }}
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Box sx={{ mb: 2 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" },
                              textTransform: "uppercase",
                              letterSpacing: 0.5,
                            }}
                            gutterBottom
                          >
                            FX Rate (Base to INR)
                          </Typography>
                          <TextField
                            type="number"
                            value={poDetailPO.fx_rate_inr || ""}
                            onChange={(e) => {
                              const newFxRate = e.target.value
                                ? parseFloat(e.target.value)
                                : null;
                              if (poDetailPO) {
                                handleUpdatePOField("fx_rate_inr", newFxRate);
                              }
                            }}
                            fullWidth
                            size="small"
                            inputProps={{ step: "0.000001", min: "0" }}
                            disabled={updatingPO}
                            helperText={`Rate to convert ${poDetailPO.currency || "currency"} to INR`}
                            sx={{
                              mt: 0.5,
                              "& .MuiInputLabel-root": {
                                fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" },
                              },
                              "& .MuiInputBase-input": {
                                fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                              },
                              "& .MuiFormHelperText-root": {
                                fontSize: { xs: "0.65rem", sm: "0.7rem" },
                              },
                            }}
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Box sx={{ mb: 2 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" },
                              textTransform: "uppercase",
                              letterSpacing: 0.5,
                            }}
                          >
                            PO Status
                          </Typography>
                          <FormControl
                            fullWidth
                            size="small"
                            sx={{
                              mt: 1,
                              "& .MuiInputLabel-root": {
                                fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" },
                              },
                              "& .MuiSelect-select": {
                                fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                              },
                            }}
                          >
                            <Select
                              value={poStatus}
                              onChange={(e) =>
                                handleUpdatePOStatus(e.target.value)
                              }
                              disabled={updatingPO}
                            >
                              <MenuItem value="Active">Active</MenuItem>
                              <MenuItem value="Expired">Expired</MenuItem>
                              <MenuItem value="Cancelled">Cancelled</MenuItem>
                              <MenuItem value="Partially Cancelled">
                                Partially Cancelled
                              </MenuItem>
                            </Select>
                          </FormControl>
                        </Box>
                      </Grid>

            
                      {/* Partially Cancelled - Cancellation Events Section */}
                      {(poStatus === "Partially Cancelled" ||
                        poDetailPO.status === "Partially Cancelled") && (
                        <Grid
                          item
                          xs={12}
                          sx={{ mt: 1 }}
                          id="po-partially-cancelled-section"
                        >
                          <Divider sx={{ my: 2 }} />
                          <Card
                            elevation={0}
                            sx={{
                              bgcolor: "warning.50",
                              border: 2,
                              borderColor: "warning.main",
                              borderRadius: 2,
                              p: 3,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mb: 3,
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  fontWeight: 600,
                                  color: "warning.dark",
                                  textTransform: "uppercase",
                                  letterSpacing: 0.5,
                                  fontSize: "0.75rem",
                                }}
                              >
                                {/* po preview */}
                                Cancellation Events
                              </Typography>
                            </Box>

                            {/* Summary Cards */}
                            <Grid container spacing={2} sx={{ mb: 3 }}>
                              {/* Original PO Value */}
                              <Grid item xs={12} sm={4}>
                                <Card
                                  elevation={0}
                                  sx={{
                                    bgcolor: "background.paper",
                                    border: 1,
                                    borderColor: "divider",
                                    borderRadius: 2,
                                  }}
                                >
                                  <CardContent
                                    sx={{ p: 2, "&:last-child": { pb: 2 } }}
                                  >
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{
                                        fontSize: "0.7rem",
                                        textTransform: "uppercase",
                                        letterSpacing: 0.5,
                                      }}
                                    >
                                      Original PO Value
                                    </Typography>
                                    <Typography
                                      variant="h6"
                                      sx={{
                                        fontWeight: 700,
                                        color: "text.primary",
                                        mt: 0.5,
                                      }}
                                    >
                                      {formatCurrency(
                                          editPOOriginalValue,
                                          poDetailPO.currency || null,
                                      )}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ fontSize: "0.7rem" }}
                                    >
                                      {poDetailPO.currency || "-"}
                                    </Typography>
                                  </CardContent>
                                </Card>
                              </Grid>

                              {/* PO Value (Current) */}
                              <Grid item xs={12} sm={4}>
                                <Card
                                  elevation={0}
                                  sx={{
                                    bgcolor: "background.paper",
                                    border: 1,
                                    borderColor: "divider",
                                    borderRadius: 2,
                                  }}
                                >
                                  <CardContent
                                    sx={{ p: 2, "&:last-child": { pb: 2 } }}
                                  >
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{
                                        fontSize: "0.7rem",
                                        textTransform: "uppercase",
                                        letterSpacing: 0.5,
                                      }}
                                    >
                                      PO Value
                                    </Typography>
                                    <Typography
                                      variant="h6"
                                      sx={{
                                        fontWeight: 700,
                                        color: "primary.main",
                                        mt: 0.5,
                                      }}
                                    >
                                      {formatCurrency(
                                        poDetailPO.po_value_original || 0,
                                        poDetailPO.currency,
                                      )}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ fontSize: "0.7rem" }}
                                    >
                                      {poDetailPO.currency || "-"}
                                    </Typography>
                                  </CardContent>
                                </Card>
                              </Grid>

                              {/* Total Reduction */}
                              <Grid item xs={12} sm={4}>
                                <Card
                                  elevation={0}
                                  sx={{
                                    bgcolor: "background.paper",
                                    border: 1,
                                    borderColor: "divider",
                                    borderRadius: 2,
                                  }}
                                >
                                  <CardContent
                                    sx={{ p: 2, "&:last-child": { pb: 2 } }}
                                  >
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{
                                        fontSize: "0.7rem",
                                        textTransform: "uppercase",
                                        letterSpacing: 0.5,
                                      }}
                                    >
                                      Total Reduction
                                    </Typography>
                                      {(() => {
                                      // Use same "value before first cancellation" as Edit page (editPOOriginalValue), not po_value_initial which can be wrong
                                      const originalValue = editPOOriginalValue;
                                      if (
                                        originalValue &&
                                        poDetailPO.po_value_original
                                      ) {
                                        return (
                                          <>
                                            <Typography
                                              variant="h6"
                                              sx={{
                                                fontWeight: 700,
                                                color: "error.main",
                                                mt: 0.5,
                                              }}
                                            >
                                              {formatCurrency(
                                                poDetailPO.po_value_original -
                                                  originalValue,
                                                poDetailPO.currency,
                                              )}
                                            </Typography>
                                            <Typography
                                              variant="caption"
                                              color="text.secondary"
                                              sx={{ fontSize: "0.7rem" }}
                                            >
                                              {(
                                                ((poDetailPO.po_value_original -
                                                  originalValue) /
                                                  originalValue) *
                                                100
                                              ).toFixed(2)}
                                              %
                                            </Typography>
                                          </>
                                        );
                                      }
                                      return (
                                        <Typography
                                          variant="body2"
                                          color="text.disabled"
                                          sx={{ mt: 0.5 }}
                                        >
                                          No cancellations yet
                                        </Typography>
                                      );
                                    })()}
                                  </CardContent>
                                </Card>
                              </Grid>
                            </Grid>

                            {/* Add New Cancellation Event Form */}
                            {/* <Card
                              elevation={1}
                              sx={{
                                mb: 3,
                                borderRadius: 2,
                                border: 1,
                                borderColor: "warning.300",
                              }}
                            >
                              <CardContent sx={{ p: 2.5 }}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 2,
                                    mb: 2,
                                  }}
                                >
                                  <Button
                                    variant="contained"
                                    color="warning"
                                    startIcon={<AddIcon />}
                                    onClick={handleAddCancellationEvent}
                                    disabled={
                                      updatingPO ||
                                      newCancellationValue === null ||
                                      !newCancellationDate
                                    }
                                    sx={{
                                      textTransform: "none",
                                      fontWeight: 600,
                                    }}
                                  >
                                    {updatingPO
                                      ? "Adding..."
                                      : "Add Cancellation Event"}
                                  </Button>
                                </Box>

                                <Grid container spacing={2}>
                                  <Grid item xs={12} sm={6}>
                                    <TextField
                                      label="Cancellation Date"
                                      type="date"
                                      value={newCancellationDate}
                                      onChange={(e) =>
                                        setNewCancellationDate(e.target.value)
                                      }
                                      fullWidth
                                      size="small"
                                      InputLabelProps={{ shrink: true }}
                                      disabled={updatingPO}
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={6}>
                                    <TextField
                                      label="PO Value After Cancellation"
                                      type="number"
                                      value={
                                        newCancellationValue !== null
                                          ? newCancellationValue
                                          : ""
                                      }
                                      onChange={(e) =>
                                        setNewCancellationValue(
                                          e.target.value
                                            ? parseFloat(e.target.value)
                                            : null,
                                        )
                                      }
                                      fullWidth
                                      size="small"
                                      inputProps={{ step: "0.01", min: "0" }}
                                      disabled={updatingPO}
                                      placeholder="Enter new value"
                                      helperText={
                                        newCancellationValue !== null &&
                                        poDetailPO.po_value_original
                                          ? `Change: ${formatCurrency(
                                              newCancellationValue -
                                                poDetailPO.po_value_original,
                                              poDetailPO.currency,
                                            )}`
                                          : ""
                                      }
                                    />
                                  </Grid>
                                  <Grid item xs={12}>
                                    <TextField
                                      label="Cancellation Description"
                                      value={newCancellationDescription}
                                      onChange={(e) =>
                                        setNewCancellationDescription(
                                          e.target.value,
                                        )
                                      }
                                      fullWidth
                                      size="small"
                                      multiline
                                      rows={2}
                                      disabled={updatingPO}
                                      placeholder="Enter description for this cancellation event..."
                                    />
                                  </Grid>
                                </Grid>
                              </CardContent>
                            </Card> */}

                            {/* Cancellation Events List */}

                            {/* updated cancellation event list */}
  
                            {/* end of cancellation event list */}
                            {cancellationEvents.length > 0 && (
                              <Box>
                                <Typography
                                  variant="subtitle2"
                                  sx={{
                                    mb: 2,
                                    fontWeight: 600,
                                    color: "text.secondary",
                                    textTransform: "uppercase",
                                    letterSpacing: 0.5,
                                    fontSize: "0.75rem",
                                  }}
                                >

                                  {/* //! ------- Preview Cancellation History Table ------- */}
                                  Cancellation History
                                </Typography>
                                <TableContainer
                                  component={Paper}
                                  elevation={1}
                                  sx={{
                                    borderRadius: 2,
                                    border: 1,
                                    borderColor: "divider",
                                    overflowX: "auto",
                                    WebkitOverflowScrolling: "touch",
                                    maxWidth: "100%",
                                  }}
                                >
                                  {/* print the poDetailPO object */}
                                  {/* <small><pre>{JSON.stringify(poDetailPO, null, 2)}</pre></small> */}
                                  <Table size="small" sx={{ minWidth: 720 }}>
                                    <TableHead>
                                      <TableRow sx={{ bgcolor: "grey.50" }}>
                                        <TableCell
                                          sx={{
                                            fontWeight: 700,
                                            fontSize: "0.75rem",
                                          }}
                                        >
                                          #
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            fontWeight: 700,
                                            fontSize: "0.75rem",
                                          }}
                                        >
                                          Date
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            fontWeight: 700,
                                            fontSize: "0.75rem",
                                          }}
                                        >
                                          Description
                                        </TableCell>
                                        {/* <TableCell
                                          sx={{
                                            fontWeight: 700,
                                            fontSize: "0.75rem",
                                          }}
                                          align="right"
                                        >
                                          Original PO Value
                                        </TableCell> */}
                                        <TableCell
                                          sx={{
                                            fontWeight: 700,
                                            fontSize: "0.75rem",
                                          }}
                                          align="right"
                                        >
                                          PO Value
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            fontWeight: 700,
                                            fontSize: "0.75rem",
                                          }}
                                          align="right"
                                        >
                                          Change Amount
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            fontWeight: 700,
                                            fontSize: "0.75rem",
                                          }}
                                          align="right"
                                        >
                                          Change %
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            fontWeight: 700,
                                            fontSize: "0.75rem",
                                          }}
                                          align="right"
                                        >
                                          Reduction from Original
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            fontWeight: 700,
                                            fontSize: "0.75rem",
                                            display: "none",
                                          }}
                                          align="right"
                                        >
                                          Actions
                                        </TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {cancellationEvents.map(
                                        (event, index) => {
                                    // Get original PO value (never changes - for "Original PO Value" column and reduction from original)
                                    const originalValue = editPOOriginalValue;

                                        // Value before THIS event (for change amount / change %).
                                        // For the first event: use stored change_amount to derive value before, else fall back to originalValue.
                                        const previousValue =
                                          index === 0
                                            ? (event.change_amount !== undefined &&
                                              event.change_amount !== null
                                              ? event.new_value - event.change_amount
                                              : originalValue)
                                            : cancellationEvents[index - 1]
                                                .new_value;

                                          // Use stored values from event if available, otherwise calculate
                                          const changeAmount =
                                            event.change_amount !== undefined &&
                                            event.change_amount !== null
                                              ? event.change_amount
                                              : event.new_value - previousValue;

                                          const changePercentage =
                                            event.change_percentage !==
                                              undefined &&
                                            event.change_percentage !== null
                                              ? event.change_percentage
                                              : previousValue !== 0
                                                ? ((event.new_value -
                                                    previousValue) /
                                                    previousValue) *
                                                  100
                                                : 0;

                                          // Calculate overall reduction from original value (for tracking)
                                          const reductionFromOriginal =
                                          event.new_value - originalValue;
                                        const reductionPercentageFromOriginal =
                                          originalValue !== 0
                                            ? (reductionFromOriginal / originalValue) * 100
                                            : 0;

                                          return (
                                            <TableRow key={event.id} hover>
                                              {/* id */}
                                              <TableCell>{index + 1}</TableCell>
                                              <TableCell>
                                                {formatDate(event.date)}
                                              </TableCell>

                                              {/* description */}
                                              <TableCell>
                                                <Typography
                                                  variant="body2"
                                                  fontSize="0.85rem"
                                                >
                                                  {event.description || "-"}
                                                </Typography>
                                              </TableCell>

                                              {/* original PO value */}
                                              {/* <TableCell align="right">
                                                <Typography
                                                  variant="body2"
                                                  fontWeight="medium"
                                                  color="text.secondary"
                                                >
                                                  {formatCurrency(
                                                    originalValue,
                                                    poDetailPO.currency || null,
                                                  )}
                                                </Typography>
                                              </TableCell> */}
                                              <TableCell align="right">
                                                <Typography
                                                  variant="body2"
                                                  fontWeight="medium"
                                                >
                                                  {formatCurrency(
                                                    event.new_value,
                                                    poDetailPO.currency,
                                                  )}
                                                </Typography>
                                              </TableCell>
                                              <TableCell align="right">
                                                <Typography
                                                  variant="body2"
                                                  fontWeight="600"
                                                  color={
                                                    changeAmount < 0
                                                      ? "error.main"
                                                      : "success.main"
                                                  }
                                                >
                                                  {formatCurrency(
                                                    changeAmount,
                                                    poDetailPO.currency,
                                                  )}
                                                </Typography>
                                              </TableCell>
                                              <TableCell align="right">
                                                <Typography
                                                  variant="body2"
                                                  color={
                                                    changePercentage < 0
                                                      ? "error.main"
                                                      : "success.main"
                                                  }
                                                >
                                                  {changePercentage.toFixed(2)}%
                                                </Typography>
                                              </TableCell>
                                              <TableCell align="right">
                                                <Typography
                                                  variant="body2"
                                                  fontWeight="600"
                                                  color="error.main"
                                                >
                                                  {formatCurrency(
                                                    reductionFromOriginal,
                                                    poDetailPO.currency,
                                                  )}
                                                </Typography>
                                                <Typography
                                                  variant="caption"
                                                  color="error.main"
                                                  sx={{
                                                    display: "block",
                                                    fontSize: "0.7rem",
                                                  }}
                                                >
                                                  (
                                                  {reductionPercentageFromOriginal.toFixed(
                                                    2,
                                                  )}
                                                  %)
                                                </Typography>
                                              </TableCell>
                                              <TableCell align="right" sx={{ display: "none" }}>
                                                <Box
                                                  sx={{
                                                    display: "flex",
                                                    gap: 0.5,
                                                    justifyContent: "flex-end",
                                                  }}
                                                >
                                                  <Tooltip title="Edit Cancellation Event">
                                                    <IconButton
                                                      size="small"
                                                      onClick={() => {
                                                        setEditingCancellationEvent(
                                                          event,
                                                        );
                                                        setEditCancellationValue(
                                                          event.new_value,
                                                        );
                                                        setEditCancellationDate(
                                                          event.date,
                                                        );
                                                        setEditCancellationDescription(
                                                          event.description ||
                                                            "",
                                                        );
                                                      }}
                                                      disabled={
                                                        updatingPO ||
                                                        can("update") === false
                                                      }
                                                      sx={{
                                                        color: "warning.main",
                                                        "&:hover": {
                                                          bgcolor:
                                                            "warning.light",
                                                          color: "white",
                                                        },
                                                      }}
                                                    >
                                                      <EditIcon fontSize="small" />
                                                    </IconButton>
                                                  </Tooltip>
                                                  <Tooltip title="Delete Cancellation Event">
                                                    <IconButton
                                                      size="small"
                                                      onClick={() =>
                                                        setConfirmDeleteCancellationEventId(
                                                          event.id,
                                                        )
                                                      }
                                                      disabled={
                                                        updatingPO ||
                                                        can("delete") === false
                                                      }
                                                      sx={{
                                                        color: "error.main",
                                                        "&:hover": {
                                                          bgcolor:
                                                            "error.light",
                                                          color: "white",
                                                        },
                                                      }}
                                                    >
                                                      <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                  </Tooltip>
                                                </Box>
                                              </TableCell>
                                            </TableRow>
                                          );
                                        },
                                      )}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              </Box>
                            )}
                          </Card>
                        </Grid>
                      )}

                      {/* Edit Cancellation Event Dialog */}
                      <Dialog
                        open={editingCancellationEvent !== null}
                        onClose={() => {
                          setEditingCancellationEvent(null);
                          setEditCancellationValue(null);
                          setEditCancellationDate("");
                          setEditCancellationDescription("");
                        }}
                        maxWidth="sm"
                        fullWidth
                        PaperProps={{
                          sx: {
                            borderRadius: 2,
                          },
                        }}
                      >
                        <form
                          onSubmit={(e) => {
                            e.preventDefault();
                            handleUpdateCancellationEvent();
                          }}
                        >
                          <Box
                            sx={{
                              p: 3,
                              borderBottom: 1,
                              borderColor: "divider",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: 1.5,
                                  bgcolor: "warning.main",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: "white",
                                }}
                              >
                                <EditIcon />
                              </Box>
                              <Box>
                                <Typography
                                  variant="h6"
                                  sx={{
                                    fontWeight: 700,
                                    color: "text.primary",
                                  }}
                                >
                                  Edit Cancellation Event
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ fontSize: "0.75rem" }}
                                >
                                  Update cancellation event details
                                </Typography>
                              </Box>
                            </Box>
                            <IconButton
                              onClick={() => {
                                setEditingCancellationEvent(null);
                                setEditCancellationValue(null);
                                setEditCancellationDate("");
                                setEditCancellationDescription("");
                              }}
                              size="small"
                              sx={{
                                color: "text.secondary",
                                "&:hover": { bgcolor: "action.hover" },
                              }}
                            >
                              <CloseIcon />
                            </IconButton>
                          </Box>

                          <DialogContent sx={{ pt: 3 }}>
                            <Grid container spacing={2.5}>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  label="Cancellation Date"
                                  type="date"
                                  value={editCancellationDate}
                                  onChange={(e) =>
                                    setEditCancellationDate(e.target.value)
                                  }
                                  fullWidth
                                  size="small"
                                  InputLabelProps={{ shrink: true }}
                                  required
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  label="PO Value After Cancellation"
                                  type="number"
                                  value={
                                    editCancellationValue !== null
                                      ? editCancellationValue
                                      : ""
                                  }
                                  onChange={(e) =>
                                    setEditCancellationValue(
                                      e.target.value
                                        ? parseFloat(e.target.value)
                                        : null,
                                    )
                                  }
                                  fullWidth
                                  size="small"
                                  inputProps={{ step: "0.01", min: "0" }}
                                  required
                                  helperText={
                                    editCancellationValue !== null &&
                                    editingCancellationEvent &&
                                    poDetailPO
                                      ? (() => {
                                          const eventIndex =
                                            cancellationEvents.findIndex(
                                              (e) =>
                                                e.id ===
                                                editingCancellationEvent.id,
                                            );
                                          // Always use po_value_initial for original value (never changes)
                                          const originalValue =
                                            poDetailPO.po_value_initial ??
                                            originalPOValue ??
                                            poDetailPO.po_value_original ??
                                            0;
                                          const previousValue =
                                            eventIndex === 0
                                              ? originalValue
                                              : cancellationEvents[
                                                  eventIndex - 1
                                                ].new_value;
                                          const change =
                                            editCancellationValue -
                                            previousValue;
                                          return `Change: ${formatCurrency(change, poDetailPO.currency || "USD")}`;
                                        })()
                                      : ""
                                  }
                                />
                              </Grid>
                              <Grid item xs={12}>
                                <TextField
                                  label="Cancellation Description"
                                  value={editCancellationDescription}
                                  onChange={(e) =>
                                    setEditCancellationDescription(
                                      e.target.value,
                                    )
                                  }
                                  fullWidth
                                  size="small"
                                  multiline
                                  rows={3}
                                  placeholder="Enter description for this cancellation event..."
                                />
                              </Grid>
                            </Grid>
                          </DialogContent>
                          <DialogActions
                            sx={{
                              p: 3,
                              borderTop: 1,
                              borderColor: "divider",
                              gap: 1.5,
                            }}
                          >
                            <Button
                              onClick={() => {
                                setEditingCancellationEvent(null);
                                setEditCancellationValue(null);
                                setEditCancellationDate("");
                                setEditCancellationDescription("");
                              }}
                              disabled={updatingPO}
                              sx={{
                                textTransform: "none",
                                minWidth: 120,
                                px: 3,
                                color: "text.secondary",
                              }}
                            >
                              Cancel
                            </Button>
                            <Button
                              type="submit"
                              variant="contained"
                              disabled={
                                updatingPO ||
                                editCancellationValue === null ||
                                !editCancellationDate ||
                                can("update") === false
                              }
                              startIcon={
                                updatingPO ? (
                                  <CircularProgress size={16} color="inherit" />
                                ) : (
                                  <EditIcon />
                                )
                              }
                              color="warning"
                              sx={{
                                textTransform: "none",
                                minWidth: 160,
                                px: 3,
                                fontWeight: 600,
                                boxShadow: 2,
                              }}
                            >
                              {updatingPO ? "Updating..." : "Update Event"}
                            </Button>
                          </DialogActions>
                        </form>
                      </Dialog>

                      {/* Remarks */}
                      <Grid item xs={12} sx={{ mt: 1 }}>
                        <Divider sx={{ my: 2 }} />
                        <Typography
                          variant="subtitle2"
                          sx={{
                            mb: 2,
                            fontWeight: 600,
                            color: "text.secondary",
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                            fontSize: "0.75rem",
                          }}
                        >
                          Remarks
                        </Typography>
                        <TextField
                          value={poRemarks}
                          onChange={(e) => setPORemarks(e.target.value)}
                          fullWidth
                          multiline
                          rows={3}
                          size="small"
                          placeholder="Add remarks or notes..."
                        />
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={handleUpdatePORemarks}
                          disabled={updatingPO}
                          sx={{ mt: 1.5 }}
                        >
                          {updatingPO ? (
                            <CircularProgress size={16} />
                          ) : (
                            "Save Remarks"
                          )}
                        </Button>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* Invoices Section */}
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: "text.primary",
                        fontSize: { xs: "1rem", sm: "1.1rem", md: "1.2rem" },
                      }}
                    >
                      Invoices ({poDetailInvoices.length})
                    </Typography>
                    <Button
                      variant="outlined"
                      disabled={can("write") === false}
                      startIcon={<CalendarIcon />}
                      onClick={() => {
                        handleClosePODetail();
                        if (poDetailPO) {
                          handleOpenInvoicePlanner(poDetailPO);
                        }
                      }}
                      size="small"
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: { xs: "0.75rem", sm: "0.85rem", md: "0.9rem" },
                        px: { xs: 1.5, sm: 2 },
                        height: { xs: 32, sm: 34, md: 36 },
                        "& .MuiButton-startIcon svg": {
                          fontSize: { xs: 18, sm: 20, md: 22 },
                        },
                      }}
                    >
                      Plan New Invoice
                    </Button>
                  </Box>

                  {loadingPODetailInvoices ? (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", p: 6 }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : poDetailInvoices.length === 0 ? (
                    <Card
                      elevation={1}
                      sx={{
                        borderRadius: 2,
                        border: 1,
                        borderColor: "divider",
                      }}
                    >
                      <Box sx={{ p: 6, textAlign: "center" }}>
                        <Typography
                          variant="h6"
                          color="text.secondary"
                          gutterBottom
                        >
                          No Invoices Found
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 3 }}
                        >
                          Start by planning invoices for this purchase order.
                        </Typography>
                        <Button
                          variant="contained"
                          disabled={can("write") === false}
                          startIcon={<CalendarIcon />}
                          onClick={() => {
                            handleClosePODetail();
                            if (poDetailPO) {
                              handleOpenInvoicePlanner(poDetailPO);
                            }
                          }}
                          sx={{ textTransform: "none", fontWeight: 600 }}
                        >
                          Plan First Invoice
                        </Button>
                      </Box>
                    </Card>
                  ) : (
                    <TableContainer
                      component={Paper}
                      elevation={1}
                      sx={{
                        borderRadius: 2,
                        border: 1,
                        borderColor: "divider",
                        overflowX: "auto",
                      }}
                    >
                      <Table size="small" sx={{ minWidth: 1200 }}>
                        <TableHead>
                          <TableRow sx={{ bgcolor: "grey.50" }}>
                            <TableCell
                              sx={{
                                fontWeight: 700,
                          fontSize: { xs: "0.7rem", sm: "0.75rem", md: "0.8rem" },
                                whiteSpace: "nowrap",
                              }}
                            >
                              Invoice Code
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 700,
                          fontSize: { xs: "0.7rem", sm: "0.75rem", md: "0.8rem" },
                                whiteSpace: "nowrap",
                              }}
                            >
                              Description
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 700,
                          fontSize: { xs: "0.7rem", sm: "0.75rem", md: "0.8rem" },
                                whiteSpace: "nowrap",
                              }}
                            >
                              Status
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 700,
                                fontSize: "0.75rem",
                                whiteSpace: "nowrap",
                              }}
                              align="right"
                            >
                              Planned Value
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 700,
                                fontSize: "0.75rem",
                                whiteSpace: "nowrap",
                              }}
                              align="right"
                            >
                              Planned (USD)
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 700,
                                fontSize: "0.75rem",
                                whiteSpace: "nowrap",
                              }}
                              align="right"
                            >
                              Planned (INR)
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 700,
                                fontSize: "0.75rem",
                                whiteSpace: "nowrap",
                              }}
                            >
                              Finance Planned
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 700,
                                fontSize: "0.75rem",
                                whiteSpace: "nowrap",
                              }}
                            >
                              Operational Planned
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 700,
                                fontSize: "0.75rem",
                                whiteSpace: "nowrap",
                              }}
                            >
                              Service Month
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 700,
                                fontSize: "0.75rem",
                                whiteSpace: "nowrap",
                              }}
                            >
                              Invoice Date
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 700,
                                fontSize: "0.75rem",
                                whiteSpace: "nowrap",
                              }}
                              align="right"
                            >
                              Invoiced Value
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 700,
                                fontSize: "0.75rem",
                                whiteSpace: "nowrap",
                              }}
                              align="right"
                            >
                              Invoiced (USD)
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 700,
                                fontSize: "0.75rem",
                                whiteSpace: "nowrap",
                              }}
                              align="right"
                            >
                              Invoiced (INR)
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 700,
                                fontSize: "0.75rem",
                                whiteSpace: "nowrap",
                              }}
                            >
                              Payment Date
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 700,
                                fontSize: "0.75rem",
                                whiteSpace: "nowrap",
                              }}
                              align="right"
                            >
                              Payment Received
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 700,
                                fontSize: "0.75rem",
                                whiteSpace: "nowrap",
                              }}
                              align="right"
                            >
                              Payment (USD)
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 700,
                                fontSize: "0.75rem",
                                whiteSpace: "nowrap",
                              }}
                              align="right"
                            >
                              Payment (INR)
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {poDetailInvoices.map((invoice) => {
                            const po = poDetailPO;
                            const fxRateUSD =
                              invoice.fx_rate_usd || po?.fx_rate_usd || null;
                            const fxRateINR =
                              invoice.fx_rate_inr || po?.fx_rate_inr || null;
                            const plannedUSD = calculateUSDValue(
                              invoice.est_value_original,
                              fxRateUSD,
                            );
                            const plannedINR = calculateINRValue(
                              invoice.est_value_original,
                              fxRateINR,
                            );
                            const invoicedUSD = calculateUSDValue(
                              invoice.invoiced_value_original,
                              fxRateUSD,
                            );
                            const invoicedINR = calculateINRValue(
                              invoice.invoiced_value_original,
                              fxRateINR,
                            );
                            const receivedUSD = calculateUSDValue(
                              invoice.payment_received_value,
                              fxRateUSD,
                            );
                            const receivedINR = calculateINRValue(
                              invoice.payment_received_value,
                              fxRateINR,
                            );
                            return (
                              <TableRow
                                key={invoice.id}
                                hover
                                sx={{ "&:hover": { bgcolor: "action.hover" } }}
                              >
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 600,
                                      color: "primary.main",
                                    }}
                                  >
                                    {invoice.invoice_code}
                                  </Typography>
                                </TableCell>
                                <TableCell sx={{ maxWidth: 200 }}>
                                  <Typography
                                    variant="body2"
                                    noWrap
                                    title={invoice.description || "-"}
                                  >
                                    {invoice.description || "-"}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={invoice.status || "-"}
                                    size="small"
                                    color={
                                      invoice.status === "Received"
                                        ? "success"
                                        : invoice.status === "Cancelled"
                                          ? "error"
                                          : invoice.status === "Delayed"
                                            ? "warning"
                                            : "default"
                                    }
                                    sx={{
                                      fontWeight: 600,
                                      fontSize: "0.7rem",
                                      height: 20,
                                    }}
                                  />
                                </TableCell>
                                <TableCell align="right">
                                  <Typography
                                    variant="body2"
                                    fontWeight="medium"
                                  >
                                    {formatCurrency(
                                      invoice.est_value_original,
                                      invoice.currency,
                                    )}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    fontSize="0.8rem"
                                  >
                                    {formatCurrency(plannedUSD, "USD")}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    fontSize="0.8rem"
                                  >
                                    {formatCurrency(plannedINR, "INR")}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" fontSize="0.8rem">
                                    {formatDate(invoice.finance_planned_date)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" fontSize="0.8rem">
                                    {formatDate(
                                      invoice.operational_planned_date,
                                    )}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" fontSize="0.8rem">
                                    {formatDate(invoice.service_month_date)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    fontSize="0.8rem"
                                    fontWeight="medium"
                                  >
                                    {formatDate(invoice.invoice_date)}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  {invoice.invoiced_value_original ? (
                                    <Typography
                                      variant="body2"
                                      fontWeight="600"
                                      color="success.main"
                                    >
                                      {formatCurrency(
                                        invoice.invoiced_value_original,
                                        invoice.currency,
                                      )}
                                    </Typography>
                                  ) : (
                                    <Typography
                                      variant="body2"
                                      color="text.disabled"
                                    >
                                      -
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell align="right">
                                  {invoicedUSD ? (
                                    <Typography
                                      variant="body2"
                                      color="success.main"
                                      fontSize="0.8rem"
                                    >
                                      {formatCurrency(invoicedUSD, "USD")}
                                    </Typography>
                                  ) : (
                                    <Typography
                                      variant="body2"
                                      color="text.disabled"
                                      fontSize="0.8rem"
                                    >
                                      -
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell align="right">
                                  {invoicedINR ? (
                                    <Typography
                                      variant="body2"
                                      color="success.main"
                                      fontSize="0.8rem"
                                    >
                                      {formatCurrency(invoicedINR, "INR")}
                                    </Typography>
                                  ) : (
                                    <Typography
                                      variant="body2"
                                      color="text.disabled"
                                      fontSize="0.8rem"
                                    >
                                      -
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" fontSize="0.8rem">
                                    {formatDate(invoice.payment_received_date)}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  {invoice.payment_received_value ? (
                                    <Typography
                                      variant="body2"
                                      fontWeight="600"
                                      color="success.main"
                                    >
                                      {formatCurrency(
                                        invoice.payment_received_value,
                                        invoice.currency,
                                      )}
                                    </Typography>
                                  ) : (
                                    <Typography
                                      variant="body2"
                                      color="text.disabled"
                                    >
                                      -
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell align="right">
                                  {receivedUSD ? (
                                    <Typography
                                      variant="body2"
                                      color="success.main"
                                      fontSize="0.8rem"
                                    >
                                      {formatCurrency(receivedUSD, "USD")}
                                    </Typography>
                                  ) : (
                                    <Typography
                                      variant="body2"
                                      color="text.disabled"
                                      fontSize="0.8rem"
                                    >
                                      -
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell align="right">
                                  {receivedINR ? (
                                    <Typography
                                      variant="body2"
                                      color="success.main"
                                      fontSize="0.8rem"
                                    >
                                      {formatCurrency(receivedINR, "INR")}
                                    </Typography>
                                  ) : (
                                    <Typography
                                      variant="body2"
                                      color="text.disabled"
                                      fontSize="0.8rem"
                                    >
                                      -
                                    </Typography>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Drawer>

      {/* Confirm delete cancellation event */}
      <Dialog
        open={confirmDeleteCancellationEventId !== null}
        onClose={() => setConfirmDeleteCancellationEventId(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Delete cancellation event?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            This action cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setConfirmDeleteCancellationEventId(null)}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              if (confirmDeleteCancellationEventId) {
                handleDeleteCancellationEvent(confirmDeleteCancellationEventId);
              }
              setConfirmDeleteCancellationEventId(null);
            }}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm delete cancellation event (Edit PO form) */}
      <Dialog
        open={confirmDeleteEditPOCancellationEventId !== null}
        onClose={() => setConfirmDeleteEditPOCancellationEventId(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Delete cancellation event?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            This action cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setConfirmDeleteEditPOCancellationEventId(null)}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              if (confirmDeleteEditPOCancellationEventId) {
                handleEditPODeleteCancellationEvent(confirmDeleteEditPOCancellationEventId);
              }
              setConfirmDeleteEditPOCancellationEventId(null);
            }}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>


      {/* Invoice Planning Drawer */}
      <Drawer
        anchor="right"
        open={openInvoicePlanner}
        onClose={handleCloseInvoicePlanner}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: "92%", md: "85%", lg: "78%", xl: "70%" },
            maxWidth: { xs: "100%", sm: 1100, md: 1200 },
            boxShadow: "-4px 0 20px rgba(0,0,0,0.1)",
            borderTopLeftRadius: { xs: 0, sm: 2 },
            borderBottomLeftRadius: { xs: 0, sm: 2 },
          },
        }}
      >
        <Box
          sx={{
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: { xs: 2, sm: 2.5, md: 3 },
              py: { xs: 2, sm: 2.5, md: 3 },
              borderBottom: 1,
              borderColor: "divider",
              bgcolor: "background.paper",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              position: "sticky",
              top: 0,
              zIndex: 1,
            }}
          >
            <Box>
              <Typography
                variant="h5"
                component="h2"
                sx={{
                  fontWeight: 700,
                  mb: 0.5,
                  lineHeight: 1.2,
                  fontSize: "clamp(1.1rem, 1.6vw, 1.5rem)",
                }}
              >
                Plan Invoices
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: { xs: "0.8rem", sm: "0.9rem", md: "0.95rem" } }}
              >
                {selectedPO?.po_code} • {selectedPO?.client_name} •{" "}
                {selectedPO?.project_name}
              </Typography>
            </Box>
            <IconButton
              onClick={handleCloseInvoicePlanner}
              sx={{
                width: { xs: 34, sm: 38, md: 40 },
                height: { xs: 34, sm: 38, md: 40 },
                border: 1,
                borderColor: "divider",
                "&:hover": {
                  bgcolor: "action.hover",
                },
              }}
            >
              <CloseIcon sx={{ fontSize: { xs: 18, sm: 20, md: 22 } }} />
            </IconButton>
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, overflowY: "auto", p: { xs: 2, sm: 3 } }}>
            {planSuccess && (
              <Alert
                severity="success"
                sx={{
                  mb: 2,
                  borderRadius: 2,
                  fontSize: { xs: "0.85rem", sm: "0.9rem", md: "0.95rem" },
                }}
                onClose={() => setPlanSuccess(null)}
              >
                {planSuccess}
              </Alert>
            )}

            {planError && (
              <Alert
                severity="error"
                sx={{
                  mb: 2,
                  borderRadius: 2,
                  fontSize: { xs: "0.85rem", sm: "0.9rem", md: "0.95rem" },
                }}
                onClose={() => setPlanError(null)}
              >
                {planError}
              </Alert>
            )}

            {selectedPO ? (
              <Card elevation={2} sx={{ mb: 3, borderRadius: 2 }}>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{
                      fontWeight: 700,
                      color: "primary.main",
                      mb: 2,
                      fontSize: { xs: "1rem", sm: "1.1rem", md: "1.2rem" },
                    }}
                  >
                    Purchase Order Summary
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" } }}
                      >
                        PO Code
                      </Typography>
                      <Typography
                        variant="body1"
                        fontWeight="medium"
                        sx={{ fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1rem" } }}
                      >
                        {selectedPO.po_code}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" } }}
                      >
                        Client
                      </Typography>
                      <Typography
                        variant="body1"
                        fontWeight="medium"
                        sx={{ fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1rem" } }}
                      >
                        {selectedPO.client_name || "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" } }}
                      >
                        Project
                      </Typography>
                      <Typography
                        variant="body1"
                        fontWeight="medium"
                        sx={{ fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1rem" } }}
                      >
                        {selectedPO.project_name || "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" } }}
                      >
                        PO Value
                      </Typography>
                      <Typography
                        variant="body1"
                        fontWeight="medium"
                        sx={{ fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1rem" } }}
                      >
                        {formatCurrency(
                          selectedPO.po_value_original,
                          selectedPO.currency,
                        )}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" } }}
                      >
                        Status
                      </Typography>
                      <Typography
                        variant="body1"
                        fontWeight="medium"
                        sx={{ fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1rem" } }}
                      >
                        {selectedPO.status || "-"}
                      </Typography>
                    </Grid>
                    {selectedPO.remarks && (
                      <Grid item xs={12}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" } }}
                        >
                          Description / Remarks
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{ fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1rem" } }}
                        >
                          {selectedPO.remarks}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            ) : null}

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, fontSize: { xs: "1rem", sm: "1.1rem", md: "1.2rem" } }}
              >
                Planned Invoices
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                disabled={can("write") === false}
                onClick={handleAddInvoiceRow}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: { xs: "0.75rem", sm: "0.85rem", md: "0.9rem" },
                  px: { xs: 1.5, sm: 2 },
                  height: { xs: 32, sm: 34, md: 36 },
                  "& .MuiButton-startIcon svg": {
                    fontSize: { xs: 18, sm: 20, md: 22 },
                  },
                }}
              >
                Add Invoice Row
              </Button>
            </Box>

            {invoicePlanRows.length === 0 ? (
              <Paper
                elevation={1}
                sx={{ p: { xs: 3, sm: 4 }, textAlign: "center", borderRadius: 2 }}
              >
                <Typography
                  variant="body1"
                  color="text.secondary"
                  gutterBottom
                  sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}
                >
                  No invoices planned yet.
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddInvoiceRow}
                  sx={{
                    mt: 2,
                    textTransform: "none",
                    fontSize: { xs: "0.75rem", sm: "0.85rem", md: "0.9rem" },
                    px: { xs: 1.5, sm: 2 },
                    height: { xs: 32, sm: 34, md: 36 },
                    "& .MuiButton-startIcon svg": {
                      fontSize: { xs: 18, sm: 20, md: 22 },
                    },
                  }}
                >
                  Add First Invoice Row
                </Button>
              </Paper>
            ) : (
              <Box>
                {invoicePlanRows.map((row, index) => (
                  <Card
                    key={row.id}
                    elevation={0}
                    sx={{
                      mb: 2,
                      p: { xs: 2, sm: 2.5 },
                      borderRadius: { xs: 2, sm: 3 },
                      border: 1,
                      borderColor: "divider",
                      bgcolor: "background.paper",
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        borderColor: "primary.300",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    <Grid container spacing={{ xs: 2, sm: 2.5 }}>

                      {/* invoice description */}
                      <Grid item xs={12}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            mb: 1,
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{
                              color: "primary.main",
                              fontWeight: 700,
                              letterSpacing: 0.5,
                              fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" },
                              textTransform: "uppercase",
                            }}
                          >
                            Invoice Item #{index + 1}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveInvoiceRow(row.id)}
                            color="error"
                            disabled={
                              invoicePlanRows.length === 1 ||
                              can("delete") === false
                            }
                            sx={{
                              mt: -1,
                              mr: -1,
                              border: 1,
                              borderColor: "transparent",
                              "&:hover": {
                                bgcolor: "error.50",
                                borderColor: "error.200",
                              },
                            }}
                          >
                            <DeleteIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                          </IconButton>
                        </Box>
                        <TextField
                          label="Invoice Description"
                          value={row.description}
                          onChange={(e) =>
                            handleUpdateInvoiceRow(
                              row.id,
                              "description",
                              e.target.value,
                            )
                          }
                          fullWidth
                          size="small"
                          required
                          error={
                            invoicePlanSubmitAttempted &&
                            (!row.description || row.description.trim().length === 0)
                          }
                          helperText={
                            invoicePlanSubmitAttempted &&
                            (!row.description || row.description.trim().length === 0)
                              ? "Invoice description is required."
                              : undefined
                          }
                          placeholder="e.g. Milestone 1 Payment"
                          multiline
                          rows={2}
                          InputProps={{
                            sx: { borderRadius: 2 },
                          }}
                          sx={{
                            "& .MuiInputLabel-root": {
                              fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" },
                            },
                            "& .MuiInputBase-input": {
                              fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                            },
                            "& .MuiFormHelperText-root": {
                              fontSize: { xs: "0.65rem", sm: "0.7rem" },
                            },
                          }}
                        />
                      </Grid>

                      {/* service month */}
                      <Grid item xs={12} sm={4}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            label="Service Month"
                            views={["year", "month"]}
                            value={
                              row.service_month
                                ? dayjs(row.service_month)
                                : null
                            }
                            onChange={(newValue) =>
                              handleUpdateInvoiceRow(
                                row.id,
                                "service_month",
                                newValue ? newValue.format("YYYY-MM") : "",
                              )
                            }
                            slotProps={{
                              textField: {
                                size: "small",
                                fullWidth: true,
                                InputProps: {
                                  sx: { borderRadius: 2 },
                                },
                                InputLabelProps: { shrink: true },
                                sx: {
                                  "& .MuiInputLabel-root": {
                                    fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" },
                                  },
                                  "& .MuiInputBase-input": {
                                    fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                                  },
                                  "& .MuiFormHelperText-root": {
                                    fontSize: { xs: "0.65rem", sm: "0.7rem" },
                                  },
                                },
                              },
                            }}
                            format="MMM YYYY"
                          />
                        </LocalizationProvider>
                      </Grid>

                      {/* planned date */}
                      <Grid item xs={12} sm={4}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            label="Planned Date"
                            value={
                              row.planned_date ? dayjs(row.planned_date) : null
                            }
                            onChange={(newValue) =>
                              handleUpdateInvoiceRow(
                                row.id,
                                "planned_date",
                                newValue ? newValue.format("YYYY-MM-DD") : "",
                              )
                            }
                            slotProps={{
                              textField: {
                                size: "small",
                                fullWidth: true,
                                required: true,
                                error:
                                  invoicePlanSubmitAttempted &&
                                  !row.planned_date,
                                helperText:
                                  invoicePlanSubmitAttempted &&
                                  !row.planned_date
                                    ? "Planned date is required"
                                    : undefined,
                                InputProps: {
                                  sx: { borderRadius: 2 },
                                },
                                InputLabelProps: { shrink: true },
                                sx: {
                                  "& .MuiInputLabel-root": {
                                    fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" },
                                  },
                                  "& .MuiInputBase-input": {
                                    fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                                  },
                                  "& .MuiFormHelperText-root": {
                                    fontSize: { xs: "0.65rem", sm: "0.7rem" },
                                  },
                                },
                              },
                            }}
                            format="DD/MM/YYYY"
                          />
                        </LocalizationProvider>
                      </Grid>

                      {/* planned amount */}
                      <Grid item xs={12} sm={4}>
                        <TextField
                          label="Amount"
                          type="number"
                          value={row.planned_amount || ""}
                          onChange={(e) =>
                            handleUpdateInvoiceRow(
                              row.id,
                              "planned_amount",
                              e.target.value
                                ? parseFloat(e.target.value)
                                : null,
                            )
                          }
                          fullWidth
                          size="small"
                          required
                          error={
                            invoicePlanSubmitAttempted &&
                            (row.planned_amount === null || row.planned_amount <= 0)
                          }
                          helperText={
                            invoicePlanSubmitAttempted &&
                            (row.planned_amount === null || row.planned_amount <= 0)
                              ? "Amount is required"
                              : undefined
                          }
                                                inputProps={{ step: "0.01", min: "0" }}
                          InputProps={{
                            startAdornment: selectedPO?.currency ? (
                              <Typography
                                variant="body2"
                                sx={{
                                  mr: 1,
                                  color: "text.secondary",
                                  fontWeight: 500,
                                  fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" },
                                }}
                              >
                                {selectedPO.currency}
                              </Typography>
                            ) : null,
                            sx: { borderRadius: 2 },
                          }}
                          sx={{
                            "& .MuiInputLabel-root": {
                              fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" },
                            },
                            "& .MuiInputBase-input": {
                              fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                            },
                            "& .MuiFormHelperText-root": {
                              fontSize: { xs: "0.65rem", sm: "0.7rem" },
                            },
                          }}
                        />
                      </Grid>


                      
                    </Grid>
                  </Card>
                ))}
              </Box>
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: { xs: "stretch", sm: "flex-end" },
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 1, sm: 2 },
              mb: { xs: 1.5, sm: 2 },
              pt: { xs: 1.5, sm: 2 },
              px: { xs: 2, sm: 3 },
              borderTop: 1,
              borderColor: "divider",
            }}
          >
            <Button
              onClick={handleCloseInvoicePlanner}
              disabled={savingPlan}
              sx={{
                textTransform: "none",
                fontSize: { xs: "0.75rem", sm: "0.85rem", md: "0.9rem" },
                minWidth: { xs: "100%", sm: 100, md: 110 },
                borderRadius: 2,
                color: "text.secondary",
                fontWeight: 600,
                height: { xs: 34, sm: 36, md: 38 },
                "&:hover": {
                  bgcolor: "action.hover",
                  color: "text.primary",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveInvoicePlan}
              disabled={
                savingPlan ||
                invoicePlanRows.length === 0 ||
                can("write") === false ||
                can("update") === false
              }
              startIcon={
                savingPlan ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <CalendarIcon />
                )
              }
              sx={{
                textTransform: "none",
                fontSize: { xs: "0.75rem", sm: "0.85rem", md: "0.9rem" },
                minWidth: { xs: "100%", sm: 140, md: 150 },
                mr: { xs: 0, sm: 1 },
                fontWeight: 600,
                borderRadius: 2,
                height: { xs: 34, sm: 36, md: 38 },
                boxShadow: "0 4px 12px rgba(37, 99, 235, 0.2)",
                "&:hover": {
                  boxShadow: "0 6px 16px rgba(37, 99, 235, 0.3)",
                  transform: "translateY(-1px)",
                },
                "& .MuiButton-startIcon svg": {
                  fontSize: { xs: 18, sm: 20, md: 22 },
                },
              }}
            >
              {savingPlan ? "Saving..." : "Save Plan"}
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* Delete PO Confirmation Dialog */}
      <Dialog
        open={deletePODialogOpen}
        onClose={handleCloseDeletePODialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: { xs: 2, sm: 2.5 },
            mx: { xs: 1.5, sm: "auto" },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontWeight: 600,
            py: { xs: 1.5, sm: 2 },
            fontSize: { xs: "1rem", sm: "1.1rem", md: "1.15rem" },
          }}
        >
          Confirm Delete
          <IconButton
            onClick={handleCloseDeletePODialog}
            size="small"
            disabled={deletingPOLoading}
            sx={{
              "&:hover": { bgcolor: "action.hover" },
            }}
          >
            <CloseIcon sx={{ fontSize: { xs: 18, sm: 20, md: 22 } }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: { xs: 1.5, sm: 2 }, pb: { xs: 1, sm: 1.5 } }}>
          <Alert
            severity="warning"
            sx={{
              mt: 0.5,
              borderRadius: 2,
              fontSize: { xs: "0.85rem", sm: "0.9rem", md: "0.95rem" },
              "& .MuiAlert-message": { width: "100%" },
            }}
          >
            Are you sure you want to delete PO{" "}
            <strong>{deletingPO?.po_code}</strong>?
            <br />
            <br />
            This action cannot be undone and will also delete all associated
            invoices.
          </Alert>
          {deletingPO && (
            <Box
              sx={{
                mt: { xs: 1.5, sm: 2 },
                p: { xs: 1.5, sm: 2 },
                bgcolor: "grey.50",
                borderRadius: 1.5,
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" } }}
              >
                <strong>Client:</strong> {deletingPO.client_name}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mt: 0.25,
                  fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                }}
              >
                <strong>Project:</strong> {deletingPO.project_name}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mt: 0.25,
                  fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                }}
              >
                <strong>Value:</strong> {deletingPO.currency}{" "}
                {deletingPO.po_value_original?.toLocaleString()}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            p: { xs: 1.5, sm: 2 },
            gap: { xs: 1, sm: 1.25 },
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            justifyContent: { xs: "stretch", sm: "flex-end" },
          }}
        >
          <Button
            onClick={handleCloseDeletePODialog}
            variant="outlined"
            disabled={deletingPOLoading}
            sx={{
              textTransform: "none",
              fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
              minWidth: { xs: "100%", sm: 110 },
              px: { xs: 2, sm: 2.5 },
              height: { xs: 34, sm: 36, md: 38 },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDeletePO}
            variant="contained"
            color="error"
            disabled={deletingPOLoading}
            startIcon={
              deletingPOLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <DeleteIcon sx={{ fontSize: { xs: 18, sm: 20, md: 22 } }} />
              )
            }
            sx={{
              textTransform: "none",
              fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
              minWidth: { xs: "100%", sm: 120 },
              px: { xs: 2, sm: 2.5 },
              height: { xs: 34, sm: 36, md: 38 },
              fontWeight: 600,
            }}
          >
            {deletingPOLoading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>


      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default POPage;
